const { useState, useEffect, useRef, useMemo } = React;
const h = React.createElement;

// ============================================================
// FILAMENT RUNS — front end (Shopify theme asset)
// Pairs with RunsCode.gs. Paste the web-app /exec URL into API_URL.
// Until then it runs in PREVIEW mode on a baked-in copy of the
// customer list; set API_URL and it goes live automatically.
// ============================================================
const API_URL = "https://script.google.com/macros/s/AKfycbwNEfNPDIilA-b_7rilz9BJ3cV2e9va5zQbwAXsFsQbcln-WwvVbD61scS4je9Dq0gO/exec";

// Reads get a hard timeout and one retry. Writes deliberately get NEITHER:
// aborting a write client-side does not stop the sheet write, so a timed-out
// save could be repeated and land twice. Same rule as the Ops app.
const READ_TIMEOUT_MS = 30000;
const RETRY_DELAY_MS = 3000;
const BOARD_CACHE_KEY = "filament_runs_board_v1";
const DRAFT_KEY = "filament_runs_draft_v1";
// An edited route that hasn't been saved yet. Kept on the phone so closing the
// app, a refresh or a dead battery doesn't silently throw a reorder away —
// reopening the run offers it back.
const ROUTE_DRAFT_KEY = "filament_runs_route_draft_v1";
function readRouteDraft(){
  try{ var d=JSON.parse(localStorage.getItem(ROUTE_DRAFT_KEY)||"null");
    return (d&&d.runId&&Array.isArray(d.stops)&&d.stops.length)?d:null; }catch(e){ return null; }
}
function writeRouteDraft(d){ try{ localStorage.setItem(ROUTE_DRAFT_KEY,JSON.stringify(d)); }catch(e){} }
function clearRouteDraft(runId){
  try{ var d=readRouteDraft(); if(!runId||!d||d.runId===runId) localStorage.removeItem(ROUTE_DRAFT_KEY); }catch(e){}
}
// What “the route as saved” means, for spotting unsaved changes: the stops in
// order, plus everything else on the Route screen that Save run writes.
// Coordinates count only for custom stops — a customer pin moved on the map is
// saved to the customer straight away and isn't an unsaved run change.
function routeSigOf(stops,date,slot,mode,backBy,driver){
  return JSON.stringify([(stops||[]).map(function(s){
    return String(s.id)+(s.custom?("@"+(+s.lat).toFixed(5)+","+(+s.lng).toFixed(5)):""); }),
    date||"",slot||"",mode||"",mode==="early"?"":(backBy||""),driver||""]);
}
// Keep the order a person set and slot any new stop in wherever it adds the
// least driving. Used for runs whose order was set by hand, so a later add or
// move doesn't hand the whole run back to the optimiser.
function keepOrderInsert(list,isNew,M){
  var base=[],add=[];
  list.forEach(function(_,i){ (isNew(i)?add:base).push(i); });
  add.forEach(function(i){
    var best=base.length,bc=Infinity;
    for(var p=0;p<=base.length;p++){
      var a=p?base[p-1]+1:0, b=p<base.length?base[p]+1:0;
      var c=M[a][i+1]+M[i+1][b]-M[a][b];
      if(c<bc){ bc=c; best=p; }
    }
    base.splice(best,0,i);
  });
  return base;
}

// Apps Script answers with an HTML sign-in or quota page rather than JSON when
// the script is busy or the session has lapsed. Parsing that throws
// "Unexpected token <", which tells a driver nothing.
async function fetchJson(url, opts, timeoutMs){
  const ms = (timeoutMs === undefined) ? READ_TIMEOUT_MS : timeoutMs;
  const useTimer = ms > 0 && typeof AbortController !== "undefined";
  const ctrl = useTimer ? new AbortController() : null;
  const timer = ctrl ? setTimeout(function(){ ctrl.abort(); }, ms) : null;
  try {
    const res = await fetch(url, Object.assign({ redirect: "follow" }, opts || {}, ctrl ? { signal: ctrl.signal } : {}));
    if(!res.ok) throw new Error("Spreadsheet returned HTTP " + res.status);
    const text = await res.text();
    try { return JSON.parse(text); }
    catch(e){ throw new Error("The spreadsheet sent a page instead of data \u2014 the script is probably busy."); }
  } catch(e){
    if(e && e.name === "AbortError") throw new Error("The spreadsheet took more than " + Math.round(ms/1000) + "s to answer.");
    throw e;
  } finally { if(timer) clearTimeout(timer); }
}

// Whether this session actually reached the backend. Every write guard reads
// this, NOT the URL: boot() can fall back to the demo data on a network
// failure while API_URL is still perfectly valid, and the old guards (which
// tested the URL) would happily post a run built from the demo venue list —
// whose IDs match no real customer — straight into the sheet.
var IS_LIVE = false;
function offline(){ return !IS_LIVE; }

const api = {
  async get(action, params) {
    const q = Object.assign({ action }, params || {});
    const qs = Object.keys(q).map(k => k + "=" + encodeURIComponent(q[k])).join("&");
    return fetchJson(`${API_URL}?${qs}`);
  },
  async getSlow(action, params) {
    const q = Object.assign({ action }, params || {});
    const qs = Object.keys(q).map(k => k + "=" + encodeURIComponent(q[k])).join("&");
    return fetchJson(`${API_URL}?${qs}`, null, 90000);
  },
  async post(action, data) {
    return fetchJson(API_URL, {
      method: "POST",
      headers: { "Content-Type": "text/plain;charset=utf-8" },
      body: JSON.stringify(Object.assign({ action }, data || {}))
    }, 0);
  }
};

function readBoardCache(){
  try {
    const p = JSON.parse(localStorage.getItem(BOARD_CACHE_KEY) || "null");
    if(!p || !p.data || !p.ts) return null;
    if(!Array.isArray(p.data.customers) || !p.data.customers.length) return null;
    return p;
  } catch(e){ return null; }
}
function writeBoardCache(data){
  if(!data || !Array.isArray(data.customers) || !data.customers.length) return;
  try { localStorage.setItem(BOARD_CACHE_KEY, JSON.stringify({ ts: Date.now(), data: data })); }
  catch(e){ /* quota or private mode — the app still works online */ }
}
function fmtCacheTime(ts){
  try {
    const d = new Date(ts);
    const t = d.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" });
    return d.toDateString() === new Date().toDateString() ? t
      : t + " on " + d.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
  } catch(e){ return "earlier"; }
}

var FALLBACK_CUSTOMERS = [{"id":"1","name":"Adisyn Cyber Pty Ltd","suburb":"Perth","lat":-31.95786,"lng":115.85574,"kegs":2,"open":"","close":""},{"id":"2","name":"Avada CrossFit","suburb":"O'Connor","lat":-32.05711,"lng":115.79444,"kegs":3,"open":"","close":""},{"id":"3","name":"Be Free Cafe","suburb":"East Perth","lat":-31.95467,"lng":115.87522,"kegs":4,"open":"","close":""},{"id":"4","name":"Bike Bar - Claremont","suburb":"Claremont","lat":-31.98494,"lng":115.78,"kegs":2,"open":"","close":""},{"id":"5","name":"Brew-Ha Kings Square","suburb":"Perth","lat":-31.9523,"lng":115.85908,"kegs":3,"open":"","close":""},{"id":"6","name":"Cafe Duo Restaurant","suburb":"Baldivis","lat":-32.31256,"lng":115.83056,"kegs":4,"open":"","close":""},{"id":"7","name":"Coco Belle Espresso Bar","suburb":"Mount Pleasant","lat":-32.03033,"lng":115.85956,"kegs":2,"open":"","close":""},{"id":"8","name":"Daisies Cottesloe","suburb":"Cottesloe","lat":-31.99889,"lng":115.76133,"kegs":3,"open":"","close":""},{"id":"9","name":"Drift Kitchen","suburb":"Scarborough","lat":-31.88944,"lng":115.76211,"kegs":4,"open":"","close":""},{"id":"10","name":"Dropinz Coffee","suburb":"Mullaloo","lat":-31.78022,"lng":115.73189,"kegs":2,"open":"","close":""},{"id":"11","name":"Emmerson's Cafe","suburb":"Joondanna","lat":-31.90978,"lng":115.83167,"kegs":3,"open":"","close":""},{"id":"12","name":"Eric Chamberlain","suburb":"Cottesloe","lat":-32.00556,"lng":115.75244,"kegs":4,"open":"","close":""},{"id":"13","name":"Fields Of Vincent","suburb":"Mt Hawthorn","lat":-31.92311,"lng":115.83844,"kegs":2,"open":"","close":""},{"id":"14","name":"Finns Espresso","suburb":"Perth","lat":-31.94897,"lng":115.86352,"kegs":3,"open":"","close":""},{"id":"15","name":"Flo Espresso","suburb":"Perth","lat":-31.95674,"lng":115.8613,"kegs":4,"open":"","close":""},{"id":"16","name":"Garage Grind (Midland Kia)","suburb":"Midland","lat":-31.89,"lng":116.00778,"kegs":2,"open":"","close":""},{"id":"17","name":"Go Coffee Go","suburb":"Doubleview","lat":-31.91256,"lng":115.79056,"kegs":3,"open":"","close":""},{"id":"18","name":"Grain Bakery (Shenton Park)","suburb":"Shenton Park","lat":-31.96233,"lng":115.80556,"kegs":4,"open":"","close":""},{"id":"19","name":"Grain Bakery WA","suburb":"Lathlain","lat":-31.96889,"lng":115.91133,"kegs":2,"open":"","close":""},{"id":"20","name":"Grounds Brew House","suburb":"Yokine","lat":-31.89944,"lng":115.85111,"kegs":3,"open":"","close":""},{"id":"21","name":"Hank's Corner","suburb":"North Perth","lat":-31.93122,"lng":115.84889,"kegs":4,"open":"","close":""},{"id":"22","name":"He Brew Espresso Pty Ltd","suburb":"Maida Vale","lat":-31.93778,"lng":116.02167,"kegs":2,"open":"","close":""},{"id":"23","name":"Jaffelato","suburb":"Rockingham","lat":-32.28256,"lng":115.72344,"kegs":3,"open":"","close":""},{"id":"24","name":"KR Surveys Pty Ltd","suburb":"Osborne Park","lat":-31.90191,"lng":115.81804,"kegs":4,"open":"","close":""},{"id":"25","name":"Koda Capital WA Pty Ltd","suburb":"West Perth","lat":-31.94367,"lng":115.84522,"kegs":2,"open":"","close":""},{"id":"26","name":"Kuld Creamery","suburb":"Kardinya","lat":-32.06044,"lng":115.809,"kegs":3,"open":"","close":""},{"id":"27","name":"La Patisserie South Perth","suburb":"South Perth","lat":-31.976,"lng":115.85978,"kegs":4,"open":"","close":""},{"id":"28","name":"Liquor Shed Jandakot","suburb":"Jandakot","lat":-32.10556,"lng":115.87556,"kegs":2,"open":"","close":""},{"id":"29","name":"Little Rokeby","suburb":"Subiaco","lat":-31.95223,"lng":115.83156,"kegs":3,"open":"","close":""},{"id":"30","name":"MBS Insurance WA Pty Ltd","suburb":"West Perth","lat":-31.94589,"lng":115.84633,"kegs":4,"open":"","close":""},{"id":"31","name":"MJA Studio","suburb":"Subiaco","lat":-31.94334,"lng":115.82711,"kegs":2,"open":"","close":""},{"id":"32","name":"Melt Slimming Studios","suburb":"Subiaco","lat":-31.95112,"lng":115.82489,"kegs":3,"open":"","close":""},{"id":"33","name":"Moose Claremont","suburb":"Claremont","lat":-31.97828,"lng":115.77667,"kegs":4,"open":"","close":""},{"id":"34","name":"Mount Street Breakfast Bar","suburb":"West Perth","lat":-31.95256,"lng":115.83744,"kegs":2,"open":"","close":""},{"id":"35","name":"NOGGOS KITCHEN AND COFFEE","suburb":"Maylands","lat":-31.93911,"lng":115.90144,"kegs":3,"open":"","close":""},{"id":"36","name":"OMG Decadent Donuts Perth Hills","suburb":"Mount Richon","lat":-32.14167,"lng":116.01722,"kegs":4,"open":"","close":""},{"id":"37","name":"Ol' Buoy Cott Main","suburb":"Cottesloe","lat":-32.00444,"lng":115.758,"kegs":2,"open":"","close":""},{"id":"38","name":"Oxford Yard","suburb":"Leederville","lat":-31.936,"lng":115.83878,"kegs":3,"open":"","close":""},{"id":"39","name":"Pinsani Pty Ltd as trustee for The Wotton & Kearney Service Trust","suburb":"Perth","lat":-31.94786,"lng":115.85686,"kegs":4,"open":"","close":""},{"id":"40","name":"Playa Coffee","suburb":"Perth","lat":-31.95563,"lng":115.86686,"kegs":2,"open":"","close":""},{"id":"41","name":"Primero Group Limited","suburb":"Osborne Park","lat":-31.89969,"lng":115.81693,"kegs":3,"open":"","close":""},{"id":"42","name":"Ricky's Restaurant","suburb":"Mt Lawley","lat":-31.92844,"lng":115.87411,"kegs":4,"open":"","close":""},{"id":"43","name":"Roasted N Toasted","suburb":"Leederville","lat":-31.93822,"lng":115.83989,"kegs":2,"open":"","close":""},{"id":"44","name":"Someday Coffee Co","suburb":"Floreat","lat":-31.93978,"lng":115.79167,"kegs":3,"open":"","close":""},{"id":"45","name":"Split on Angelo","suburb":"South Perth","lat":-31.98156,"lng":115.85644,"kegs":4,"open":"","close":""},{"id":"46","name":"TST Social Club","suburb":"Swanbourne","lat":-31.97711,"lng":115.77044,"kegs":2,"open":"","close":""},{"id":"47","name":"The Herdsman Market - Cafe","suburb":"Churchlands","lat":-31.91467,"lng":115.79822,"kegs":3,"open":"","close":""},{"id":"48","name":"Urban Kitchen Cafe","suburb":"Balcatta","lat":-31.87744,"lng":115.829,"kegs":4,"open":"","close":""},{"id":"49","name":"Venn Coffee - Bicton","suburb":"Bicton","lat":-32.026,"lng":115.78378,"kegs":2,"open":"","close":""},{"id":"50","name":"Venn Coffee - East Victoria Park","suburb":"East Victoria Park","lat":-31.98256,"lng":115.89756,"kegs":3,"open":"","close":""},{"id":"51","name":"Wholefood Circus","suburb":"Shenton Park","lat":-31.96233,"lng":115.80556,"kegs":4,"open":"","close":""},{"id":"52","name":"Your Daily Grind","suburb":"Maddington","lat":-32.05089,"lng":115.99233,"kegs":2,"open":"","close":""},{"id":"53","name":"Yours Truly Co WA","suburb":"Subiaco","lat":-31.94334,"lng":115.82711,"kegs":3,"open":"","close":""},{"id":"54","name":"safestyle","suburb":"Osborne Park","lat":-31.90302,"lng":115.81249,"kegs":4,"open":"","close":""},{"id":"55","name":"Foodies Market Claremont IGA","suburb":"Claremont","lat":-31.97828,"lng":115.77667,"kegs":2,"open":"","close":""},{"id":"56","name":"Little Rocket Coffee Pty Ltd","suburb":"Crawley","lat":-31.98656,"lng":115.81344,"kegs":3,"open":"","close":""},{"id":"57","name":"Ri-Vive Nutrition","suburb":"Midland","lat":-31.89111,"lng":116.01444,"kegs":4,"open":"","close":""},{"id":"58","name":"Spirit of Little Things","suburb":"Subiaco","lat":-31.94557,"lng":115.82822,"kegs":2,"open":"","close":""},{"id":"59","name":"Caffissimo Kardinya","suburb":"Kardinya","lat":-32.06044,"lng":115.809,"kegs":3,"open":"","close":""}];
const FALLBACK_SETTINGS = { maxKegs:25, slotFactor:0.5, maxHours:3, serviceMin:10, prepPerKeg:1.25, prepBaseMin:10,
  mustBeBackBy:"10:30", breweryLat:-31.9008, breweryLng:115.8136, roadFactor:1.4, avgSpeed:35, deadlineBias:0.12,
  planFillHours:3.5, planMaxExtraMin:20, planReliefTolMin:15, lateAfter:"Tue 20:00",
  friRunHours:3, friEarlyBackBy:"09:00", friLateBackBy:"13:00", planDriver:"", planTodayUntil:"12:00" };

// Sign-in identities. admin=true unlocks Plan + Settings and the full nav.
// Edit names / usernames / pins here. (Front-end gate only — for UX, not security.)
// initials drive the run ID (BG-120826). Keep these in step with
// DRIVER_INITIALS in RunsCode.gs — the backend is what actually assigns the
// ID; these are only used to show it before the run is saved.
const USERS = [
  { username:"brendan", name:"Brendan", admin:true, password:"filament1",    initials:"BG" },
  { username:"aaron",   name:"Aaron",   admin:true, password:"filament2026", initials:"AE" },
  { username:"ollie",   name:"Ollie",   admin:false, password:"nolan2026",    initials:"ON" }
];
function initialsOf(name){
  var m=USERS.filter(function(u){ return u.name===name; })[0];
  if(m) return m.initials;
  return String(name||"X").split(/\s+/).map(function(w){ return w.charAt(0); }).join("").toUpperCase().substring(0,3)||"X";
}
// What the backend will name a run saved now. It picks the next free letter if
// the driver already has one that day, so this is the base, not a promise.
function runIdFor(driver,iso){
  var p=String(iso||"").split("-");
  if(p.length<3) return initialsOf(driver)+"-";
  return initialsOf(driver)+"-"+("0"+p[2]).slice(-2)+("0"+p[1]).slice(-2)+String(p[0]).slice(-2);
}
const SESSION_KEY = "filament_runs_user";
function dayKey(){ var d=new Date(); return d.getFullYear()+"-"+(d.getMonth()+1)+"-"+d.getDate(); }
function savedUser(){
  try{ var s=JSON.parse(localStorage.getItem(SESSION_KEY)||"null");
    if(s&&s.username&&s.day===dayKey()){ var m=USERS.filter(function(u){return u.username===s.username;})[0]; if(m) return m; }
  }catch(e){} return null;
}

// ============================================================
// Engine (instant local estimate; backend re-optimises on real roads)
// ============================================================
function toMin(v){ if(v==null||v==="")return null; var m=String(v).trim().match(/^(\d{1,2}):(\d{2})/); return m?(+m[1])*60+(+m[2]):null; }
function toHHMM(m){ if(m==null||!isFinite(m))return "\u2014"; m=Math.round(m); var hh=Math.floor(m/60),mm=m%60; return (hh<10?"0":"")+hh+":"+(mm<10?"0":"")+mm; }
function hmMin(m){ if(m==null||!isFinite(m))return "\u2014"; m=Math.round(m); var h=Math.floor(m/60),mm=m%60; return (h>0?(h+"h "):"")+mm+"m"; }
function isoOf(s){ if(!s)return ""; if(/^\d{4}-\d{2}-\d{2}/.test(String(s)))return String(s).slice(0,10);
  var M={Jan:1,Feb:2,Mar:3,Apr:4,May:5,Jun:6,Jul:7,Aug:8,Sep:9,Oct:10,Nov:11,Dec:12};
  var m=String(s).match(/(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})/);
  if(m)return m[3]+"-"+("0"+M[m[2]]).slice(-2)+"-"+("0"+m[1]).slice(-2);
  var d=new Date(s); return isNaN(d)?"":(d.getFullYear()+"-"+("0"+(d.getMonth()+1)).slice(-2)+"-"+("0"+d.getDate()).slice(-2)); }
function tomorrowISO(){ var d=new Date(); d.setDate(d.getDate()+1); return d.getFullYear()+"-"+("0"+(d.getMonth()+1)).slice(-2)+"-"+("0"+d.getDate()).slice(-2); }
function niceDate(iso){ var p=String(iso||"").split("-"); if(p.length<3)return String(iso||""); var d=new Date(+p[0],+p[1]-1,+p[2]); return d.getDate()+" "+MON[d.getMonth()]+" "+d.getFullYear(); }
function fmtWhen(iso,slot){ var p=String(iso||"").split("-"); if(p.length<3)return (iso||"")+(slot?(" \u00B7 "+slot):""); var d=new Date(+p[0],+p[1]-1,+p[2]); return DOW[d.getDay()]+" "+d.getDate()+" "+MON[d.getMonth()]+(slot?(" \u00B7 "+slot):""); }
function geoOK(p){ var a=Number(p&&p.lat),o=Number(p&&p.lng); return isFinite(a)&&isFinite(o)&&a<0&&a>-45&&o>100&&o<170; }
// Does this order line take up a keg slot? Coffee bags ("CB - Single") and
// extras travel on the run and belong on the load list, but they are not kegs.
// Summing raw line quantities counted an 18-bag order as 18 kegs, which padded
// van-prep time and tripped the keg cap on runs carrying hardly any kegs.
// Mirrors isKegLine_ in RunsCode.gs — keep the two in step.
function isKegLine(l){
  if(!l) return false;
  var size=String(l.size||"").trim().toLowerCase();
  if(size==="bag"||size==="extra") return false;
  if(size&&!/^\d+(\.\d+)?\s*l$/.test(size)) return false;
  return !isNonKegName(l.product);
}
// A product name that can never be a keg. "Extra" / "Extras - ..." is the
// wholesale sheet's catch-all for one-off gear. Mirrors isNonKegLine_.
function isNonKegName(prod){
  var p=String(prod||"").toLowerCase().trim();
  if(p.indexOf("bags")===0||p.indexOf("coffee bag")!==-1) return true;
  if(/^extras?\b/.test(p)) return true;
  return /squealer|glass|box of|merch|sticker|tap badge|corflute|a-?frame|gas (bottle|cylinder)|regulator/.test(p);
}
// ---- Extras named from their notes (Oct 2026) ----
// The Item column for one-off gear is a generic "Extra"; what's actually going
// out is typed in the notes ("A-FRAME ONLY", "GAS CYLINDER"). The load list
// showed "Extra ×0" — or nothing — so the A-frame never reached the driver.
// Mirrors extraName_ / lineQty_ in RunsCode.gs — keep the two in step.
function sourceNoteOf(notes){
  return String(notes||"").split(/\s*\u00B7\s*/).filter(function(p){
    p=p.trim();
    return p&&!/^(day|run|deliver)\s/i.test(p)&&p.charAt(0)!=="#"&&p.charAt(0)!=="\u26A0";
  }).join(" \u00B7 ");
}
function extraNameFromNote(note){
  var s=String(note||"").replace(/^\s*only\s+/i,"").replace(/\s+only\s*$/i,"").trim();
  if(!s||s.length>40) return "";
  if(s===s.toUpperCase()) s=s.toLowerCase().replace(/(^|[\s\-\/(])([a-z])/g,function(m,a,b){ return a+b.toUpperCase(); });
  return s;
}
function extraName(l){
  var p=String((l&&l.product)||"").trim();
  if(!/^extras?\b/i.test(p)) return p;
  return extraNameFromNote(sourceNoteOf(l&&l.notes))||p||"Extra";
}
// A blank quantity on an extra means one of it. Kegs keep their number.
function lineQty(l){ var q=+(l&&l.qty)||0; return (q<=0&&l&&!isKegLine(l))?1:q; }
function kegsOfLines(lines){ return (lines||[]).reduce(function(a,l){ return a+(isKegLine(l)?(+l.qty||0):0); },0); }
// Everything on the order that isn't a keg, so a bag-only stop reads as
// "0 kegs \u00B7 18 items" rather than looking empty.
function extrasOfLines(lines){ return (lines||[]).reduce(function(a,l){ return a+(isKegLine(l)?0:lineQty(l)); },0); }
function hav(a,b){ var R=6371,dLa=(b.lat-a.lat)*Math.PI/180,dLo=(b.lng-a.lng)*Math.PI/180,p1=a.lat*Math.PI/180,p2=b.lat*Math.PI/180; var x=Math.sin(dLa/2)**2+Math.cos(p1)*Math.cos(p2)*Math.sin(dLo/2)**2; return 2*R*Math.asin(Math.sqrt(x)); }
function estMatrix(pts,s){ var n=pts.length,M=[]; for(var i=0;i<n;i++){M[i]=[];for(var j=0;j<n;j++)M[i][j]=i===j?0:hav(pts[i],pts[j])*s.roadFactor/s.avgSpeed*60;} return M; }
function routeDrive(o,M){ var t=M[0][o[0]+1],c=o[0]+1; for(var k=1;k<o.length;k++){t+=M[c][o[k]+1];c=o[k]+1;} return t+M[c][0]; }
// Stop time comes from the Regions tab (stopMin, carried on the customer) and
// falls back to the Settings default — same rule as svcMin_ in RunsCode.gs.
function stopMinOf(st,s){ var v=st&&+st.stopMin; return (v>0)?v:s.serviceMin; }
function rels(o,st,s,M){ var rel=0,cur=0,arr=[]; for(var k=0;k<o.length;k++){rel+=M[cur][o[k]+1];arr.push(rel);rel+=stopMinOf(st[o[k]],s);cur=o[k]+1;} return {arr:arr,total:rel+M[cur][0]}; }
function feas(o,st,s,M){ var r=rels(o,st,s,M),arr=r.arr,lc=Infinity,eo=-Infinity,bind=null;
  var early=(s.scheduleMode==="early");
  var assumedOpen=toMin(s.defaultOpen); if(assumedOpen==null)assumedOpen=360; // unspecified hours -> assume 06:00 open (early mode)
  for(var k=0;k<o.length;k++){var c=toMin(st[o[k]].close),op=toMin(st[o[k]].open);
    if(op==null&&early)op=assumedOpen;
    if(c!=null){var v=c-arr[k];if(v<lc){lc=v;bind=st[o[k]].name;}} if(op!=null){var w=op-arr[k];if(w>eo)eo=w;}}
  // Mirrors feas_ in RunsCode.gs (Oct 2026): opening hours are physical
  // (windowGap), "back by" is a guide (backLate) — a run that can't make it
  // leaves later and finishes late instead of being called impossible.
  var backBy=early?null:toMin(s.mustBeBackBy);
  var lr=(backBy==null)?Infinity:(backBy-r.total),ls=Math.min(lc,lr);
  var gap=(isFinite(eo)&&isFinite(lc)&&eo>lc)?(eo-lc):0, anchor;
  if(early) anchor=isFinite(eo)?eo:(toMin(s.shiftStart)||0);
  else if(eo<=ls) anchor=ls;
  else if(!gap) anchor=eo;
  else anchor=isFinite(lc)?lc:eo;
  var back=anchor+r.total;
  return {arr:arr,total:r.total,latestStart:ls,earliestStart:eo,anchor:anchor,backToBase:back,bind:bind,
    feasibleWindows:gap<=0,windowGap:gap,backLate:(backBy!=null)?Math.max(0,Math.round(back-backBy)):0,overCap:r.total>s.maxHours*60}; }
function twoOpt(o,st,s,M){ var best=o.slice(),imp=true,bestGap=feas(best,st,s,M).windowGap;
  while(imp){imp=false;var base=routeDrive(best,M);
    for(var i=0;i<best.length-1;i++)for(var j=i+1;j<best.length;j++){var c=best.slice(0,i).concat(best.slice(i,j+1).reverse(),best.slice(j+1));
      if(routeDrive(c,M)+0.01<base){var g=feas(c,st,s,M).windowGap; if(g<=bestGap+0.01){best=c;bestGap=g;imp=true;base=routeDrive(best,M);}}}} return best; }
// Mirrors deadlinePenalty_ / orOpt_ in RunsCode.gs — this is the instant local
// estimate shown before the backend answers, so the two have to agree or the
// route visibly reshuffles the moment live roads come back.
var DEADLINE_PENALTY_CAP=12;
function deadlinePenalty(st,s,tightest){
  var cl=toMin(st.close);
  if(cl==null||tightest==null) return 0;
  var slack=cl-tightest;
  if(slack<=0) return 0;
  return Math.min((s.deadlineBias||0)*slack,DEADLINE_PENALTY_CAP);
}
function orOpt(o,st,s,M){
  var best=o.slice(),base=routeDrive(best,M),improved=true,guard=0,bestGap=feas(best,st,s,M).windowGap;
  while(improved&&guard++<40){
    improved=false;
    for(var i=0;i<best.length&&!improved;i++){
      for(var j=0;j<best.length;j++){
        if(i===j) continue;
        var trial=best.slice();
        trial.splice(j,0,trial.splice(i,1)[0]);
        var d=routeDrive(trial,M);
        if(d+0.01<base){ var g=feas(trial,st,s,M).windowGap; if(g>bestGap+0.01) continue; best=trial; base=d; bestGap=g; improved=true; break; }
      }
    }
  }
  return best;
}
function endNearBase(o,st,s,M){ if(o.length<2) return o; var rev=o.slice().reverse();
  var gO=feas(o,st,s,M).windowGap, gR=feas(rev,st,s,M).windowGap;
  if(gO+0.01<gR) return o; if(gR+0.01<gO) return rev;
  return (M[0][rev[rev.length-1]+1]<M[0][o[o.length-1]+1])?rev:o; }
function windowRepair(o,st,s,M){
  var best=o.slice(),gap=feas(best,st,s,M).windowGap,guard=0;
  while(gap>0&&guard++<60){
    var pick=null,pg=gap,pd=Infinity;
    for(var i=0;i<best.length;i++)for(var j=0;j<best.length;j++){ if(i===j) continue;
      var t=best.slice(); t.splice(j,0,t.splice(i,1)[0]);
      var g=feas(t,st,s,M).windowGap,d=routeDrive(t,M);
      if(g<pg-0.01||(pick&&Math.abs(g-pg)<=0.01&&d<pd)){ pick=t; pg=g; pd=d; } }
    if(!pick) break; best=pick; gap=pg; }
  return best;
}
// Mirrors isLateOpener_ — a lunchtime opener (Spirit, 1pm) always goes last.
var LATE_OPENER_FROM=12*60;
function isLateOpener(st,i,s,M){
  var op=toMin(st&&st.open); if(op==null) return false;
  if(op>=LATE_OPENER_FROM) return true;
  var bb=toMin(s.mustBeBackBy);
  return s.scheduleMode!=="early"&&bb!=null&&M&&M[i+1]&&(op+stopMinOf(st,s)+M[i+1][0])>bb;
}
function optimiseLocal(st,s,M){
  var late=[],norm=[];
  st.forEach(function(x,i){ (isLateOpener(x,i,s,M)?late:norm).push(i); });
  if(late.length&&norm.length){
    late.sort(function(a,b){ return (toMin(st[a].open)||0)-(toMin(st[b].open)||0); });
    var ids=[0].concat(norm.map(function(i){ return i+1; }));
    var subM=ids.map(function(r){ return ids.map(function(c){ return M[r][c]; }); });
    var so=optimiseCoreLocal(norm.map(function(i){ return st[i]; }),s,subM);
    return so.map(function(k){ return norm[k]; }).concat(late);
  }
  return optimiseCoreLocal(st,s,M);
}
function optimiseCoreLocal(st,s,M){ var vis=st.map(function(){return false;}),o=[],cur=0;
  for(var step=0;step<st.length;step++){
    var tightest=null;
    for(var t=0;t<st.length;t++){ if(vis[t])continue; var tc=toMin(st[t].close); if(tc!=null&&(tightest==null||tc<tightest)) tightest=tc; }
    var b=-1,bs=Infinity;
    for(var j=0;j<st.length;j++){ if(vis[j])continue; var sc=M[cur][j+1]+deadlinePenalty(st[j],s,tightest); if(sc<bs){bs=sc;b=j;} }
    o.push(b);vis[b]=true;cur=b+1;}
  if(feas(o,st,s,M).windowGap>0){
    var edf=st.map(function(_,i){return i;}).sort(function(a,b){ var ca=toMin(st[a].close),cb=toMin(st[b].close); return (ca==null?1e9:ca)-(cb==null?1e9:cb); });
    var r1=windowRepair(o,st,s,M),r2=windowRepair(edf,st,s,M),g1=feas(r1,st,s,M).windowGap,g2=feas(r2,st,s,M).windowGap;
    o=(g2+0.01<g1||(Math.abs(g1-g2)<=0.01&&routeDrive(r2,M)<routeDrive(r1,M)))?r2:r1;
  }
  var best=twoOpt(o,st,s,M); best=orOpt(best,st,s,M); best=twoOpt(best,st,s,M);
  return endNearBase(best,st,s,M); }
// The flags a run carries (Oct 2026). Flags, not blocks: the first one asks for
// a reason, once per run; after that the run just shows them. Mirrors runFlags_.
function runFlagList(x,s,backBy){
  var out=[]; if(!x) return out;
  if(x.overKegs) out.push((x.kegs!=null?x.kegs:x.totalKegs)+" kegs \u2014 over the "+s.maxKegs+" keg cap");
  if(x.overCap) out.push("over the "+(x.maxHours||s.maxHours)+"h cap");
  if((x.backLate||0)>0&&backBy) out.push("back about "+(typeof x.backToBase==="number"?toHHMM(x.backToBase):(x.backToBase||"late"))+", after the "+backBy+" back-by");
  if(x.feasibleWindows===false||x.feasible===false) out.push((x.bind||x.tightestStop||"A stop")+" can\u2019t be reached in its opening hours \u2014 move it to another run or day");
  return out;
}
function computeInfo(stops, s, shiftStart){
  if(!stops.length) return null;
  var kegs=stops.reduce(function(a,b){return a+(+b.kegs||0);},0), prep=(s.prepBaseMin||0)+s.prepPerKeg*kegs, shift=toMin(shiftStart);
  var base={ kegs:kegs, prep:prep, total:null, leaveBy:null, prepBy:null, bind:null,
    feasibleWindows:false, overCap:false, overKegs:kegs>s.maxKegs, prepLate:false, missing:0 };
  var brewery={lat:Number(s.breweryLat),lng:Number(s.breweryLng)};
  if(!geoOK(brewery)) return Object.assign(base,{error:"brewery"});
  var good=stops.filter(geoOK), bad=stops.length-good.length;
  if(!good.length) return Object.assign(base,{error:"coords",missing:bad});
  var M=estMatrix([brewery].concat(good),s), order=good.map(function(_,i){return i;}), f=feas(order,good,s,M);
  var leaveBy=f.anchor, prepBy=leaveBy-prep;
  return { kegs:kegs, prep:prep, total:f.total, leaveBy:leaveBy, prepBy:prepBy, bind:f.bind,
    feasibleWindows:f.feasibleWindows, overCap:f.overCap, overKegs:kegs>s.maxKegs, backLate:f.backLate,
    backToBase:f.backToBase, firstArrival:f.anchor+(f.arr[0]||0), mode:(s.scheduleMode==="early"?"early":"back"),
    prepLate:(shift!=null&&shift>prepBy), missing:bad };
}

// ============================================================
// Styles + libs
// ============================================================
function injectAssets(){
  if(!document.getElementById("fr-css")){
    var st=document.createElement("style"); st.id="fr-css"; st.textContent = CSS; document.head.appendChild(st);
  }
  if(!document.getElementById("fr-leaflet-css")){
    var l=document.createElement("link"); l.id="fr-leaflet-css"; l.rel="stylesheet"; l.href="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css"; document.head.appendChild(l);
  }
  if(!window.L && !document.getElementById("fr-leaflet-js")){
    var s=document.createElement("script"); s.id="fr-leaflet-js"; s.src="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js"; document.head.appendChild(s);
  }
}
const CSS = `
:root{--ink:#0A0A0A;--paper:#fff;--canvas:#FAFAF8;--g05:#F2F2EF;--g10:#E8E8E4;--g40:#A8A8A0;--g60:#6E6E68;--g80:#2E2E2A;--yellow:#F5D60E;--red:#D02C2C;--green:#1F8B5A;--sans:-apple-system,BlinkMacSystemFont,'SF Pro Display',system-ui,sans-serif;--mono:ui-monospace,'SF Mono',Menlo,monospace}
.fr *{margin:0;padding:0;box-sizing:border-box}
.fr{font-family:var(--sans);color:var(--ink);max-width:430px;margin:0 auto;padding:0 18px 110px;background:var(--canvas);min-height:100vh}
.fr-eyebrow{font-size:10px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:var(--g60)}
.fr-title{font-size:32px;font-weight:800;letter-spacing:-1.5px;line-height:1.05;margin-top:5px}
.fr-sub{font-size:13px;color:var(--g60);margin-top:6px}
.fr-hdr{display:flex;justify-content:space-between;align-items:center;padding:16px 0 6px}
.fr-brand{font-size:11px;font-weight:700;letter-spacing:.5px}
.fr-dot{display:inline-block;width:6px;height:6px;background:var(--yellow);border-radius:50%;margin-right:6px;vertical-align:1px}
.fr-badge{font-size:9px;font-weight:700;letter-spacing:.5px;text-transform:uppercase;padding:3px 8px;border-radius:5px;background:var(--ink);color:var(--paper)}
.fr-badge.preview{background:var(--yellow);color:var(--ink)}
.fr-badge.driver{background:var(--yellow);color:var(--ink)}
.fr-refresh{width:30px;height:30px;border-radius:8px;border:1px solid var(--g10);background:var(--paper);display:flex;align-items:center;justify-content:center;cursor:pointer;color:var(--g60);padding:0}
.fr-refresh:active{transform:scale(.92)}
.fr-refresh svg{width:16px;height:16px;stroke:currentColor;fill:none;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}
.fr-refresh.spin svg{animation:fr-spin .7s linear infinite}
@keyframes fr-spin{to{transform:rotate(360deg)}}
.fr-run{background:var(--paper);border:1.5px solid var(--g10);border-radius:16px;padding:18px;margin-top:14px;cursor:pointer;transition:transform .12s,border-color .15s}
.fr-run:active{transform:scale(.985)}
.fr-run.ready{border-color:var(--yellow);border-width:2px}
.fr-run.onroad{border-color:var(--green);border-width:2px;background:rgba(31,139,90,.04)}
.fr-run-tag.road{background:var(--green);color:#fff}
.fr-run.done{border-color:var(--green);border-width:2px;background:rgba(31,139,90,.07)}
.fr-run-tag.done{background:var(--green);color:#fff}
.fr-orstop{display:flex;align-items:center;gap:12px;padding:13px 0;border-bottom:1px solid var(--g05)}
.fr-orstop:last-child{border-bottom:none}
.fr-orstop-n{font-weight:600;font-size:14.5px}
.fr-orstop-s{font-size:12px;color:var(--g60);margin-top:2px}
.fr-orstop-e{font-family:var(--mono);font-size:15px;font-weight:700;text-align:right}
.fr-orstop-el{font-size:8px;color:var(--g60);letter-spacing:1px;text-transform:uppercase;margin-top:2px;text-align:right}
.fr-run-top{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:14px}
.fr-run-meta{font-size:10px;color:var(--g60);letter-spacing:1px;text-transform:uppercase;font-weight:700}
.fr-run-name{font-size:24px;font-weight:800;letter-spacing:-.8px;margin-top:4px;line-height:1.1}
.fr-run-tag{display:inline-flex;align-items:center;gap:4px;font-size:9px;font-weight:700;letter-spacing:1px;text-transform:uppercase;padding:4px 8px;border-radius:5px}
.fr-run-tag.go{background:var(--yellow);color:var(--ink)}
.fr-run-tag.loading{background:var(--ink);color:var(--paper)}
.fr-run-stats{display:flex;padding-top:14px;margin-top:4px;border-top:1px solid var(--g10)}
.fr-dstat{flex:1}
.fr-dstat-v{font-family:var(--mono);font-size:22px;font-weight:700;letter-spacing:-.5px;line-height:1}
.fr-dstat-l{font-size:9px;color:var(--g60);letter-spacing:1px;text-transform:uppercase;margin-top:5px}
.fr-run-cta{margin-top:14px;padding-top:14px;border-top:1px solid var(--g10);display:flex;justify-content:space-between;align-items:center}
.fr-run-cta-l{font-size:11px;color:var(--g60);font-weight:500}
.fr-run-cta-r{display:flex;align-items:center;gap:6px;font-size:13px;font-weight:700;color:var(--ink)}
.fr-card{background:var(--paper);border:1px solid var(--g10);border-radius:14px;padding:16px;margin-top:14px}
.fr-btn{display:flex;align-items:center;justify-content:center;gap:7px;width:100%;border:none;border-radius:11px;padding:13px;font-family:var(--sans);font-weight:700;font-size:15px;cursor:pointer}
.fr-btn.ink{background:var(--ink);color:var(--paper)}
.fr-btn.out{background:var(--paper);color:var(--ink);border:1.5px solid var(--g10)}
.fr-btn.yellow{background:var(--yellow);color:var(--ink)}
.fr-btn.danger{background:transparent;color:var(--red);border:1.5px solid var(--red)}
.fr-plancount{margin-top:8px;display:flex;align-items:baseline;gap:2px}
.fr-plancount-n{font-family:var(--mono);font-size:22px;font-weight:800;letter-spacing:-.5px;color:var(--ink)}
.fr-plancount-l{font-size:12px;font-weight:600;color:var(--g60)}
.fr-plancount-x{font-size:12px;font-weight:600;color:var(--yellow-deep,#C9A800)}
.fr-plan-divider{font-size:10px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:var(--g40);margin:16px 2px 6px}
.fr-run-del{width:30px;height:30px;border-radius:8px;border:1px solid var(--g10);background:var(--paper);color:var(--g40);font-size:18px;line-height:1;cursor:pointer;flex-shrink:0;display:flex;align-items:center;justify-content:center}
.fr-run-del:active{background:var(--g05);color:var(--red)}
.fr-btn.sm{padding:10px;font-size:13px;border-radius:9px}
.fr-row{display:flex;gap:8px;margin-top:14px}
.fr-row .fr-btn{flex:1}
#rmap{height:240px;width:100%;border-radius:12px;overflow:hidden;background:var(--g05)}
.fr-status{margin-top:12px;border-radius:9px;padding:10px 13px;font-size:12.5px;font-weight:600;line-height:1.35}
.fr-status.warn{background:#FEF8C7;color:#8a7400}.fr-status.bad{background:rgba(208,44,44,.1);color:var(--red)}
.fr-stop{display:flex;align-items:center;gap:12px;padding:11px 0;border-bottom:1px solid var(--g05)}
.fr-stop:last-child{border-bottom:none}
.fr-seq{width:26px;height:26px;border-radius:50%;background:var(--ink);color:#fff;font:700 12px var(--mono);display:flex;align-items:center;justify-content:center;flex-shrink:0}
.fr-seq.first{background:var(--yellow);color:var(--ink)}
.fr-sname{font-weight:600;font-size:14px}
.fr-ssub{font-size:12px;color:var(--g60);margin-top:1px}
.fr-tag{font-size:9px;font-weight:700;color:var(--red);background:rgba(208,44,44,.1);padding:1px 6px;border-radius:4px;margin-left:6px}
.fr-ctrls{margin-left:auto;display:flex;gap:5px;flex-shrink:0}
.fr-mini{width:30px;height:30px;border-radius:8px;border:1px solid var(--g10);background:var(--paper);display:flex;align-items:center;justify-content:center;font-size:15px;cursor:pointer;color:var(--g60)}
.fr-mini.danger{color:var(--red)}
.fr-depart{margin-top:14px;background:var(--ink);color:#fff;border-radius:14px;padding:16px}
.fr-when{margin-top:14px;display:flex;align-items:center;gap:10px;background:var(--paper);border:1px solid var(--g10);border-radius:12px;padding:10px 12px}
.fr-sched{margin-top:10px;background:var(--paper);border:1px solid var(--g10);border-radius:12px;padding:12px}
.fr-seg{display:flex;gap:6px;background:var(--g05);border-radius:9px;padding:4px}
.fr-segb{flex:1;border:none;background:none;font-family:var(--sans);font-size:12px;font-weight:700;color:var(--g60);padding:9px 6px;border-radius:6px;cursor:pointer;letter-spacing:.2px}
.fr-segb.on{background:var(--ink);color:var(--paper)}
.fr-sched-row{display:flex;justify-content:space-between;align-items:center;margin-top:10px}
.fr-sched-l{font-size:13px;color:var(--g80);font-weight:500}
.fr-sched-note{font-size:11.5px;color:var(--g60);margin-top:10px;line-height:1.4}
.fr-when-l{font-size:10px;font-weight:700;letter-spacing:.5px;text-transform:uppercase;color:var(--g60)}
.fr-when-date{flex:1;font-family:var(--mono);font-size:15px;font-weight:600;border:none;background:transparent;color:var(--ink);outline:none}
.fr-when-slots{display:flex;gap:4px;background:var(--g05);border-radius:8px;padding:3px}
.fr-slot{border:none;background:transparent;font-size:12px;font-weight:700;letter-spacing:.5px;padding:5px 11px;border-radius:6px;color:var(--g60);cursor:pointer}
.fr-slot.on{background:var(--ink);color:var(--paper)}
.fr-drow{display:flex;gap:10px;align-items:center}
.fr-dk{font-size:9px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:var(--g40)}
.fr-dv{font-family:var(--mono);font-size:26px;font-weight:700;letter-spacing:-1px;margin-top:3px}
.fr-dv.accent{color:var(--yellow)}
.fr-dtime{font-family:var(--mono);font-size:24px;font-weight:700;background:transparent;border:none;color:#fff;width:100%;outline:none;margin-top:2px}
.fr-dtime::-webkit-calendar-picker-indicator{filter:invert(1);opacity:.5}
.fr-dnote{font-size:11.5px;color:var(--g40);margin-top:11px;line-height:1.4}
.fr-dnote.bad{color:#FF8A7A;font-weight:600}
.fr-pick{margin-top:8px;border:1px solid var(--g10);border-radius:11px;overflow:hidden;max-height:300px;overflow-y:auto}
.fr-pickrow{padding:11px 14px;font-size:13px;font-weight:600;border-bottom:1px solid var(--g05);cursor:pointer;display:flex;justify-content:space-between;align-items:center}
.fr-pickrow span{font-size:11px;color:var(--g40);font-weight:500}
.fr-nav{position:fixed;bottom:0;left:50%;transform:translateX(-50%);width:100%;max-width:430px;background:rgba(248,248,246,.94);backdrop-filter:blur(16px);-webkit-backdrop-filter:blur(16px);border-top:1px solid #e0e0dc;display:flex;padding:6px 4px max(26px,env(safe-area-inset-bottom));z-index:100}
.fr-ni{flex:1;display:flex;flex-direction:column;align-items:center;gap:3px;padding:6px 0;cursor:pointer;color:var(--g40);font-size:8px;font-weight:700;letter-spacing:.5px;text-transform:uppercase;border:none;background:none;font-family:var(--sans)}
.fr-ni.on{color:var(--ink)}
.fr-ni svg{width:22px;height:22px;stroke:currentColor;fill:none;stroke-width:1.8;stroke-linecap:round;stroke-linejoin:round}
.fr-login{position:fixed;inset:0;background:var(--ink);color:var(--paper);display:flex;flex-direction:column;justify-content:center;padding:40px 28px;z-index:500;max-width:430px;margin:0 auto;font-family:var(--sans)}
.fr-login *{font-family:var(--sans)}
.fr-login-brand{font-size:11px;font-weight:700;letter-spacing:2px;color:var(--yellow)}
.fr-login-title{font-size:42px;font-weight:800;letter-spacing:-2px;margin-top:24px;line-height:1;color:var(--paper)}
.fr-login-sub{font-size:14px;color:var(--g40);margin-top:8px}
.fr-login-field{margin-bottom:14px}
.fr-login-label{font-size:10px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:var(--g40);margin-bottom:6px}
.fr-login-input{width:100%;background:#161616;border:1.5px solid #333;border-radius:11px;padding:14px;font-size:16px;color:var(--paper);outline:none}
.fr-login-input:focus{border-color:var(--yellow)}
.fr-login .fr-btn{margin-top:8px}
.fr-toast{position:fixed;bottom:90px;left:50%;transform:translateX(-50%);background:var(--ink);color:#fff;padding:11px 18px;border-radius:10px;font-size:13px;font-weight:600;z-index:600;max-width:90%;text-align:center}
.fr-tmr{margin-top:18px;background:var(--ink);color:#fff;border-radius:16px;padding:18px}
.fr-tmr-top{display:flex;justify-content:space-between;align-items:baseline;margin-bottom:14px}
.fr-tmr-eb{font-size:10px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:var(--yellow)}
.fr-tmr-run{font-size:12px;font-weight:600;color:var(--g40)}
.fr-tmr-times{display:flex;align-items:center;gap:14px}
.fr-tmr-t{flex:1}.fr-tmr-arrow{color:var(--g40);font-size:20px}
.fr-tmr-meta{font-size:11.5px;color:var(--g40);margin-top:14px;line-height:1.4}
.fr-empty{text-align:center;color:var(--g40);font-size:13px;padding:26px 0}
.fr-acc{margin-top:16px}
.fr-acc-h{display:flex;justify-content:space-between;align-items:center;cursor:pointer;padding:13px 16px;background:var(--yellow);border-radius:12px;font-weight:800;letter-spacing:-.3px;font-size:15px}
.fr-acc-h.plain{background:var(--paper);border:1px solid var(--g10);font-weight:700}
.fr-acc-h.dark{background:var(--ink);color:var(--paper)}
.fr-acc-h.dark .fr-acc-c{color:var(--g40)}
.fr-runwrap{margin-top:8px;border:1.5px solid var(--g10);border-radius:13px;overflow:hidden;background:var(--paper)}
.fr-runhead{display:flex;align-items:center;gap:10px;padding:13px 15px;cursor:pointer}
.fr-runhead-n{font-weight:700;font-size:14.5px;letter-spacing:-.2px}
.fr-runhead-s{font-size:11.5px;color:var(--g60);margin-top:2px}
.fr-runbody{border-top:1px solid var(--g10);background:var(--g05);padding:10px 12px}
.fr-sug{border:2px solid var(--ink);border-radius:13px;overflow:hidden;background:var(--paper);margin-top:8px}
.fr-sug.out{border-color:var(--yellow)}
.fr-sug.bad{border-color:var(--red)}
.fr-sug-h{display:flex;align-items:center;gap:10px;padding:12px 14px;cursor:pointer}
.fr-sug-t{font-weight:800;font-size:14.5px;letter-spacing:-.2px}
.fr-sug-s{font-size:11.5px;color:var(--g60);margin-top:2px}
.fr-sug-tag{font-size:9px;font-weight:700;letter-spacing:.5px;text-transform:uppercase;padding:3px 7px;border-radius:5px;background:var(--yellow);color:var(--ink)}
.fr-sug-mv{font-size:10px;font-weight:700;padding:3px 8px;border-radius:6px;border:1px solid var(--g10);background:var(--paper);color:var(--g60);cursor:pointer;flex-shrink:0}
.fr-sug-mv:active{background:var(--g05)}
.fr-sug-pick{display:flex;flex-wrap:wrap;gap:5px;padding:6px 0 2px}
.fr-sug-pick button{font-size:11px;font-weight:600;padding:5px 9px;border-radius:7px;border:1px solid var(--ink);background:var(--paper);cursor:pointer;font-family:var(--sans)}
.fr-runstop{display:flex;align-items:center;gap:8px;padding:7px 2px;font-size:12.5px;border-bottom:1px solid var(--g10)}
.fr-runstop:last-of-type{border-bottom:none}
.fr-runstop-n{flex:1;font-weight:600}
.fr-runstop-k{font-family:var(--mono);color:var(--g60);font-weight:600}
.fr-acc-c{font-family:var(--mono);font-size:14px;font-weight:700}
.fr-ord{background:var(--paper);border:1.5px solid var(--g10);border-radius:13px;padding:13px 15px;margin-top:8px;cursor:pointer;transition:border-color .12s,transform .12s}
.fr-ord:active{transform:scale(.99)}
.fr-ord.sel{border-color:var(--ink);border-width:2px}
.fr-ord.locked{opacity:.6;cursor:default}
.fr-ord.resolved{opacity:1;border-color:var(--green)}
.fr-addr{margin-top:10px;padding-top:10px;border-top:1px dashed var(--g10)}
.fr-addr-in{width:100%;padding:9px 11px;border:1px solid var(--g10);border-radius:9px;font-size:13px;font-family:var(--sans);box-sizing:border-box;margin-top:6px}
.fr-addr-in:focus{outline:none;border-color:var(--ink)}
.fr-addr-l{font-size:10px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:var(--g40)}
.fr-ord-top{display:flex;justify-content:space-between;align-items:flex-start;gap:10px}
.fr-ord-name{font-weight:700;font-size:14.5px;letter-spacing:-.2px}
.fr-ord-sub{font-size:11.5px;color:var(--g60);margin-top:2px}
.fr-ord-k{font-family:var(--mono);font-weight:700;font-size:17px;line-height:1}
.fr-ord-kl{font-size:8px;color:var(--g60);letter-spacing:1px;text-transform:uppercase;margin-top:3px}
.fr-pills{display:flex;flex-wrap:wrap;gap:5px;margin-top:10px}
.fr-ppill{display:inline-flex;align-items:center;gap:5px;padding:3px 8px;background:var(--g05);border-radius:11px;font-size:11px;font-weight:600}
.fr-pdot{width:8px;height:8px;border-radius:50%;flex-shrink:0}
.fr-pq{font-family:var(--mono);color:var(--g60);font-weight:600}
.fr-flag{font-size:9px;font-weight:700;color:var(--red);background:rgba(208,44,44,.1);padding:2px 6px;border-radius:4px}
.fr-flag.ok{color:var(--green);background:rgba(31,139,90,.1)}
.fr-check{width:21px;height:21px;border-radius:6px;border:2px solid var(--g20);flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;color:#fff}
.fr-check.on{background:var(--ink);border-color:var(--ink)}
.fr-buildbar{position:fixed;bottom:78px;left:50%;transform:translateX(-50%);width:100%;max-width:430px;padding:0 18px;z-index:90}
.fr-buildbar .fr-btn{box-shadow:0 6px 20px rgba(0,0,0,.22)}

/* ===== LOAD SCREEN ===== */
.fr-lstepper{display:flex;gap:6px;background:var(--g05);padding:5px;border-radius:10px;margin-top:14px}
.fr-lstep{flex:1;display:flex;align-items:center;justify-content:center;gap:4px;padding:9px 6px;border-radius:7px;font-size:10px;font-weight:700;letter-spacing:.3px;color:var(--g60);text-align:center;border:none;background:none;font-family:var(--sans);cursor:pointer;transition:all .2s}
.fr-lstep.on{background:var(--paper);color:var(--ink);box-shadow:0 1px 2px rgba(0,0,0,.06)}
.fr-lstep.done{color:var(--ink)}
.fr-lstep.done::before{content:'✓';font-size:12px;font-weight:800;color:var(--green);margin-right:1px}
.fr-lprog{display:flex;justify-content:space-between;align-items:center}
.fr-lprog-v{font-family:var(--mono);font-size:30px;font-weight:800;letter-spacing:-1.5px;line-height:1;margin-top:6px}
.fr-lbar{height:4px;background:var(--g10);border-radius:2px;margin-top:14px;overflow:hidden}
.fr-lbar-fill{height:100%;background:var(--ink);transition:width .3s}
.fr-seclab{display:flex;justify-content:space-between;align-items:baseline;margin:20px 4px 8px}
.fr-seclab .fr-eyebrow.muted{color:var(--g40)}
/* keg matrix */
.fr-kegm{width:100%;border-collapse:collapse}
.fr-kegm th,.fr-kegm td{padding:13px 4px;text-align:right;font-family:var(--mono);font-variant-numeric:tabular-nums;border-bottom:1px solid var(--g10)}
.fr-kegm th{font-size:9px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:var(--g60);padding:8px 4px;border-bottom:1.5px solid var(--g20)}
.fr-kegm th:first-child{text-align:left;padding-left:8px}
.fr-kegm td{font-size:18px;font-weight:700;color:var(--ink)}
.fr-kegm td.prod{text-align:left;font-size:14px;font-weight:600;padding-left:8px;font-family:var(--sans);letter-spacing:-.1px}
.fr-kegm td.prod .d{display:inline-block;width:9px;height:9px;border-radius:50%;margin-right:8px;vertical-align:middle}
.fr-kegm td.zero{color:var(--g20);font-weight:500}
.fr-kegm td.cell{cursor:pointer;position:relative;border-radius:6px;transition:all .15s}
.fr-kegm td.cell:active{background:var(--g05)}
.fr-kegm td.cell.done{color:var(--green);text-decoration:line-through;text-decoration-color:var(--green);text-decoration-thickness:2px;opacity:.6}
.fr-kegm tr.tot td{border-bottom:none;border-top:2px solid var(--ink);font-size:20px;letter-spacing:-.5px;padding-top:12px;padding-bottom:4px}
.fr-kegm tr.tot td.tdone{color:var(--green);text-decoration:line-through;text-decoration-color:var(--green);text-decoration-thickness:2px}
.fr-kegm tr.tot td.prod{font-size:9px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;color:var(--g60)}
/* extras */
.fr-exsec{margin-top:18px}
.fr-exhead{font-size:13px;font-weight:700;letter-spacing:-.1px;padding:0 4px 8px}
.fr-exlist{background:var(--paper);border:1px solid var(--g10);border-radius:12px;overflow:hidden}
.fr-exrow{display:flex;align-items:center;border-bottom:1px solid var(--g10)}
.fr-exrow:last-child{border-bottom:none}
.fr-extick{flex:1;display:flex;align-items:center;gap:10px;padding:15px 8px 15px 16px;min-width:0}
.fr-extick .d{width:9px;height:9px;border-radius:50%;flex-shrink:0}
.fr-exname{font-size:15px;font-weight:600;letter-spacing:-.2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.fr-exqty{font-family:var(--mono);font-size:22px;font-weight:700;letter-spacing:-.5px;color:var(--ink);padding:12px 16px;cursor:pointer;flex-shrink:0;border-radius:6px;transition:all .15s}
.fr-exqty:active{background:var(--g05)}
.fr-exchev{width:44px;align-self:stretch;display:flex;align-items:center;justify-content:center;cursor:pointer;color:var(--g40);border-left:1px solid var(--g10)}
.fr-exchev svg{width:18px;height:18px;stroke:currentColor;fill:none;stroke-width:2;transition:transform .2s}
.fr-exrow.exp .fr-exchev svg{transform:rotate(90deg)}
.fr-exrow.done .fr-exqty{color:var(--green);text-decoration:line-through;text-decoration-color:var(--green);text-decoration-thickness:2px;opacity:.7}
.fr-exrow.done .fr-exname{opacity:.45}.fr-exrow.done .fr-extick .d{opacity:.45}
.fr-exdet{display:none;padding:0 16px 12px;background:var(--g05);border-top:1px solid var(--g10)}
.fr-exrow.exp + .fr-exdet{display:block}
.fr-exdrow{display:flex;align-items:center;gap:10px;padding:10px 0;border-bottom:1px solid var(--g10);font-size:12px;cursor:pointer}
.fr-exdrow:last-child{border-bottom:none}
.fr-exdrow:active{background:var(--g10)}
.fr-exds{flex:1;color:var(--ink);font-weight:500}
.fr-exdm{color:var(--g60);font-size:11px}
.fr-exdq{font-family:var(--mono);font-weight:700;font-size:13px;padding:2px 6px;border-radius:4px}
.fr-exdrow.done .fr-exdq{color:var(--green);text-decoration:line-through;text-decoration-color:var(--green);text-decoration-thickness:2px;opacity:.7}
.fr-exdrow.done .fr-exds{opacity:.45}
.fr-exconfirm{display:flex;justify-content:space-between;align-items:center;margin-top:14px;padding:0 4px}
.fr-exconfirm-l{font-size:12px;color:var(--g60);font-weight:500}
.fr-exhead-c{color:var(--g40);font-weight:600}
.fr-exempty{background:var(--paper);border:1px solid var(--g10);border-radius:12px;padding:15px 16px;display:flex;align-items:flex-start;gap:14px}
.fr-exempty-n{font-family:var(--mono);font-size:22px;font-weight:800;color:var(--g40);line-height:1;flex-shrink:0}
.fr-exempty-m{font-size:12.5px;color:var(--g60);line-height:1.45}
.fr-set-row{display:flex;justify-content:space-between;align-items:center;gap:12px;padding:11px 2px;border-bottom:1px solid var(--g05)}
.fr-set-row:last-child{border-bottom:none}
.fr-set-l{font-size:13.5px;color:var(--g80);flex:1;min-width:0}
.fr-set-hint{font-size:10px;color:var(--g40);margin-top:2px}
.fr-set-input{width:108px;text-align:right;font-family:var(--mono);font-size:15px;font-weight:600;border:1px solid var(--g10);border-radius:8px;padding:8px 10px;background:var(--paper);color:var(--ink);outline:none;flex-shrink:0}
.fr-set-input:focus{border-color:var(--ink)}
/* vehicle check */
.fr-vc{display:flex;align-items:center;gap:14px;padding:16px 0;border-bottom:1px solid var(--g10);cursor:pointer}
.fr-vc:last-child{border-bottom:none}
.fr-vc-chk{width:26px;height:26px;border-radius:8px;border:2px solid var(--g40);display:flex;align-items:center;justify-content:center;flex-shrink:0;color:transparent;transition:all .2s}
.fr-vc.done .fr-vc-chk{background:var(--ink);border-color:var(--ink);color:#fff}
.fr-vc-name{font-size:15px;font-weight:600}
.fr-vc-hint{font-size:11px;color:var(--g60);margin-top:3px}
/* highlighted check (line cleaning) — matches the green banner in Field Ops */
.fr-vc.green{background:#EEFBF3;border:1.5px solid #2D7A4F;border-radius:12px;padding:14px;margin:8px -8px 10px}
.fr-vc.green:last-child{border-bottom:1.5px solid #2D7A4F}
.fr-vc.green .fr-vc-chk{border-color:#2D7A4F}
.fr-vc.green.done .fr-vc-chk{background:var(--ink);border-color:var(--ink)}
.fr-vc.green .fr-vc-hint{color:var(--ink);font-size:12px}
.fr-vc-tag{font-size:10.5px;font-weight:800;letter-spacing:1px;text-transform:uppercase;color:#2D7A4F;margin-bottom:2px}
.fr-vc-chk svg{width:14px;height:14px;stroke:currentColor;fill:none;stroke-width:3;stroke-linecap:round;stroke-linejoin:round}
/* final load summary (vehicle step) */
.fr-sum-h{font-size:10.5px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--g60);padding:10px 0 4px}
.fr-sum-row{display:flex;align-items:center;gap:10px;padding:8px 0;border-bottom:1px solid var(--g05);font-size:14.5px}
.fr-sum-row:last-child{border-bottom:none}
.fr-sum-n{flex:1;min-width:0;display:flex;align-items:center;gap:8px;font-weight:600}
.fr-sum-n .d{width:9px;height:9px;border-radius:50%;flex-shrink:0;display:inline-block}
.fr-sum-s{width:36px;text-align:right;color:var(--g60);font-size:12.5px;font-family:var(--mono)}
.fr-sum-q{width:44px;text-align:right;font-weight:700;font-family:var(--mono)}
/* sticky load footer */
.fr-lfoot{margin-top:18px}
.fr-btn.disabled{background:var(--g10);color:var(--g40);cursor:not-allowed}
.fr-btn.disabled:active{transform:none}
.fr-btn.sm{padding:10px 14px;font-size:12px;border-radius:9px;width:auto}
/* depart overlay */
.fr-depart-ov{position:fixed;inset:0;background:rgba(10,10,10,.55);backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);z-index:550;display:flex;align-items:flex-end;justify-content:center}
.fr-depart-card{background:var(--paper);width:100%;max-width:430px;border-radius:20px 20px 0 0;padding:26px 24px max(26px,env(safe-area-inset-bottom));animation:frUp .25s ease}
@keyframes frUp{from{transform:translateY(40px);opacity:.6}to{transform:translateY(0);opacity:1}}
.fr-busy-ov{position:fixed;inset:0;background:rgba(10,10,10,.45);backdrop-filter:blur(3px);-webkit-backdrop-filter:blur(3px);z-index:650;display:flex;align-items:center;justify-content:center;animation:frFade .15s ease}
@keyframes frFade{from{opacity:0}to{opacity:1}}
.fr-busy-card{background:var(--paper);border-radius:18px;padding:28px 30px;display:flex;flex-direction:column;align-items:center;gap:12px;min-width:220px;max-width:80%;text-align:center;box-shadow:0 16px 50px rgba(0,0,0,.3);animation:frUp .2s ease}
.fr-busy-card.done{background:var(--yellow)}
.fr-busy-t{font-size:17px;font-weight:800;letter-spacing:-.4px;color:var(--ink)}
.fr-busy-sub{font-size:12.5px;color:var(--g60);line-height:1.4}
.fr-busy-spin{width:34px;height:34px;border-radius:50%;border:3px solid var(--g10);border-top-color:var(--ink);animation:fr-spin .7s linear infinite}
.fr-busy-check{width:46px;height:46px;border-radius:50%;background:var(--ink);color:var(--yellow);display:flex;align-items:center;justify-content:center;font-size:24px;font-weight:800}
.fr-depart-ic{width:54px;height:54px;border-radius:50%;background:var(--yellow);display:flex;align-items:center;justify-content:center;margin-bottom:16px}
.fr-depart-ic svg{width:26px;height:26px;stroke:var(--ink);fill:none;stroke-width:2.4;stroke-linecap:round;stroke-linejoin:round}
`;

// ============================================================
// App
// ============================================================
class Boundary extends React.Component{
  constructor(p){ super(p); this.state={err:null}; }
  static getDerivedStateFromError(e){ return { err:(e&&e.message)||"Something went wrong" }; }
  render(){
    if(this.state.err) return h("div",{className:"fr-card",style:{marginTop:18}},
      h("div",{className:"fr-eyebrow",style:{color:"var(--red)"}},"This screen hit a snag"),
      h("div",{className:"fr-sub",style:{marginTop:6}},this.state.err),
      h("div",{className:"fr-sub",style:{marginTop:6}},"Use the tabs below to carry on."));
    return this.props.children;
  }
}
function App(){
  const [loading,setLoading]=useState(true);
  const [live,setLive]=useState(false);
  const [user,setUser]=useState(savedUser);
  const [customers,setCustomers]=useState([]);
  const [settings,setSettings]=useState(FALLBACK_SETTINGS);
  const [screen,setScreen]=useState("today");
  const [stops,setStops]=useState([]);
  const [loadingRun,setLoadingRun]=useState(false);
  const [loadPick,setLoadPick]=useState(false);   // true only when a run was explicitly chosen to load (vs tapping the Load nav tab)
  const [saveState,setSaveState]=useState(null);   // null | "saving" | "saved"
  // Route-screen save safety (Oct 2026). manualOrder: the stop order was set by
  // hand, so later edits keep it. runSavedAt: the version of the run this screen
  // opened, so a save can't land on top of someone else's newer one. savedSig:
  // the route as last loaded or saved, to tell when there's unsaved work.
  const [manualOrder,setManualOrder]=useState(false);
  const [runSavedAt,setRunSavedAt]=useState(undefined);
  const [savedSig,setSavedSig]=useState("");
  const [saveIssue,setSaveIssue]=useState(null);   // {title, lines[]} shown on the Route screen until dismissed
  const [busyMsg,setBusyMsg]=useState(null);      // {title,sub} while the sheet is being written
  const [shiftStart,setShiftStart]=useState("06:30");
  const [runDate,setRunDate]=useState("");
  const [runSlot,setRunSlot]=useState("AM");
  const [runMode,setRunMode]=useState("back");
  const [runBackBy,setRunBackBy]=useState("12:30");
  const [runDriver,setRunDriver]=useState("");
  const [history,setHistory]=useState([]);
  const [histAll,setHistAll]=useState(false);
  const [histLoaded,setHistLoaded]=useState(false);
  const [runOverride,setRunOverride]=useState(null);   // {hours,kegs,reason}
  const [orderWeek,setOrderWeek]=useState("");
  const [currentRunId,setCurrentRunId]=useState("");
  const [savedRuns,setSavedRuns]=useState([]);
  const [orders,setOrders]=useState(null);
  const [routeGeo,setRouteGeo]=useState(null);
  const [roadInfo,setRoadInfo]=useState(null);
  const [edit,setEdit]=useState(false);
  const [pickerOpen,setPickerOpen]=useState(false);
  const [toast,setToastMsg]=useState(null);
  const [refreshing,setRefreshing]=useState(false);
  const [refreshNonce,setRefreshNonce]=useState(0);
  const [runUpdates,setRunUpdates]=useState([]);   // saved runs whose orders have since moved
  const [updatesOpen,setUpdatesOpen]=useState(false);
  const [applying,setApplying]=useState("");

  // ---- Local planning board -------------------------------------------
  // Everything on the Plan screen is edited here first and written to the
  // spreadsheet once, when you tap Save. Previously every move, add, remove
  // and driver change was its own round trip, and each of those re-read the
  // Ops master dozens of times before answering.
  const [boardStops,setBoardStops]=useState({});     // runId -> full stops (coords + order lines)
  const [dirtyRuns,setDirtyRuns]=useState({});       // runId -> true, awaiting save
  const [deletedRuns,setDeletedRuns]=useState([]);   // runIds to remove on save
  const [savedAtOf,setSavedAtOf]=useState({});       // runId -> version as loaded, for the clash check
  const [committing,setCommitting]=useState(false);
  const [skipChanges,setSkipChanges]=useState({});   // customerId -> parked?
  const [commitBlock,setCommitBlock]=useState(null);  // caps hit on save, awaiting a reason
  const newRunSeq=useRef(1);

  // ---- Loading / lock state --------------------------------------------
  const [cacheTs,setCacheTs]=useState(null);         // set when showing a cached board
  const [bootError,setBootError]=useState(null);
  const [stale,setStale]=useState(false);
  const [lockInfo,setLockInfo]=useState({locked:false});
  const [lockToken,setLockToken]=useState("");
  const [lockHeld,setLockHeld]=useState(false);
  const mapEl=useRef(null);
  const mapObj=useRef(null);
  const layerObj=useRef(null);

  useEffect(()=>{ injectAssets(); boot(); },[]);
  function showToast(m){ setToastMsg(m); clearTimeout(window._frT); window._frT=setTimeout(()=>setToastMsg(null),2200); }
  function handleLogin(u){ setUser(u); try{ localStorage.setItem(SESSION_KEY,JSON.stringify({username:u.username,day:dayKey()})); }catch(e){} }
  // Cache-busting reload: navigating to a unique URL forces the phone to
  // re-fetch the JS asset from Shopify (not the cached copy), so code updates
  // show immediately. Login persists for the day via localStorage, and boot()
  // reloads fresh data on init. (Same pattern as the Brewery app.)
  function refreshAll(){ if(refreshing) return;
    if(routeDirty&&!window.confirm("This route has unsaved changes. They’ll be kept on this phone and offered back when you reopen the run.\n\nRefresh anyway?")) return;
    setRefreshing(true);
    try{ window.location.href = window.location.pathname + "?_=" + Date.now(); }
    catch(e){ setRefreshing(false); showToast("Couldn\u2019t refresh"); } }

  // One call instead of three, with the last good copy painted first so the
  // app is usable before the network answers and stays usable if it doesn't.
  function applyBoard(d, fromCache){
    if(!d) return;
    if(Array.isArray(d.customers) && d.customers.length) setCustomers(d.customers);
    if(d.settings) setSettings(Object.assign({},FALLBACK_SETTINGS,d.settings||{}));
    if(Array.isArray(d.orders)) setOrders(d.orders);
    if(Array.isArray(d.runs)) setSavedRuns(d.runs);
    if(d.stops) setBoardStops(d.stops);
    if(Array.isArray(d.updates)) setRunUpdates(d.updates);
    if(d.lock) setLockInfo(d.lock);
    var sa = {};
    (d.runs||[]).forEach(function(r){ sa[r.runId] = r.savedAt || ""; });
    setSavedAtOf(sa);
    setDirtyRuns({}); setDeletedRuns([]); setSkipChanges({});
    setStale(fromCache ? true : false);
  }

  // A sync brings in new orders while you are part-way through planning, so
  // the fresh board is MERGED rather than swapped in: anything you have drafted
  // or edited but not yet saved is kept exactly as it is, and the new orders
  // simply appear in the unplanned list alongside it.
  function mergeBoard(d){
    if(!d) return;
    if(Array.isArray(d.customers)&&d.customers.length) setCustomers(d.customers);
    if(d.settings) setSettings(Object.assign({},FALLBACK_SETTINGS,d.settings||{}));
    if(Array.isArray(d.updates)) setRunUpdates(d.updates);
    if(d.lock) setLockInfo(d.lock);

    const isLocal=function(id){ return String(id).indexOf("NEW-")===0||!!dirtyRuns[id]; };

    setSavedRuns(function(prev){
      const keep=(prev||[]).filter(function(r){ return isLocal(r.runId); });
      const keepIds={}; keep.forEach(function(r){ keepIds[r.runId]=true; });
      return (d.runs||[]).filter(function(r){ return !keepIds[r.runId]; }).concat(keep);
    });
    setBoardStops(function(prev){
      const n=Object.assign({},d.stops||{});
      Object.keys(prev||{}).forEach(function(id){ if(isLocal(id)) n[id]=prev[id]; });
      return n;
    });
    setSavedAtOf(function(prev){
      const n=Object.assign({},prev||{});
      (d.runs||[]).forEach(function(r){ if(!dirtyRuns[r.runId]) n[r.runId]=r.savedAt||""; });
      return n;
    });
    // Orders come back fresh from the sheet, then local decisions are laid back
    // over the top: a venue sitting on an unsaved draft run must not reappear
    // in "Orders to plan", and an order parked as no-delivery stays parked.
    setOrders(function(){
      const assigned={};
      Object.keys(boardStops||{}).forEach(function(id){
        if(!isLocal(id)) return;
        (boardStops[id]||[]).forEach(function(st){ assigned[String(st.id)]=id; });
      });
      return (d.orders||[]).map(function(o){
        const cid=String(o.customerId);
        if(assigned[cid]) return Object.assign({},o,{status:"Planned",runId:assigned[cid]});
        if(skipChanges[cid]!==undefined)
          return Object.assign({},o,{status:skipChanges[cid]?"No delivery":"Pending",runId:""});
        return o;
      });
    });
    setStale(false);
  }

  async function fetchBoard(){
    // planBoard is the single-call board. An older deployment answers
    // "Unknown action", so fall back to the three original reads — the two
    // halves can be released in either order.
    var d = await api.getSlow("planBoard");
    if(d && d.error && /unknown action/i.test(String(d.error))){
      const [b,o,rd]=await Promise.all([api.get("bootstrap"),api.get("loadOrders"),api.get("loadRuns")]);
      if(!b||b.error||!b.customers) throw new Error((b&&b.error)||"bad");
      d = { customers:b.customers, settings:b.settings, orders:Array.isArray(o)?o:[],
            runs:Array.isArray(rd)?rd:[], stops:{}, updates:[], lock:{locked:false}, _legacy:true };
    }
    if(!d || d.error || !Array.isArray(d.customers) || !d.customers.length)
      throw new Error((d&&d.error) || "The spreadsheet returned no customer data.");
    return d;
  }

  async function boot(){
    const cached = readBoardCache();
    if(cached){ applyBoard(cached.data, true); setCacheTs(cached.ts); setLoading(false); IS_LIVE = true; }
    var attempt = async function(retriesLeft){
      try{
        const d = await fetchBoard();
        IS_LIVE = true;
        applyBoard(d, false);
        writeBoardCache(d);
        setCacheTs(null); setBootError(null); setLive(true);
        if(d._legacy){ loadHistory(false); loadUpdates(); }
      }catch(e){
        if(retriesLeft > 0){ setTimeout(function(){ attempt(retriesLeft-1); }, RETRY_DELAY_MS); return; }
        setBootError((e && e.message) || String(e));
        if(cached){
          // Keep working on the cached board — it is real data, just older.
          IS_LIVE = true; setLive(true); setStale(true);
        } else {
          // Nothing real to show. The demo list is clearly labelled PREVIEW and
          // every write is refused while IS_LIVE is false.
          IS_LIVE = false;
          setCustomers(FALLBACK_CUSTOMERS); setSettings(FALLBACK_SETTINGS); setLive(false);
          setOrders(FALLBACK_ORDERS); setSavedRuns([]);
        }
      }
      setLoading(false);
    };
    attempt(1);
    // Recent runs used to load only as a side effect of saving or reassigning a
    // run, so opening the app cold left Today reading "No finished runs yet" —
    // the one place you'd look to find where an order went. Fired here rather
    // than awaited in the Promise.all above: it's the slowest read of the three
    // and it must not hold up the board.
    loadHistory(false);
  }
  // Kept off the boot path deliberately: it's a comparison across two tabs, and
  // a slow or failed answer shouldn't hold up the board or blank it.
  async function loadUpdates(){
    if(offline()) return;
    try{ const u=await api.get("loadRunUpdates"); if(Array.isArray(u)) setRunUpdates(u); }catch(e){}
  }

  // A stop built from an order plus the customer record, entirely locally.
  function stopFromOrderLocal(custId){
    const want=String(custId);
    const o=(orders||[]).filter(function(x){ return String(x.customerId)===want; })[0];
    const c=(customers||[]).filter(function(x){ return String(x.id)===want; })[0];
    if(!o||!c||!geoOK(c)) return null;
    return { id:want, name:o.customerName||c.name, suburb:c.suburb||"", lat:Number(c.lat), lng:Number(c.lng),
      open:c.open||"", close:c.close||"", kegs:kegsOfLines(o.lines),
      products:(o.lines||[]).map(function(l){ return l.product; }), lines:o.lines||[],
      orderNo:o.orderNo||"", note:"", stopMin:c.stopMin||null };
  }

  // ============================================================
  // LOCAL PLANNING ENGINE
  // ------------------------------------------------------------
  // The same optimiser the app already carries as its offline fallback, used
  // here to re-time a run the instant it changes. Straight-line rather than
  // road distances, so the numbers read LONGER than reality — which is why the
  // Plan screen says so, and why the real road times are worked out once, on
  // save, where the caps are actually enforced.
  // ============================================================
  function localPlan(stopList, mode, backBy, keepIds){
    const list=(stopList||[]).slice();
    const kegs=list.reduce(function(a,b){ return a+(+b.kegs||0); },0);
    const out={ ordered:list, totalKegs:kegs, totalMin:null, leaveBy:"", overCap:false,
                overKegs:kegs>(settings.maxKegs||25), feasible:true, estimate:true };
    const brewery={lat:Number(settings.breweryLat),lng:Number(settings.breweryLng)};
    const good=list.filter(geoOK);
    if(!geoOK(brewery)||!good.length) return out;
    const s=Object.assign({},settings,{scheduleMode:(mode==="early"?"early":"back"),
      mustBeBackBy:backBy||settings.mustBeBackBy,shiftStart:shiftStart});
    const M=estMatrix([brewery].concat(good),s);
    // A hand-ordered run keeps its order; anything new is slotted in.
    const order=keepIds
      ? keepOrderInsert(good,function(i){ return !keepIds[String(good[i].id)]; },M)
      : optimiseLocal(good,s,M);
    const ordered=order.map(function(i){ return good[i]; })
      .concat(list.filter(function(x){ return !geoOK(x); }));
    const f=feas(order,good,s,M);
    out.ordered=ordered; out.totalMin=Math.round(f.total); out.leaveBy=toHHMM(f.anchor);
    out.overCap=f.overCap; out.feasible=f.feasibleWindows; out.bind=f.bind;
    out.backLate=(s.scheduleMode==="early")?0:f.backLate; out.backToBase=toHHMM(f.backToBase);
    out.backBy=(s.scheduleMode==="early")?"":s.mustBeBackBy;
    return out;
  }

  // Recompute one run in the local board and mark it unsaved.
  function reprice(runId, stopList, extra, meta){
    const run=meta||(savedRuns||[]).filter(function(r){ return r.runId===runId; })[0]||{};
    let keepIds=null;
    if(run.manualOrder){
      keepIds={};
      (boardStops[runId]||[]).forEach(function(st){ keepIds[String(st.id)]=true; });
    }
    const p=localPlan(stopList, run.scheduleMode, run.mustBeBackBy, keepIds);
    setBoardStops(function(m){ const n=Object.assign({},m); n[runId]=p.ordered; return n; });
    setSavedRuns(function(rs){ return (rs||[]).map(function(r){
      if(r.runId!==runId) return r;
      return Object.assign({},r,extra||{},{
        totalKegs:p.totalKegs, estHours:p.totalMin!=null?+(p.totalMin/60).toFixed(2):r.estHours,
        latestStart:p.leaveBy||r.latestStart, estimate:true,
        overCap:p.overCap, overKegs:p.overKegs, feasible:p.feasible, bind:p.bind,
        backLate:p.backLate, backToBase:p.backToBase, backBy:p.backBy,
        stopRows:p.ordered.map(function(st,i){
          return { seq:i+1, customerId:String(st.id), name:st.name,
                   kegs:+st.kegs||0, items:extrasOfLines(st.lines), eta:"", orderNo:st.orderNo||"" }; })
      });
    }); });
    setDirtyRuns(function(d){ const n=Object.assign({},d); n[runId]=true; return n; });
    return p;
  }

  function setOrderRun(custId, runId, status){
    setOrders(function(os){ return (os||[]).map(function(o){
      return String(o.customerId)===String(custId)
        ? Object.assign({},o,{ status:status, runId:runId||"" }) : o; }); });
  }

  const pendingCount = Object.keys(dirtyRuns).length + deletedRuns.length + Object.keys(skipChanges).length;

  // ============================================================
  // SAVE THE BOARD  — one call, all or nothing
  // ============================================================
  async function commitBoard(arg){
    // Reasons typed into the flags box come in here directly. Reading them back
    // out of state meant the re-send used the render from BEFORE they were set.
    const typed=(arg&&arg.__reasons)?arg.__reasons:{};
    if(committing) return;
    if(offline()){ showToast("Preview \u2014 can\u2019t save"); return; }
    if(!pendingCount){ showToast("Nothing to save"); return; }
    setCommitting(true);
    setBusyMsg({title:"Saving the board\u2026",sub:"Working out the real road times and writing to the spreadsheet."});
    try{
      const runsOut=(savedRuns||[]).filter(function(r){ return dirtyRuns[r.runId]; }).map(function(r){
        const isNew=String(r.runId||"").indexOf("NEW-")===0;
        return { runId:isNew?"":r.runId, clientId:r.runId, driver:r.driver||"", date:r.date||"", slot:r.slot||"",
          scheduleMode:r.scheduleMode||"back", mustBeBackBy:r.mustBeBackBy||"",
          orderWeek:r.orderWeek||"", savedAt:isNew?"":(savedAtOf[r.runId]||""),
          overrideReason:typed[r.runId]||r.overrideReason||"",
          stops:(boardStops[r.runId]||[]).map(function(st){
            return { id:st.id, name:st.name, lat:st.lat, lng:st.lng, kegs:st.kegs||0,
              open:st.open||"", close:st.close||"", products:st.products||[],
              lines:st.lines||[], note:st.note||"", orderNo:st.orderNo||"" }; }) };
      });
      const r=await api.post("commitPlan",{ user:(user&&user.name)||"", token:lockToken,
        runs:runsOut, deleteRunIds:deletedRuns,
        skipOrders:Object.keys(skipChanges).map(function(k){ return {customerId:k, skip:skipChanges[k]}; }) });
      setBusyMsg(null);
      if(r&&r.blocked){
        setCommitting(false);
        const overridable=(r.problems||[]).filter(function(p){ return p.overridable; });
        // Over a cap is a policy call, so ask for the reason rather than just
        // refusing. Anything physical (opening hours, a departed run) or a
        // clash with someone else's save is reported and stands.
        if(overridable.length&&!(r.conflicts||[]).length){
          var prior=(overridable.filter(function(p){ return p.priorReason; })[0]||{}).priorReason||"";
          setCommitBlock({ problems:overridable, others:(r.problems||[]).filter(function(p){ return !p.overridable; }), reason:prior });
          return r;
        }
        const first=(r.problems&&r.problems[0])||(r.conflicts&&r.conflicts[0]);
        showToast("Not saved \u2014 "+((first&&first.reason)||r.reason||"something needs fixing"));
        if((r.conflicts||[]).length) setCommitBlock({ conflicts:r.conflicts });
        return r;
      }
      if(r&&r.success){
        clearDraft();
        await reloadBoard(true);
        showToast("Saved \u2014 "+r.committed+(r.committed===1?" run":" runs")
          +(r.deleted?(" \u00B7 "+r.deleted+" deleted"):"")+" \u00B7 times now on real roads");
        setCommitting(false);
        return r;
      }
      setCommitting(false);
      showToast((r&&r.error)||"Couldn\u2019t save the board");
      return r;
    }catch(e){
      setBusyMsg(null); setCommitting(false);
      showToast("Couldn\u2019t save \u2014 check signal. Your changes are still here.");
    }
  }

  // Re-send the board with a written reason against each over-cap run.
  function confirmOverride(){
    const b=commitBlock; if(!b||!b.problems) return;
    const why=String(b.reason||"").trim(); if(!why) return;
    // Matched on the ID the app sent (clientId), so a brand-new run — which
    // has no sheet ID yet — gets its reason too. It used to be dropped, and
    // the board bounced off the same block every time it was saved.
    const ids={}; b.problems.forEach(function(p){ if(p.clientId) ids[p.clientId]=true; if(p.runId) ids[p.runId]=true; });
    const anyId=Object.keys(ids).length>0;
    setSavedRuns(function(rs){ return (rs||[]).map(function(r){
      return (ids[r.runId]||(!anyId&&dirtyRuns[r.runId]))
        ? Object.assign({},r,{overrideReason:why}) : r; }); });
    const typed={};
    (savedRuns||[]).forEach(function(r){ if(ids[r.runId]||(!anyId&&dirtyRuns[r.runId])) typed[r.runId]=why; });
    setCommitBlock(null);
    commitBoard({__reasons:typed});
  }
  function p_hasId(ps){ return (ps||[]).some(function(p){ return !!p.runId; }); }

  function discardChanges(){
    clearDraft();
    reloadBoard(true);
    showToast("Changes discarded");
  }

  async function reloadBoard(force){
    if(offline()) return;
    if(!force && pendingCount) return;   // never overwrite unsaved work
    try{ const d=await fetchBoard(); IS_LIVE=true; applyBoard(d,false); writeBoardCache(d); setCacheTs(null); }
    catch(e){ /* keep what is on screen */ }
  }

  // ---- Draft persistence: an unfinished board survives closing the app ----
  function saveDraft(){
    try{
      if(!pendingCount) return;
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ ts:Date.now(), runs:savedRuns,
        stops:boardStops, orders:orders, dirty:dirtyRuns, deleted:deletedRuns, savedAt:savedAtOf }));
    }catch(e){}
  }
  function clearDraft(){ try{ localStorage.removeItem(DRAFT_KEY); }catch(e){} }
  function readDraft(){
    try{ const d=JSON.parse(localStorage.getItem(DRAFT_KEY)||"null");
      return (d&&d.runs&&d.dirty&&(Object.keys(d.dirty).length||(d.deleted||[]).length))?d:null; }
    catch(e){ return null; }
  }
  function restoreDraft(d){
    if(!d) return;
    setSavedRuns(d.runs||[]); setBoardStops(d.stops||{}); if(d.orders) setOrders(d.orders);
    setDirtyRuns(d.dirty||{}); setDeletedRuns(d.deleted||[]); setSavedAtOf(d.savedAt||{});
    showToast("Unsaved changes restored");
  }
  useEffect(function(){ saveDraft(); },[dirtyRuns,deletedRuns,boardStops,savedRuns]);

  // ---- Offer an unfinished board back ------------------------------------
  const draftAsked=useRef(false);
  useEffect(function(){
    if(screen!=="plan"||draftAsked.current||loading||offline()) return;
    draftAsked.current=true;
    const d=readDraft();
    if(!d) return;
    const when=fmtCacheTime(d.ts);
    if(window.confirm("You have unsaved changes from "+when+".\n\nRestore them? (Cancel discards them.)")) restoreDraft(d);
    else clearDraft();
  },[screen,loading]);

  // ---- Planning lock: one planner at a time, no take-over ----------------
  useEffect(function(){
    if(screen!=="plan"||offline()||!user) return;
    let alive=true, timer=null;
    (async function(){
      try{
        const r=await api.post("planLockClaim",{user:user.name});
        if(!alive) return;
        if(r&&r.granted){ setLockHeld(true); setLockToken(r.token||""); setLockInfo({locked:false}); }
        else { setLockHeld(false); setLockInfo({locked:true,user:(r&&r.user)||"Someone",
          since:(r&&r.since)||"", expiresIn:(r&&r.expiresIn)||0}); }
      }catch(e){ setLockHeld(false); }
    })();
    timer=setInterval(function(){
      if(!lockToken) return;
      api.post("planLockRenew",{token:lockToken}).then(function(x){
        if(x&&x.renewed===false) setLockHeld(false);
      }).catch(function(){});
    },5*60*1000);
    return function(){
      alive=false; if(timer) clearInterval(timer);
      // Hold the lock while there is unsaved work, so stepping to another tab
      // mid-plan doesn't hand the board to the other person.
      if(lockToken && !pendingCount) api.post("planLockRelease",{token:lockToken}).catch(function(){});
    };
  },[screen,user]);

  // Rebuild one run's stops from the current orders and re-time it.
  async function acceptUpdate(runId){
    if(applying) return;
    setApplying(runId);
    try{
      const r=await api.post("applyRunUpdate",{runId:runId});
      if(r&&r.success){
        await reloadOrdersRuns();
        var warn=r.overKegs?" \u2014 now over the keg cap":(r.overCap?" \u2014 now over the hour cap":"");
        showToast("Run updated \u2014 "+r.totalKegs+" kegs"+warn);
      } else showToast((r&&r.error)||"Couldn\u2019t update that run");
    }catch(e){ showToast("Couldn\u2019t update \u2014 check signal"); }
    setApplying("");
  }

  // Everything that used to re-read three endpoints now refreshes the one
  // board call — and refuses to do it while there is unsaved work on screen.
  async function reloadOrdersRuns(){
    if(offline()) return;
    if(pendingCount){ showToast("You have unsaved changes \u2014 save or discard first"); return; }
    await reloadBoard(false);
    loadHistory(histAll);
  }
  // Reassign a planned run from the Today screen. The run's stops aren't
  // loaded there, so this is a one-field write on the backend rather than a
  // re-save — and because the ID carries the driver's initials, it comes back
  // renamed.
  async function changeRunDriver(runId,driver){
    if(offline()){ showToast("Preview \u2014 can\u2019t change driver"); return; }
    try{
      const r=await api.post("setRunDriver",{runId:runId,driver:driver});
      if(r&&r.success){
        if(currentRunId===runId&&r.runId) setCurrentRunId(r.runId);
        await reloadOrdersRuns();
        showToast(r.renamedFrom?(driver+" \u2014 run is now "+r.runId):("Driver set to "+driver));
        return r;
      }
      showToast((r&&r.error)||"Couldn\u2019t change driver");
      return r;
    }catch(e){ showToast("Couldn\u2019t change driver \u2014 check signal"); }
  }

  // Past runs. A driver only ever gets their own — the filter is applied on
  // the backend rather than hidden in the render, so the rows never travel.
  async function loadHistory(all){
    if(offline()){ setHistLoaded(true); return; }
    var who=(user&&user.admin&&all)?"":((user&&user.name)||"");
    try{ const r=await api.get("loadRunHistory",{driver:who,limit:30});
      if(Array.isArray(r)) setHistory(r); }catch(e){}
    setHistLoaded(true);
  }
  function toggleHistScope(){
    var next=!histAll; setHistAll(next); setHistLoaded(false); loadHistory(next);
  }
  // Pull this week's orders from the external Wholesale sheet into the Runs
  // Orders tab, then reload. Plan screen only; the header refresh stays a read.
  // Pull this week's orders from the wholesale sheet, then merge them in.
  // Runs already saved to the sheet are never touched by a sync \u2014 it only
  // rebuilds the Orders tab \u2014 and unsaved drafts are preserved locally here.
  async function syncOrders(){
    if(offline()){ showToast("Preview \u2014 can\u2019t sync orders"); return {error:"preview"}; }
    try{
      const before=(orders||[]).length;
      const r=await api.post("syncOrders",{});
      if(r&&r.success){
        try{
          const d=await fetchBoard();
          IS_LIVE=true; mergeBoard(d); writeBoardCache(d); setCacheTs(null);
          const added=Math.max(0,(d.orders||[]).length-before);
          showToast("Synced \u2014 "+(added?(added+" new "+(added===1?"order":"orders")):"no new orders")
            +(pendingCount?" \u00B7 your unsaved work is untouched":""));
        }catch(e){ showToast("Synced, but couldn\u2019t refresh the board \u2014 pull down to retry"); }
      } else { showToast("Sync failed \u2014 "+((r&&r.error)||"unknown")); }
      return r||{error:"no response"};
    }catch(e){ showToast("Sync failed \u2014 network"); return {error:"network"}; }
  }
  async function deleteRunById(runId){
    if(!runId) return;
    if(offline()){ showToast("Preview \u2014 can\u2019t delete runs"); return; }
    try{
      // A run on the local board that was never saved just disappears.
      if(String(runId).indexOf("NEW-")===0){
        setSavedRuns(function(rs){ return (rs||[]).filter(function(x){ return x.runId!==runId; }); });
        setBoardStops(function(m2){ const n=Object.assign({},m2); delete n[runId]; return n; });
        setDirtyRuns(function(d){ const n=Object.assign({},d); delete n[runId]; return n; });
        setOrders(function(os){ return (os||[]).map(function(o){
          return o.runId===runId ? Object.assign({},o,{status:"Pending",runId:""}) : o; }); });
        showToast("Run removed from the board");
        return;
      }
      const r=await api.post("deleteRun",{runId:runId});
      if(r&&r.success){
        if(currentRunId===runId){ setStops([]); setCurrentRunId(""); setRouteGeo(null); }
        // Tidy locally rather than reloading, so unsaved work on other runs survives.
        setSavedRuns(function(rs){ return (rs||[]).filter(function(x){ return x.runId!==runId; }); });
        setBoardStops(function(m2){ const n=Object.assign({},m2); delete n[runId]; return n; });
        setDirtyRuns(function(d){ const n=Object.assign({},d); delete n[runId]; return n; });
        setOrders(function(os){ return (os||[]).map(function(o){
          return o.runId===runId ? Object.assign({},o,{status:"Pending",runId:""}) : o; }); });
        showToast("Run deleted \u2014 its orders are available again");
      } else showToast("Couldn\u2019t delete that run");
    }catch(e){ showToast("Couldn\u2019t delete that run"); }
  }

  // Optimistic: the driver is standing at the van with the doors shut, so the
  // app confirms immediately and reconciles with the sheet afterwards. Waiting
  // on the Apps Script round trip made "Start run" feel broken on a weak
  // signal. If the write fails the run goes back on the board, in its original
  // position, with a toast saying so — nothing is silently lost.
  function startRunById(runId){
    if(!runId){ setScreen("today"); showToast("No run to start"); return; }

    const nowHHMM=(function(){ var d=new Date(); return ("0"+d.getHours()).slice(-2)+":"+("0"+d.getMinutes()).slice(-2); })();
    const restore=function(msg){
      setSavedRuns(function(rs){ return (rs||[]).map(function(x){
        return x.runId===runId ? Object.assign({},x,{onRoad:false,status:"Planned",actualStart:""}) : x; }); });
      showToast(msg);
    };

    // ---- confirm now ----
    // Flip it to on-road in place rather than removing it: the run stays on
    // Today as the green "where to next" card until its estimated finish.
    setSavedRuns(function(rs){ return (rs||[]).map(function(x){
      return x.runId===runId ? Object.assign({},x,{onRoad:true,status:"On the road",actualStart:nowHHMM}) : x; }); });
    if(currentRunId===runId){ setCurrentRunId(""); setStops([]); setRouteGeo(null); }
    setScreen("today");
    showToast("Run started \u2014 safe driving");

    // Preview mode (no backend): the local clear above is the whole flow.
    if(offline()) return;

    // ---- reconcile in the background ----
    api.post("startRun",{ runId:runId, departedBy:(user&&user.name)||"" })
      .then(function(r){
        if(r&&r.success){ reloadOrdersRuns(); return; }
        restore((r&&r.error)?("Couldn\u2019t start: "+r.error+" \u2014 run is back on the board")
                            :"Couldn\u2019t start the run \u2014 it\u2019s back on the board");
      })
      .catch(function(){ restore("No signal \u2014 run is back on the board, try again"); });
  }

  const engineSettings = useMemo(function(){ return Object.assign({},settings,{scheduleMode:runMode,mustBeBackBy:runBackBy,shiftStart:shiftStart}); },[settings,runMode,runBackBy,shiftStart]);
  const info = useMemo(()=>computeInfo(stops,engineSettings,shiftStart),[stops,engineSettings,shiftStart]);

  // ---- Unsaved route changes ----
  const routeSig = routeSigOf(stops,runDate,runSlot,runMode,runBackBy,runDriver);
  const routeDirty = !loadingRun && stops.length>0 && !!currentRunId && routeSig!==savedSig;
  useEffect(function(){
    if(!currentRunId||!stops.length||loadingRun) return;
    if(routeDirty) writeRouteDraft({ runId:currentRunId, stops:stops, runDate:runDate, runSlot:runSlot,
      runMode:runMode, runBackBy:runBackBy, runDriver:runDriver, manualOrder:manualOrder,
      savedAt:runSavedAt, ts:Date.now() });
    else clearRouteDraft(currentRunId);
  },[routeSig,manualOrder,routeDirty]);
  // Last line of defence when the browser itself is closed or reloaded.
  useEffect(function(){
    function warn(e){ if(routeDirty){ e.preventDefault(); e.returnValue=""; return ""; } }
    window.addEventListener("beforeunload",warn);
    return function(){ window.removeEventListener("beforeunload",warn); };
  },[routeDirty]);
  const draftRunId = routeDirty ? currentRunId : ((readRouteDraft()||{}).runId||"");

  // ---- map ----
  useEffect(()=>{
    if(screen!=="route") return;
    let tries=0;
    const t=setInterval(()=>{
      tries++;
      if(window.L && mapEl.current){ clearInterval(t); ensureMap(); drawMap(); }
      if(tries>40) clearInterval(t);
    },100);
    return ()=>{ clearInterval(t); if(mapObj.current){ try{mapObj.current.remove();}catch(e){} mapObj.current=null; } layerObj.current=null; };
  },[screen]);
  useEffect(()=>{ if(screen==="route" && mapObj.current) drawMap(); },[stops,routeGeo,edit]);
  // fetch the real road path for the current ordered stops
  useEffect(()=>{
    setRouteGeo(null); setRoadInfo(null);
    if(screen!=="route" || offline()) return;
    if(info && info.error) return;
    const good=stops.filter(geoOK);
    if(!good.length) return;
    let cancelled=false;
    api.post("routeGeometry",{stops:good.map(s=>({id:s.id,lat:Number(s.lat),lng:Number(s.lng),kegs:s.kegs||0,lines:s.lines||[],open:s.open||"",close:s.close||""})),settings:{scheduleMode:runMode,mustBeBackBy:runBackBy,shiftStart:shiftStart}})
      .then(r=>{ if(cancelled||!r) return;
        if(r.geometry && r.geometry.length>1 && r.source==="ors") setRouteGeo(r.geometry);
        if(r.timing) setRoadInfo(r.timing); })
      .catch(()=>{});
    return ()=>{ cancelled=true; };
  },[stops,screen,runMode,runBackBy]);

  function ensureMap(){
    if(mapObj.current) return;
    const b={lat:settings.breweryLat,lng:settings.breweryLng};
    mapObj.current=L.map(mapEl.current,{zoomControl:false,scrollWheelZoom:false}).setView([b.lat,b.lng],12);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",{maxZoom:19,attribution:"\u00A9 OpenStreetMap"}).addTo(mapObj.current);
    setTimeout(()=>mapObj.current&&mapObj.current.invalidateSize(),60);
  }
  function pin(label,kind){
    const bg=kind==="base"?"#0A0A0A":(kind==="active"?"#F5D60E":"#fff"),fg=kind==="active"?"#0A0A0A":(kind==="base"?"#fff":"#0A0A0A"),sh=kind==="base"?"border-radius:4px":"border-radius:50%";
    return L.divIcon({className:"",iconSize:[26,26],iconAnchor:[13,13],html:'<div style="width:26px;height:26px;'+sh+';background:'+bg+';color:'+fg+';border:2px solid #0A0A0A;display:flex;align-items:center;justify-content:center;font:700 12px/1 ui-monospace,monospace;box-shadow:0 1px 3px rgba(0,0,0,.3)">'+label+'</div>'});
  }
  function drawMap(){
    if(!mapObj.current||!window.L) return;
    if(layerObj.current) layerObj.current.remove();
    layerObj.current=L.layerGroup().addTo(mapObj.current);
    const b={lat:settings.breweryLat,lng:settings.breweryLng};
    const line=[[b.lat,b.lng]].concat(stops.map(s=>[s.lat,s.lng])); line.push([b.lat,b.lng]);
    if(routeGeo&&routeGeo.length>1){ L.polyline(routeGeo,{color:"#0A0A0A",weight:3,opacity:.75}).addTo(layerObj.current); }
    else if(stops.length){ L.polyline(line,{color:"#0A0A0A",weight:2,dashArray:"5 5",opacity:.55}).addTo(layerObj.current); }
    L.marker([b.lat,b.lng],{icon:pin("B","base")}).addTo(layerObj.current).bindPopup("Brewery / base");
    stops.forEach(function(s,i){
      var mk=L.marker([s.lat,s.lng],{icon:pin(String(i+1),i===0?"active":""),draggable:!!edit}).addTo(layerObj.current);
      mk.bindPopup("<b>"+s.name+"</b>"+(edit?"<br>Drag the pin to correct it":""));
      // Only draggable in Edit, so a pin can't be knocked off its spot by a
      // thumb while someone is reading the route.
      if(edit) mk.on("dragend",function(ev){ var p=ev.target.getLatLng(); movePin(i,p.lat,p.lng); });
    });
    if(stops.length){ mapObj.current.invalidateSize(); mapObj.current.fitBounds(line,{padding:[16,16]}); }
  }

  // ---- actions ----
  function addStop(c){
    setStops(function(s){
      var add=Object.assign({},c), list=s.concat([add]);
      // On a hand-ordered run, slot the new stop in where it adds least driving
      // rather than hanging it off the end.
      var brewery={lat:Number(settings.breweryLat),lng:Number(settings.breweryLng)};
      if(!manualOrder||!geoOK(add)||!geoOK(brewery)||!s.every(geoOK)) return list;
      var M=estMatrix([brewery].concat(list),settings);
      var order=keepOrderInsert(list,function(i){ return i===list.length-1; },M);
      return order.map(function(i){ return list[i]; });
    });
  }
  function removeStop(i){ setStops(s=>s.filter((_,k)=>k!==i)); }
  function move(i,d){ setManualOrder(true); setStops(s=>{ const j=i+d; if(j<0||j>=s.length) return s; const n=s.slice(); const t=n[i]; n[i]=n[j]; n[j]=t; return n; }); }

  // A pin dragged on the map. The geocoder lands on the middle of a building,
  // the wrong end of a long street, or the centre's main entrance rather than
  // the loading bay — and a moved stockist keeps its old spot until someone
  // corrects it. Writes back to the customer record so the fix sticks for
  // every future run, not just this one.
  async function movePin(i,lat,lng){
    var st=stops[i]; if(!st) return;
    setStops(function(list){ var n=list.slice(); if(!n[i]) return list;
      n[i]=Object.assign({},n[i],{lat:lat,lng:lng}); return n; });
    // An ad-hoc address has no customer row to write to; it travels with the
    // run and is saved by Save run like any other edit.
    if(!st.id||st.custom||String(st.id).charAt(0)==="X"){ showToast("Pin moved \u2014 saved with the run"); return; }
    if(offline()){ showToast("Preview \u2014 pin moved on screen only"); return; }
    try{
      var r=await api.post("setCustomerCoords",{customerId:st.id,lat:lat,lng:lng});
      if(r&&r.success){
        // Keep the in-memory customer list in step, or reopening the run would
        // put the pin straight back where it was.
        setCustomers(function(cs){ return (cs||[]).map(function(c){
          return String(c.id)===String(st.id)?Object.assign({},c,{lat:lat,lng:lng}):c; }); });
        showToast(st.name+" \u2014 new location saved");
      } else showToast((r&&r.error)||"Couldn\u2019t save that pin");
    }catch(e){ showToast("Couldn\u2019t save the pin \u2014 check signal"); }
  }

  // Orders any list of stops and commits it. Kept separate from optimise() so a
  // late add can be re-slotted straight away without waiting for setStops to
  // land — nobody should have to know to press Optimise afterwards.
  async function optimiseList(list){
    if(!list||!list.length) return { ordered:list||[], source:"none" };
    const mapped=list.map(s=>({id:s.id,name:s.name,lat:s.lat,lng:s.lng,kegs:s.kegs||0,open:s.open||"",close:s.close||"",products:s.products||[],lines:s.lines||[],note:s.note||""}));
    try{
      if(offline()) throw new Error("no url");
      const r=await api.post("optimiseRun",{stops:mapped,settings:{shiftStart,scheduleMode:runMode,mustBeBackBy:runBackBy}});
      if(r&&r.stops){
        const byId={}; list.forEach(s=>byId[s.id]=s);
        const ordered=r.stops.map(rs=>byId[rs.id]).filter(Boolean);
        // Anything the optimiser couldn't place (no coordinates) is kept rather
        // than silently dropped from the run.
        list.forEach(function(s){ if(!ordered.some(function(x){ return String(x.id)===String(s.id); })) ordered.push(s); });
        setStops(ordered);
        return { ordered:ordered, source:r.source };
      }
      throw new Error(r&&r.error||"bad");
    }catch(e){
      const good=list.filter(geoOK), bad=list.filter(function(x){ return !geoOK(x); });
      if(!good.length){ setStops(list); return { ordered:list, source:"none" }; }
      const M=estMatrix([{lat:engineSettings.breweryLat,lng:engineSettings.breweryLng}].concat(good),engineSettings);
      const order=optimiseLocal(good,engineSettings,M);
      const ordered=order.map(function(i){ return good[i]; }).concat(bad);
      setStops(ordered);
      return { ordered:ordered, source:"estimate" };
    }
  }

  async function optimise(){
    if(!stops.length) return;
    if(manualOrder&&currentRunId&&!window.confirm("This run’s stop order was set by hand. Optimise it and replace that order?")) return;
    const r=await optimiseList(stops);
    setManualOrder(false);
    showToast(r.source==="ors"?"Optimised on live roads":"Optimised (estimate)");
  }

  // override is {hours,kegs,reason} from the Route screen. A bare boolean is
  // still accepted so any older caller keeps working, and it means hours.
  async function saveRun(override){
    if(!stops.length) return;
    const ovIn=(override&&typeof override==="object")?override:{hours:!!override,kegs:false,reason:""};
    const ov=runOverride||{};
    const wantHours=!!ovIn.hours||!!ov.hours;
    const wantKegs =!!ovIn.kegs ||!!ov.kegs;
    const why=String(ovIn.reason||ov.reason||"").trim();
    const mapped=stops.map(s=>({id:s.id,name:s.name,lat:s.lat,lng:s.lng,kegs:s.kegs||0,open:s.open||"",close:s.close||"",products:s.products||[],lines:s.lines||[],note:s.note||"",orderNo:s.orderNo||""}));
    setSaveState("saving");
    try{
      if(offline()) throw new Error("no url");
      const sigNow=routeSig;
      const runPayload={runId:currentRunId,driver:runDriver||user.name,date:niceDate(runDate),slot:runSlot,orderWeek:orderWeek,scheduleMode:runMode,mustBeBackBy:runBackBy,
        overrideLong:wantHours, overrideKegs:wantKegs, overrideReason:why, manualOrder:!!manualOrder};
      // The version this screen opened. The backend refuses the save if the run
      // has been saved by anyone since, instead of quietly writing over it.
      if(currentRunId&&runSavedAt!==undefined) runPayload.savedAt=runSavedAt||"";
      const r=await api.post("saveRun",{run:runPayload,stops:mapped});
      if(r&&r.blocked){
        setSaveState(null);
        if(r.conflict){
          setSaveIssue({title:"Not saved — someone else changed this run",
            lines:[(r.reason||"This run was saved by someone else after you opened it."),
              "Your changes are still on this phone. Note them down, tap “All runs”, reopen the run to see the latest version, and make your change again."]});
          return r;
        }
        setSaveIssue({title:"Not saved — this run is flagged",
          lines:[(r.reason||"The run is over a cap or misses an opening time."),
            r.overridable?(user.admin?"Type a reason under the flags above, then tap Save run again.":"An admin needs to approve this run before it can be saved."):""].filter(Boolean)});
        // The backend names which cap was hit, so say that rather than always
        // blaming the run-time cap — and only point at \u2018Save anyway\u2019 when there
        // is actually one to tap.
        if(r.needsReason){ showToast("That override needs a reason before it can be saved."); return r; }
        showToast((r.reason||"Blocked")+(r.overridable?" Add a reason under the flags and save again.":""));
        return r;
      }
      if(r&&r.success){
        if(r.runId) setCurrentRunId(r.runId);
        setRunSavedAt(r.savedAt!==undefined?(r.savedAt||""):undefined);
        setSavedSig(sigNow); setSaveIssue(null);
        clearRouteDraft();
        reloadOrdersRuns();
        setSaveState("saved");
        setTimeout(function(){ setSaveState(null); }, 1300);
        if(r.renamedFrom) showToast("Driver changed \u2014 run is now "+r.runId);
        else if(r.prepWarning) showToast(r.prepWarning);
        // Stops taken off in the route editor are released by the backend and
        // land back in the unplanned list. Say so \u2014 otherwise a removal looks
        // identical to a save that did nothing.
        else if(r.released>0) showToast("Saved \u2014 "+r.released+(r.released===1?" venue":" venues")+" back in unplanned");
        return r;
      }
      throw new Error(r&&r.error||"bad");
    }catch(e){
      setSaveState(null);
      setSaveIssue({title:"Not saved \u2014 couldn\u2019t reach the spreadsheet",
        lines:["Your changes are still on this phone. Check signal and tap Save run again \u2014 nothing has changed on the sheet."]});
    }
  }

  // Drop a late order onto a run that's already planned, without leaving the
  // Plan screen. This used to open the run in the route editor, wait for its
  // stops to load, append, re-optimise and then need a manual Save — four
  // screens for one drop. The backend does the same planning and the same cap
  // checks in one call, and re-slots the stop into the loop rather than
  // hanging it off the end with a wrong ETA.
  // Local. The stop is slotted into the loop and the run re-timed on the spot;
  // nothing reaches the spreadsheet until Save.
  function addOrderToRun(run,customerId,reason){
    if(!run||!customerId) return;
    if(offline()){ showToast("Preview \u2014 can\u2019t change runs"); return {error:"preview"}; }
    const st=stopFromOrderLocal(customerId);
    if(!st){ showToast("That order has no map location \u2014 add an address for it first."); return {error:"no location"}; }
    const cur=(boardStops[run.runId]||[]).slice();
    if(cur.some(function(x){ return String(x.id)===String(customerId); })){
      showToast("Already on that run"); return {error:"duplicate"};
    }
    const p=reprice(run.runId, cur.concat([st]), reason?{overrideReason:reason}:null);
    setOrderRun(customerId, run.runId, "Planned");
    if(p.overKegs) showToast(st.name+" added \u2014 now over the keg cap");
    else if(p.overCap) showToast(st.name+" added \u2014 now over the hour cap");
    else showToast(st.name+" added to "+(run.driver||run.runId));
    return {success:true, added:st.name, overKegs:p.overKegs, overCap:p.overCap};
  }

  // Several orders onto one run in a single re-time. Calling addOrderToRun in a
  // loop would read the same (stale) board each time and keep only the last.
  // Used by a week suggestion that joins a run already on the board.
  function addOrdersToRun(run,customerIds,reason){
    if(!run||!customerIds||!customerIds.length) return {error:"nothing"};
    if(offline()){ showToast("Preview \u2014 can\u2019t change runs"); return {error:"preview"}; }
    const cur=(boardStops[run.runId]||[]).slice(), have={}, add=[], missing=[];
    cur.forEach(function(x){ have[String(x.id)]=true; });
    customerIds.forEach(function(id){
      if(have[String(id)]) return;
      const st=stopFromOrderLocal(id);
      if(!st){ missing.push(id); return; }
      have[String(id)]=true; add.push(st);
    });
    if(!add.length){ showToast(missing.length?"Those orders have no map location":"Already on that run"); return {error:"none"}; }
    const p=reprice(run.runId, cur.concat(add), reason?{overrideReason:reason}:null);
    add.forEach(function(st){ setOrderRun(st.id, run.runId, "Planned"); });
    const who=(run.driver||run.runId);
    if(p.overKegs) showToast(add.length+" added to "+who+" \u2014 now over the keg cap");
    else if(p.overCap) showToast(add.length+" added to "+who+" \u2014 now over the hour cap");
    else showToast(add.length+(add.length===1?" stop":" stops")+" added to "+who+". Tap Save when you\u2019re done.");
    return {success:true, added:add.length, overKegs:p.overKegs, overCap:p.overCap};
  }

  // Save a suggested run straight from the Plan screen. The stops arrive
  // already optimised (the backend ordered them, and a move re-orders them),
  // so there's nothing the route editor would add — going via it just to press
  // Save costs a screen change and a reload between every run.
  // Move a stop between two saved runs. The backend does both runs at once so
  // the sheet is never half-moved, and re-optimises each afterwards.
  // Local move between two runs, both re-timed immediately.
  function moveBetweenRuns(fromRunId,toRunId,customerId,reason){
    if(offline()){ showToast("Preview \u2014 can\u2019t move stops"); return {error:"preview"}; }
    const from=(boardStops[fromRunId]||[]).slice();
    let moved=null;
    const kept=from.filter(function(x){
      if(!moved&&String(x.id)===String(customerId)){ moved=x; return false; } return true; });
    if(!moved) moved=stopFromOrderLocal(customerId);
    if(!moved){ showToast("Couldn\u2019t find that stop"); return {error:"missing"}; }
    reprice(fromRunId, kept, null);
    const p=reprice(toRunId, (boardStops[toRunId]||[]).concat([moved]), reason?{overrideReason:reason}:null);
    setOrderRun(customerId, toRunId, "Planned");
    if(p.overKegs||p.overCap) showToast(moved.name+" moved \u2014 that run is now over a cap");
    else showToast(moved.name+" moved \u2014 both runs re-timed");
    return {success:true, moved:moved.name, overKegs:p.overKegs, overCap:p.overCap};
  }

  // Take a stop off a saved run — typically because the venue collected its own
  // order. The order goes back to Pending so it shows as unplanned rather than
  // disappearing entirely.
  // Local. The order goes back to the unplanned list straight away.
  function removeFromRun(runId,customerId){
    if(offline()){ showToast("Preview \u2014 can\u2019t change runs"); return {error:"preview"}; }
    const cur=(boardStops[runId]||[]).slice();
    let gone=null;
    const kept=cur.filter(function(x){
      if(!gone&&String(x.id)===String(customerId)){ gone=x; return false; } return true; });
    reprice(runId, kept, null);
    setOrderRun(customerId, "", "Pending");
    showToast(((gone&&gone.name)||"Stop")+" removed \u2014 back in orders to plan");
    return {success:true, removed:(gone&&gone.name)||"Stop"};
  }

  // Park an order that needs no delivery (usually collected in person), or put
  // it back. Parked orders stay on the sheet so they can still be reconciled
  // against the wholesale source — they're just out of planning.
  // Local park / unpark. Committed with the rest of the board.
  function setOrderSkip(customerId,skip){
    if(offline()){ showToast("Preview \u2014 can\u2019t change orders"); return {error:"preview"}; }
    const o=(orders||[]).filter(function(x){ return String(x.customerId)===String(customerId); })[0];
    const onRun=o&&o.runId;
    if(skip&&onRun){ showToast("That order is on a run \u2014 take it off the run first."); return {error:"on run"}; }
    setOrders(function(os){ return (os||[]).map(function(x){
      return String(x.customerId)===String(customerId)
        ? Object.assign({},x,{status:skip?"No delivery":"Pending",runId:""}) : x; }); });
    setSkipChanges(function(m2){ const n=Object.assign({},m2); n[String(customerId)]=!!skip; return n; });
    showToast(skip?(((o&&o.customerName)||"Order")+" \u2014 no delivery needed")
                  :(((o&&o.customerName)||"Order")+" back in orders to plan"));
    return {success:true, customerName:(o&&o.customerName)||""};
  }

  // "Save for <driver>" on a suggested run now puts it on the local board as a
  // new run rather than writing it immediately. It goes to the sheet with
  // everything else when you tap Save.
  function saveSuggested(newStops,date,opts){
    opts=opts||{};
    if(!newStops||!newStops.length) return {error:"no stops"};
    if(offline()){ showToast("Preview \u2014 can\u2019t save runs"); return {error:"preview"}; }
    const id="NEW-"+(newRunSeq.current++);
    const mode=(opts.scheduleMode==="early")?"early":"back";
    const ov=opts.override||{};
    const mapped=newStops.map(function(s){ return {id:s.id,name:s.name,suburb:s.suburb||"",lat:s.lat,lng:s.lng,
      kegs:s.kegs||0,open:s.open||"",close:s.close||"",products:s.products||[],lines:s.lines||[],
      note:s.note||"",orderNo:s.orderNo||""}; });
    const runDate=niceDate(opts.runDate||tomorrowISO());
    setSavedRuns(function(rs){ return (rs||[]).concat([{ runId:id, date:runDate,
      driver:opts.driver||user.name, status:"Planned", onRoad:false, totalKegs:0, estHours:0,
      latestStart:"", scheduleMode:mode, mustBeBackBy:opts.mustBeBackBy||(settings&&settings.mustBeBackBy)||"12:30",
      slot:opts.slot||"AM", orderWeek:date||"", savedAt:"", isNew:true,
      overrideReason:String(ov.reason||"").trim(), stopRows:[] }]); });
    setBoardStops(function(m2){ const n=Object.assign({},m2); n[id]=mapped; return n; });
    setDirtyRuns(function(d){ const n=Object.assign({},d); n[id]=true; return n; });
    mapped.forEach(function(st){ setOrderRun(st.id, id, "Planned"); });
    // Pass the schedule explicitly: the new run isn't in savedRuns yet from
    // this closure's point of view, and a late-opening run planned "as early as
    // possible" must not be re-timed against the morning deadline.
    setTimeout(function(){ reprice(id, mapped, null, {scheduleMode:mode,mustBeBackBy:opts.mustBeBackBy||(settings&&settings.mustBeBackBy)||"12:30"}); }, 0);
    showToast("Added to the board \u2014 "+(opts.driver||user.name)+" \u00B7 "+fmtWhen(opts.runDate||tomorrowISO(),opts.slot||"AM")+". Tap Save when you\u2019re done.");
    return {success:true, runId:id, local:true};
  }

  function buildFromOrders(newStops,date,opts){
    opts=opts||{};
    setOrderWeek(date||""); setRunDate(opts.runDate||tomorrowISO()); setRunSlot(opts.slot||"AM"); setCurrentRunId("");
    setRunMode(opts.scheduleMode==="early"?"early":"back"); setRunBackBy(opts.mustBeBackBy||(settings&&settings.mustBeBackBy)||"12:30");
    setRunDriver(opts.driver||""); setRunOverride(opts.override||null);
    setManualOrder(false); setRunSavedAt(undefined); setSavedSig(""); setSaveIssue(null);
    var ordered=newStops, brewery={lat:Number(settings.breweryLat),lng:Number(settings.breweryLng)};
    var good=newStops.filter(geoOK);
    if(geoOK(brewery)&&good.length>1){
      var M=estMatrix([brewery].concat(good),settings);
      var order=optimiseLocal(good,settings,M);
      ordered=order.map(function(i){return good[i];}).concat(newStops.filter(function(s){return !geoOK(s);}));
    }
    setStops(ordered); setScreen("route");
    showToast(ordered.length+" stops \u2014 route built, review & save");
  }
  async function openRun(runId,date,slot,week,target,mode,backBy){
    // Opening any run replaces the stops on screen. Unsaved route changes are
    // kept on the phone, but say so rather than letting them vanish quietly.
    if(routeDirty){
      var same=String(runId||"")===String(currentRunId||"");
      if(!window.confirm(same&&target==="route"
          ? "This route has unsaved changes. Reload the saved version?\n\n(Your changes stay on this phone and will be offered back.)"
          : "The route you were editing has unsaved changes.\n\nThey’ll stay on this phone and be offered back when you reopen that run. Continue?")){
        setScreen("route"); return [];
      }
    }
    const meta=(savedRuns||[]).filter(function(r){ return r.runId===runId; })[0]||null;
    const draft=readRouteDraft();
    setSaveIssue(null);
    // The run's own driver. Leaving this as whoever was last selected meant
    // saving someone else's route quietly reassigned it (and renamed its ID)
    // to the person holding the phone.
    setRunDriver((meta&&meta.driver)||"");
    setManualOrder(!!(meta&&meta.manualOrder));
    setRunSavedAt(meta?(meta.savedAt||""):undefined);
    setRunDate(isoOf(date)); setRunSlot(slot||""); setOrderWeek(week||date||""); setCurrentRunId(runId||"");
    setRunMode(mode==="early"?"early":"back"); setRunBackBy((backBy&&/^\d{1,2}:\d{2}/.test(backBy))?backBy:((settings&&settings.mustBeBackBy)||"12:30"));
    if(target==="load") setLoadPick(true);
    setStops([]); setRouteGeo(null); setLoadingRun(true); setScreen(target||"route");   // open immediately
    if(offline()){ setLoadingRun(false); return []; }
    try{
      const rs=await api.get("loadRunStops",{runId});
      const arr=Array.isArray(rs)?rs:((rs&&rs.stops)||[]);
      const byId={}; customers.forEach(c=>byId[c.id]=c);
      const ordById={}; (orders||[]).forEach(function(o){ ordById[String(o.customerId)]=o; });
      const ns=arr.map(s=>{ const c=byId[s.customerId]||{}; var k=s.kegs;
        var embedded=(Array.isArray(k)&&k.length&&typeof k[0]==="object")?k:(s.lines||null);
        var ord=ordById[String(s.customerId)];
        var lines=embedded||(ord&&ord.lines&&ord.lines.length?ord.lines:null);   // rebuild from Orders if the saved run only kept a total
        var kn = Array.isArray(k)
          ? ((k.length&&typeof k[0]==="object") ? kegsOfLines(k) : k.reduce(function(a,x){return a+(+x||0);},0))
          : (+k||0);
        if(Array.isArray(k)&&!(k.length&&typeof k[0]==="object")&&lines) kn=kegsOfLines(lines);
        var isCustom = !byId[s.customerId];   // no matching customer = ad-hoc address stop
        var lat = isFinite(Number(s.lat)) ? Number(s.lat) : c.lat;   // prefer coords saved with the run
        var lng = isFinite(Number(s.lng)) ? Number(s.lng) : c.lng;
        return {id:s.customerId,name:s.customerName||c.name,suburb:isCustom?"Custom stop":(c.suburb||""),lat:lat,lng:lng,
          open:c.open||"",close:c.close||"",kegs:kn,orderNo:s.orderNo||(ord&&ord.orderNo)||"",stopMin:c.stopMin||null,
          // Carried so a re-save recalculates tap cards and corflute instead of
          // writing empty lists over them.
          products:(lines||[]).map(function(l){ return l&&l.product; }).filter(Boolean),
          lines:lines,custom:isCustom,eta:s.eta||"",note:s.note||"",
          tapCards:Array.isArray(s.tapCards)?s.tapCards:[],
          corflute:s.corflute||""}; }).filter(s=>isFinite(s.lat)&&isFinite(s.lng));
      const dIso=isoOf(date), dMode=(mode==="early"?"early":"back");
      const dBack=(backBy&&/^\d{1,2}:\d{2}/.test(backBy))?backBy:((settings&&settings.mustBeBackBy)||"12:30");
      setSavedSig(routeSigOf(ns,dIso,slot||"",dMode,dBack,(meta&&meta.driver)||""));
      if(draft&&draft.runId===runId&&target==="route"){
        if(window.confirm("You have unsaved changes to this route from "+fmtCacheTime(draft.ts)+".\n\nRestore them? (Cancel throws them away.)")){
          setStops(draft.stops); setRunDate(draft.runDate||dIso); setRunSlot(draft.runSlot||slot||"");
          setRunMode(draft.runMode||dMode); setRunBackBy(draft.runBackBy||dBack);
          setRunDriver(draft.runDriver||(meta&&meta.driver)||""); setManualOrder(!!draft.manualOrder);
          // Keep the version the edits were made against, so if someone has
          // saved this run since, the save is stopped rather than overwriting.
          if(draft.savedAt!==undefined) setRunSavedAt(draft.savedAt);
          setLoadingRun(false);
          showToast("Unsaved changes restored \u2014 tap Save run to keep them");
          return draft.stops;
        }
        clearRouteDraft(runId);
      }
      setStops(ns); setLoadingRun(false);
      return ns;
    }catch(e){ setLoadingRun(false); showToast("Couldn\u2019t load that run\u2019s stops"); }
    return [];
  }

  if(loading) return h("div",{className:"fr",style:{paddingTop:80,textAlign:"center",color:"var(--g60)"}},"Loading\u2026");
  if(!user) return h(Login,{onPick:handleLogin});

  return h("div",{className:"fr"},
    h("div",{className:"fr-hdr"},
      h("span",{className:"fr-brand"},h("span",{className:"fr-dot"}),"FILAMENT \u00B7 RUNS"),
      h("div",{style:{display:"flex",alignItems:"center",gap:8}},
        h("button",{className:"fr-refresh"+(refreshing?" spin":""),onClick:refreshAll,title:"Refresh","aria-label":"Refresh"},refreshIcon()),
        h("span",{className:"fr-badge"+(live?(user.admin?"":" driver"):" preview")},live?(user.name+" \u00B7 "+(user.admin?"ADMIN":"DRIVER")):"PREVIEW")
      )
    ),
    (stale||bootError)?h("div",{style:{display:"flex",alignItems:"center",gap:10,padding:"10px 13px",
      margin:"0 0 4px",background:bootError?"#FFF0ED":"#FFF8E1",
      border:"1px solid "+(bootError?"#F0B0A0":"#FFE082"),borderRadius:11,
      fontSize:12.5,fontWeight:600,color:bootError?"var(--red)":"#B45309"}},
      h("span",{style:{flex:1}}, bootError
        ? ((cacheTs?("Showing the board from "+fmtCacheTime(cacheTs)+" \u2014 "):"")+"couldn\u2019t reach the spreadsheet.")
        : "Showing a saved copy of the board."),
      h("button",{className:"fr-btn out sm",style:{width:"auto",padding:"5px 10px",fontSize:11,flexShrink:0},
        onClick:function(){ setBootError(null); boot(); }},"Retry")):null,
    commitBlock?h("div",{className:"fr-card",style:{borderColor:"var(--red)",borderWidth:2,marginTop:8}},
      h("div",{className:"fr-eyebrow",style:{color:"var(--red)"}},
        commitBlock.conflicts?"Someone else changed a run":"Flagged \u2014 needs a reason once"),
      (commitBlock.conflicts||commitBlock.problems||[]).map(function(p,i){
        return h("div",{key:i,className:"fr-sub",style:{marginTop:6}},p.reason); }),
      (commitBlock.others||[]).map(function(p,i){
        return h("div",{key:"o"+i,className:"fr-sub",style:{marginTop:6,color:"var(--red)"}},p.reason); }),
      commitBlock.problems&&!(commitBlock.others||[]).length?h("div",null,
        h("input",{type:"text",value:commitBlock.reason||"",placeholder:"Reason to save it anyway (asked once per run)",
          onChange:function(e){ var v=e.target.value; setCommitBlock(function(b){ return Object.assign({},b,{reason:v}); }); },
          style:{width:"100%",marginTop:10,padding:"9px 11px",border:"1px solid var(--red)",borderRadius:9,
            fontSize:13,fontFamily:"var(--sans)",boxSizing:"border-box"}}),
        h("div",{style:{display:"flex",gap:8,marginTop:10}},
          h("button",{className:"fr-btn "+(String(commitBlock.reason||"").trim()?"ink":"disabled"),style:{flex:1},
            disabled:!String(commitBlock.reason||"").trim(),onClick:confirmOverride},"Save it anyway"),
          h("button",{className:"fr-btn out",style:{width:"auto"},
            onClick:function(){ setCommitBlock(null); showToast("Not saved"); }},"Cancel"))
      ):h("div",{style:{marginTop:10}},
        h("button",{className:"fr-btn out",onClick:function(){ setCommitBlock(null); }},"OK"))
    ):null,
    runUpdates.length?h("div",null,
      h("div",{onClick:function(){ setUpdatesOpen(function(v){ return !v; }); },
        style:{display:"flex",alignItems:"center",gap:8,padding:"10px 13px",margin:"0 0 4px",
          background:"#FFF8E1",border:"1px solid #FFE082",borderRadius:11,
          fontSize:12.5,fontWeight:700,color:"#B45309",cursor:"pointer"}},
        h("span",null,"\u26A0 "+(runUpdates.length===1?"An order on a saved run has changed":
          (runUpdates.length+" saved runs have changed orders"))),
        h("span",{style:{marginLeft:"auto",textDecoration:"underline",whiteSpace:"nowrap"}},
          updatesOpen?"Hide":"View")),
      updatesOpen?h("div",{className:"fr-card",style:{marginTop:8,borderColor:"#FFE082"}},
        runUpdates.map(function(u){
          return h("div",{key:u.runId,style:{paddingBottom:12,marginBottom:12,
            borderBottom:"1px solid var(--g10)"}},
            h("div",{style:{fontWeight:800,fontSize:15,letterSpacing:-.2}},
              (u.driver||"Run")+" \u00B7 "+(u.date||"")+(u.slot?(" \u00B7 "+u.slot):"")),
            h("div",{style:{fontFamily:"var(--mono)",fontSize:11,color:"var(--g40)",marginTop:2}},u.runId),
            u.changes.map(function(c,i){
              return h("div",{key:i,style:{marginTop:9}},
                h("div",{style:{fontSize:13,fontWeight:600}},
                  c.name+(c.gone?" \u2014 order no longer on the sheet"
                    :(": "+c.was+" \u2192 "+c.now+(c.now===1?" keg":" kegs")))),
                c.changedAt?h("div",{style:{fontSize:11.5,color:"var(--g60)",marginTop:2}},
                  "Order changed "+c.changedAt):null);
            }),
            u.savedAt?h("div",{style:{fontSize:11.5,color:"var(--g60)",marginTop:6}},
              "Run saved "+u.savedAt):null,
            h("div",{style:{fontSize:11.5,color:"var(--g60)",marginTop:8,lineHeight:1.45}},
              "Accepting re-times the run on the latest order. The van is loaded from the run, so until you do it will be packed to the older number."),
            h("button",{className:"fr-btn ink sm",style:{marginTop:10},
              disabled:!!applying,
              onClick:function(){ acceptUpdate(u.runId); }},
              applying===u.runId?"Updating\u2026":"OK \u2014 update this run"));
        })
      ):null
    ):null,
    h(Boundary,{key:screen},
      screen==="today"  && h(Today,{stops,savedRuns,currentRunId,draftRunId,admin:user.admin,me:user.name,history,histAll,histLoaded,onToggleHist:toggleHistScope,onSetDriver:changeRunDriver,onBuild:()=>setScreen("plan"),onOpen:()=>setScreen("route"),onOpenRun:openRun,onLoadRun:function(id,date,slot,week,mode,backBy){ openRun(id,date,slot,week,"load",mode,backBy); },onViewRun:function(id,date,slot,week,mode,backBy){ openRun(id,date,slot,week,"onrun",mode,backBy); }}),
      screen==="onrun"  && h(OnRun,{stops,loadingRun,runDate,runSlot,currentRunId,savedRuns,onBack:function(){ setScreen("today"); }}),
      screen==="route"  && h(Route,{stops,info,roadInfo,settings,admin:user.admin,me:user.name,runDate,setRunDate,runSlot,setRunSlot,runMode,setRunMode,runBackBy,setRunBackBy,edit,setEdit,mapEl,move,removeStop,optimise,saveRun,pickerOpen,setPickerOpen,customers,addStop,showToast,currentRunId,savedRuns,loadingRun,runDriver,setRunDriver,
        manualOrder,routeDirty,saveIssue,onDismissIssue:function(){ setSaveIssue(null); },onIssue:setSaveIssue,
        onPickRun:function(r){ openRun(r.runId,r.date,r.slot,r.orderWeek,"route",r.scheduleMode,r.mustBeBackBy); },
        onCloseRun:function(){
          if(routeDirty&&!window.confirm("This route has unsaved changes. Close it anyway?\n\n(They stay on this phone and will be offered back when you reopen the run.)")) return;
          setStops([]); setCurrentRunId(""); setRouteGeo(null); setSaveIssue(null); },
        onBuild:function(){ setScreen("plan"); },
        onLoad:function(){
          // The van is loaded from the saved run. Loading from an unsaved edit
          // would pack one thing and record another.
          if(routeDirty){ setSaveIssue({title:"Save the route first",
            lines:["You\u2019ve changed this route but not saved it. Tap Save run, then load the van."]}); return; }
          setLoadPick(true); setScreen("load"); },onDeleteDraft:function(){ setStops([]); setRouteGeo(null); setScreen("today"); showToast("Draft discarded"); },onDeleteRun:deleteRunById}),
      screen==="plan"   && h(Plan,{live,customers,settings,orders,runs:savedRuns,history,boardStops,pendingCount,onCommit:commitBoard,onDiscard:discardChanges,committing,lockInfo,lockHeld,shiftStart,onBuild:buildFromOrders,onOpenRun:openRun,onDeleteRun:deleteRunById,onSync:syncOrders,showToast,onAddToRun:addOrderToRun,onAddManyToRun:addOrdersToRun,onSaveRun:saveSuggested,driverDefault:(user&&user.name)||"",onMoveRun:moveBetweenRuns,onRemoveRun:removeFromRun,onSkipOrder:setOrderSkip}),
      screen==="load"   && h(Load,{stops,loadingRun,loadPick,currentRunId,savedRuns,runDate,runSlot,live,showToast,onPickRun:function(r){ openRun(r.runId,r.date,r.slot,r.orderWeek,"load",r.scheduleMode,r.mustBeBackBy); },onDepart:function(){ startRunById(currentRunId); },onGoToday:function(){ setScreen("today"); }}),
      screen==="settings"&& h(SettingsView,{settings,live,admin:user.admin,setSettings,showToast})
    ),
    h(Nav,{user,screen,setScreen:function(sc){ if(sc==="load") setLoadPick(false); setScreen(sc); }}),
    busyMsg && h("div",{className:"fr-busy-ov"},
      h("div",{className:"fr-busy-card"},
        h("div",{className:"fr-busy-spin"}),
        h("div",{className:"fr-busy-t"},busyMsg.title),
        h("div",{className:"fr-busy-sub"},busyMsg.sub))),
    saveState && h("div",{className:"fr-busy-ov"},
      h("div",{className:"fr-busy-card"+(saveState==="saved"?" done":"")},
        saveState==="saving"
          ? [h("div",{className:"fr-busy-spin",key:"s"}),
             h("div",{className:"fr-busy-t",key:"t"},"Saving run\u2026"),
             h("div",{className:"fr-busy-sub",key:"u"},"Writing to the spreadsheet \u2014 just a moment.")]
          : [h("div",{className:"fr-busy-check",key:"c"},"\u2713"),
             h("div",{className:"fr-busy-t",key:"t"},"Run saved")]
      )
    ),
    toast && h("div",{className:"fr-toast"},toast)
  );
}

function Login({onPick}){
  const [u,setU]=useState(""); const [p,setP]=useState(""); const [err,setErr]=useState("");
  function submit(){
    var q=u.toLowerCase().trim();
    var found=USERS.filter(function(x){return x.username===q||x.name.toLowerCase()===q;})[0];
    if(found && p.trim()===found.password){ onPick(found); }
    else setErr("Wrong username or password.");
  }
  function onKey(e){ if(e.key==="Enter") submit(); }
  return h("div",{className:"fr-login"},
    h("div",{className:"fr-login-brand"},"FILAMENT \u00B7 RUNS"),
    h("div",{className:"fr-login-title"},"Hello.",h("br"),"Sign in."),
    h("div",{className:"fr-login-sub"},"Plan the runs. Load the van. Hit the road."),
    h("div",{style:{marginTop:40}},
      h("div",{className:"fr-login-field"},
        h("div",{className:"fr-login-label"},"Username"),
        h("input",{className:"fr-login-input",value:u,autoCapitalize:"none",autoCorrect:"off",spellCheck:false,onChange:function(e){setU(e.target.value);setErr("");},onKeyDown:onKey})
      ),
      h("div",{className:"fr-login-field"},
        h("div",{className:"fr-login-label"},"Password"),
        h("input",{className:"fr-login-input",type:"password",value:p,onChange:function(e){setP(e.target.value);setErr("");},onKeyDown:onKey})
      ),
      err?h("div",{style:{color:"#FF8A7A",fontSize:12,fontWeight:600,margin:"2px 0 12px"}},err):null,
      h("button",{className:"fr-btn yellow",onClick:submit},"Sign in")
    )
  );
}

const DOW=["Sun","Mon","Tue","Wed","Thu","Fri","Sat"], MON=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function dlabel(d){ return DOW[d.getDay()]+" "+d.getDate()+" "+MON[d.getMonth()]; }
// Same broad naming the backend uses for suggested runs, so a draft and a
// suggestion describe themselves the same way. Bearings from the CBD rather
// than the brewery — see compassZone_ in RunsCode.gs.
var CBD_LAT=-31.9505, CBD_LNG=115.8605, CITY_RADIUS_KM=3.5;
function compassZone(st){
  if(!isFinite(st.lat)||!isFinite(st.lng)) return null;
  if(hav({lat:CBD_LAT,lng:CBD_LNG},{lat:st.lat,lng:st.lng})<CITY_RADIUS_KM) return "City";
  var y=st.lat-CBD_LAT, x=(st.lng-CBD_LNG)*Math.cos(CBD_LAT*Math.PI/180);
  var deg=Math.atan2(x,y)*180/Math.PI; if(deg<0) deg+=360;
  if(deg<45||deg>=315) return "North";
  if(deg<135) return "East";
  if(deg<225) return "South";
  return "West";
}
function regionOf(stops){
  var counts={}, order=[];
  (stops||[]).forEach(function(st){
    var z=compassZone(st); if(!z) return;
    if(counts[z]==null){ counts[z]=0; order.push(z); }
    counts[z]++;
  });
  if(!order.length) return "Across Perth";
  var total=order.reduce(function(a,z){ return a+counts[z]; },0);
  order.sort(function(a,b){
    if(counts[b]!==counts[a]) return counts[b]-counts[a];
    return a==="City"?-1:(b==="City"?1:0);
  });
  var top=order.filter(function(z){ return counts[z]/total>=0.25; }).slice(0,2);
  if(!top.length) top=[order[0]];
  return top.join(" & ");
}

// Runs come back dated "12 Aug 2026" — the sheet's format, not an ISO string,
// so it needs its own parse rather than Date.parse, which reads it differently
// across browsers.
function runDateTs(v){
  var p=String(v||"").trim().split(/\s+/);
  if(p.length<3) return NaN;
  var mi=MON.indexOf(p[1].slice(0,3));
  if(mi<0||!isFinite(+p[0])||!isFinite(+p[2])) return NaN;
  var d=new Date(+p[2],mi,+p[0]); d.setHours(0,0,0,0);
  return d.getTime();
}
// Monday, to match the delivery week the orders are pasted against.
function weekStartTs(){
  var n=new Date(); n.setHours(0,0,0,0);
  n.setDate(n.getDate()-((n.getDay()+6)%7));
  return n.getTime();
}

function Today({stops,savedRuns,currentRunId,draftRunId,admin,me,history,histAll,histLoaded,onToggleHist,onSetDriver,onBuild,onOpen,onOpenRun,onLoadRun,onViewRun}){
  var now=new Date();
  var [histOpen,setHistOpen]=useState(false);
  // A planned run expands in place. Reading who's on it shouldn't mean opening
  // the editor — that's a screen change, a load, and a route you might edit by
  // accident when all you wanted was to look.
  var [cardOpen,setCardOpen]=useState(false);
  // A finished run is a record, not a task: one line, expandable for the
  // venues. Nothing here is tappable through to the editor — editing a run
  // that already happened would rewrite history.
  // The stop list with its OMO column — the whole reason you open a past run,
  // and identical whether the card is this week's big one or an older slim one.
  function histStops(r){
    return (r.stopRows&&r.stopRows.length)
      ? h("div",null,
          h("div",{style:{display:"flex",gap:8,fontSize:10,fontWeight:700,color:"var(--g40)",
            textTransform:"uppercase",letterSpacing:.7,paddingBottom:6,borderBottom:"1px solid var(--g10)"}},
            h("div",{style:{flex:1,minWidth:0}},"Customer"),
            h("div",{style:{width:38,textAlign:"right",flexShrink:0}},"Kegs"),
            h("div",{style:{width:84,textAlign:"right",flexShrink:0}},"OMO")),
          r.stopRows.map(function(sr,i){
            return h("div",{key:i,style:{display:"flex",gap:8,alignItems:"baseline",fontSize:13,
              padding:"7px 0",borderBottom:"1px solid var(--g05)"}},
              h("div",{style:{flex:1,minWidth:0}},
                sr.name||"\u2014",
                sr.items?h("span",{style:{color:"var(--g40)",fontSize:11}}," \u00B7 "+sr.items+" items"):null),
              h("div",{style:{width:38,textAlign:"right",flexShrink:0,fontFamily:"var(--mono)",fontWeight:700}},sr.kegs||0),
              h("div",{style:{width:84,textAlign:"right",flexShrink:0,fontFamily:"var(--mono)",
                fontSize:11,color:"var(--g60)",overflow:"hidden",textOverflow:"ellipsis"}},sr.orderNo||"\u2014"));
          }))
      : h("div",{style:{fontSize:12,color:"var(--g60)"}},"No stops recorded");
  }

  // This week's finished runs get the full-size treatment the on-road card has.
  // Recent orders are the ones you're actually hunting for — "where did that go"
  // is answered by this week, not by a run from a fortnight ago — and a slim
  // grey row buried under Recent runs made them the hardest thing to find.
  function weekCard(r){
    var open=!!histOpen && histOpen===r.runId;
    return h("div",{className:"fr-run done",key:r.runId,style:{marginTop:8,cursor:"pointer"},
      onClick:function(){ setHistOpen(open?false:r.runId); }},
      h("div",{className:"fr-run-top"},
        h("div",null,
          h("div",{className:"fr-run-meta"},
            (r.date||"")+(r.slot?(" \u00B7 "+r.slot):"")+(r.departed?(" \u00B7 left "+r.actualStart):" \u00B7 not departed")),
          h("div",{className:"fr-run-name"},r.driver||"Run")),
        h("div",{className:"fr-run-tag done"},open?"\u25BE":"Done")
      ),
      h("div",{className:"fr-run-stats"},
        h("div",{className:"fr-dstat"},h("div",{className:"fr-dstat-v"},r.totalKegs||0),h("div",{className:"fr-dstat-l"},"Kegs")),
        h("div",{className:"fr-dstat"},h("div",{className:"fr-dstat-v"},r.stops||0),h("div",{className:"fr-dstat-l"},(r.stops===1?"Stop":"Stops"))),
        h("div",{className:"fr-dstat"},h("div",{className:"fr-dstat-v"},r.actualStart||"\u2014"),h("div",{className:"fr-dstat-l"},"Departed"))
      ),
      open?h("div",{style:{marginTop:12,paddingTop:12,borderTop:"1px solid var(--g10)"}},
        histStops(r),
        r.notes?h("div",{style:{fontSize:12,color:"var(--g60)",marginTop:9,lineHeight:1.5}},r.notes):null
      ):null,
      h("div",{className:"fr-run-cta"},
        h("div",{className:"fr-run-cta-l"},r.runId),
        h("div",{className:"fr-run-cta-r"},open?"Close":"Tap for detail"))
    );
  }

  function pastCard(r){
    var open=!!histOpen && histOpen===r.runId;
    return h("div",{className:"fr-card",key:r.runId,style:{padding:"12px 14px",marginTop:8,cursor:"pointer"},
      onClick:function(){ setHistOpen(open?false:r.runId); }},
      h("div",{style:{display:"flex",alignItems:"center",gap:10}},
        h("div",{style:{flex:1,minWidth:0}},
          h("div",{style:{fontSize:11,color:"var(--g40)",fontWeight:700,letterSpacing:.4}},
            (r.date||"")+(r.slot?(" \u00B7 "+r.slot):"")+(r.departed?(" \u00B7 left "+r.actualStart):" \u00B7 not departed")),
          h("div",{style:{fontWeight:700,fontSize:15,marginTop:2}},(r.driver||"Run")+" \u00B7 "+r.runId)),
        h("div",{style:{textAlign:"right"}},
          h("div",{className:"fr-ord-k"},r.totalKegs||0),
          h("div",{className:"fr-ord-kl"},"kegs"))),
      h("div",{style:{fontSize:12,color:"var(--g60)",marginTop:6}},
        (r.stops||0)+(r.stops===1?" stop":" stops")+" \u00B7 "+hmMin((r.estHours||0)*60)+" est"
        +(open?"":" \u00B7 tap for detail")),
      open?h("div",{style:{marginTop:10}},
        histStops(r),
        r.notes?h("div",{style:{fontSize:12,color:"var(--g60)",marginTop:9,lineHeight:1.5}},r.notes):null
      ):null);
  }
  function runCard(r){
    var open=cardOpen===r.runId;
    return h("div",{className:"fr-run ready",key:r.runId,style:{cursor:"default"}},
      h("div",{className:"fr-run-top",style:{cursor:"pointer"},
        onClick:function(){ setCardOpen(open?false:r.runId); }},
        h("div",null,
          h("div",{className:"fr-run-meta"},(r.status||"Planned")+(r.date?(" \u00B7 "+r.date):"")+(r.slot?(" \u00B7 "+r.slot):"")),
          h("div",{className:"fr-run-name"},r.driver||"Unassigned"),
          h("div",{style:{fontFamily:"var(--mono)",fontSize:12,color:"var(--g40)",marginTop:3}},
            r.runId+" \u00B7 "+((r.stopRows&&r.stopRows.length)||0)+" stops"+(open?"":" \u00B7 tap for detail"))),
        h("div",{className:"fr-run-tag go"},open?"\u25BE":"Saved")
      ),
      (draftRunId&&draftRunId===r.runId)?h("div",{className:"fr-status warn",style:{marginTop:0,marginBottom:10}},
        admin?"Unsaved route changes on this phone \u2014 tap Edit, restore them and Save run."
             :"Unsaved route changes on this phone \u2014 tap View route to restore and save them."):null,
      // Reassigning here saves reopening the route just to change who's on it.
      // The ID follows the driver, so the line above updates with it.
      admin?h("div",{style:{marginTop:10}},
        h("div",{className:"fr-when-l",style:{marginBottom:6}},"Driver"),
        h("div",{className:"fr-seg"}, USERS.map(function(u){
          return h("button",{key:u.username,className:"fr-segb"+(r.driver===u.name?" on":""),
            onClick:function(e){ e.stopPropagation(); if(r.driver!==u.name) onSetDriver&&onSetDriver(r.runId,u.name); }},u.name);
        }))
      ):null,
      h("div",{className:"fr-run-stats"},
        h("div",{className:"fr-dstat"},h("div",{className:"fr-dstat-v"},r.totalKegs||0),h("div",{className:"fr-dstat-l"},"Kegs")),
        h("div",{className:"fr-dstat"},h("div",{className:"fr-dstat-v"},hmMin((r.estHours||0)*60)),h("div",{className:"fr-dstat-l"},"Estimated")),
        h("div",{className:"fr-dstat"},h("div",{className:"fr-dstat-v"},r.latestStart||"\u2014"),h("div",{className:"fr-dstat-l"},"Leave by"))
      ),
      open?h("div",{style:{marginTop:12,paddingTop:12,borderTop:"1px solid var(--g10)"}},
        (r.stopRows&&r.stopRows.length)
          ? h("div",null,
              h("div",{style:{display:"flex",gap:8,fontSize:10,fontWeight:700,color:"var(--g40)",
                textTransform:"uppercase",letterSpacing:.7,paddingBottom:6,borderBottom:"1px solid var(--g10)"}},
                h("div",{style:{width:18,flexShrink:0}},"#"),
                h("div",{style:{flex:1,minWidth:0}},"Customer"),
                h("div",{style:{width:38,textAlign:"right",flexShrink:0}},"Kegs"),
                h("div",{style:{width:48,textAlign:"right",flexShrink:0}},"ETA")),
              r.stopRows.map(function(sr,i){
                return h("div",{key:i,style:{display:"flex",gap:8,alignItems:"baseline",fontSize:13,
                  padding:"7px 0",borderBottom:"1px solid var(--g05)"}},
                  h("div",{style:{width:18,flexShrink:0,fontFamily:"var(--mono)",color:"var(--g40)"}},sr.seq||i+1),
                  h("div",{style:{flex:1,minWidth:0}},
                    sr.name||"\u2014",
                    sr.items?h("span",{style:{color:"var(--g40)",fontSize:11}}," \u00B7 "+sr.items+" items"):null),
                  h("div",{style:{width:38,textAlign:"right",flexShrink:0,fontFamily:"var(--mono)",fontWeight:700}},sr.kegs||0),
                  h("div",{style:{width:48,textAlign:"right",flexShrink:0,fontFamily:"var(--mono)",
                    fontSize:12,color:"var(--g60)"}},sr.eta||"\u2014"));
              }))
          : h("div",{style:{fontSize:12,color:"var(--g60)"}},"No stops recorded on this run yet."),
        r.notes?h("div",{style:{fontSize:12,color:"var(--g60)",marginTop:9,lineHeight:1.5}},r.notes):null
      ):null,
      h("div",{className:"fr-run-cta",style:{gap:8}},
        h("button",{className:"fr-btn out sm",style:{flex:1},onClick:function(){ onOpenRun(r.runId,r.date,r.slot,r.orderWeek,"route",r.scheduleMode,r.mustBeBackBy); }}, admin?"Edit":"View route"),
        h("button",{className:"fr-btn ink sm",style:{flex:1},onClick:function(){ onLoadRun(r.runId,r.date,r.slot,r.orderWeek,r.scheduleMode,r.mustBeBackBy); }},"Load van")
      )
    );
  }
  // A run that's already left shows separately: green, tappable, and it drops
  // off by itself once the planner's estimated finish passes.
  function onRoadCard(r){
    return h("div",{className:"fr-run onroad",key:r.runId,style:{cursor:"pointer"},
      onClick:function(){ onViewRun(r.runId,r.date,r.slot,r.orderWeek,r.scheduleMode,r.mustBeBackBy); }},
      h("div",{className:"fr-run-top"},
        h("div",null,
          h("div",{className:"fr-run-meta"},"On the road"+(r.actualStart?(" \u00B7 left "+r.actualStart):"")+(r.slot?(" \u00B7 "+r.slot):"")),
          h("div",{className:"fr-run-name"},r.driver||"Run")),
        h("div",{className:"fr-run-tag road"},"ON RUN")
      ),
      h("div",{className:"fr-run-stats"},
        h("div",{className:"fr-dstat"},h("div",{className:"fr-dstat-v"},r.totalKegs||0),h("div",{className:"fr-dstat-l"},"Kegs")),
        h("div",{className:"fr-dstat"},h("div",{className:"fr-dstat-v"},hmMin((r.estHours||0)*60)),h("div",{className:"fr-dstat-l"},"Estimated")),
        h("div",{className:"fr-dstat"},h("div",{className:"fr-dstat-v"},r.actualStart||"\u2014"),h("div",{className:"fr-dstat-l"},"Departed"))
      ),
      h("div",{className:"fr-run-cta"},
        h("div",{className:"fr-run-cta-l"},r.runId),
        h("div",{className:"fr-run-cta-r"},"Where to next \u2192"))
    );
  }

  // A run dated on or after Monday is "this week". Anything that won't parse
  // falls through to the older list rather than being promoted on a guess.
  var wkTs=weekStartTs();
  var histWeek=(history||[]).filter(function(r){ var t=runDateTs(r.date); return !isNaN(t)&&t>=wkTs; });
  var histPrior=(history||[]).filter(function(r){ var t=runDateTs(r.date); return isNaN(t)||t<wkTs; });

  var onRoad=(savedRuns||[]).filter(function(r){ return r.onRoad; });
  var planned=(savedRuns||[]).filter(function(r){ return !r.onRoad; });
  var hasWorking=stops.length>0 && !currentRunId, hasSaved=planned.length>0;
  return h("div",null,
    h("div",{style:{marginTop:12}},
      h("div",{className:"fr-eyebrow"},"Today \u00B7 "+dlabel(now)),
      h("div",{className:"fr-title"},"Your runs."),
      h("div",{className:"fr-sub"}, onRoad.length
        ? (onRoad.length+(onRoad.length===1?" run out now":" runs out now")+(hasSaved?(" \u00B7 "+planned.length+" to come"):""))
        : (hasSaved?(planned.length+(planned.length===1?" saved run":" saved runs")+" \u00B7 tap to open"):"No runs saved yet."))
    ),
    onRoad.map(onRoadCard),
    hasSaved?planned.map(runCard):null,
    hasWorking?h("div",{className:"fr-run",style:{borderStyle:"dashed"},onClick:onOpen},
      h("div",{className:"fr-run-top"},
        h("div",null,h("div",{className:"fr-run-meta"},"Working \u00B7 unsaved"),h("div",{className:"fr-run-name"},stops.length+(stops.length===1?" stop":" stops"))),
        h("div",{className:"fr-run-tag loading"},"Draft")
      ),
      h("div",{className:"fr-run-cta"},h("div",{className:"fr-run-cta-l"},regionOf(stops)),h("div",{className:"fr-run-cta-r"},"Open \u2192"))
    ):null,
    (!hasSaved&&!hasWorking&&!onRoad.length)?h("div",{className:"fr-card",style:{textAlign:"center",padding:"26px 16px"}},
      h("div",{className:"fr-sub",style:{marginTop:0}},"No run built yet."),
      h("button",{className:"fr-btn ink sm",style:{marginTop:14,maxWidth:200,marginLeft:"auto",marginRight:"auto"},onClick:onBuild},"Build a run")
    ):null,
    h("div",{style:{marginTop:26}},
      h("div",{style:{display:"flex",alignItems:"center",gap:10}},
        h("div",{className:"fr-eyebrow",style:{flex:1}},"Recent runs"),
        admin?h("button",{className:"fr-btn out sm",style:{width:"auto",padding:"5px 10px",fontSize:11},
          onClick:onToggleHist},histAll?"Everyone":("Just "+(me||"me"))):null),
      (histWeek.length||histPrior.length)
        ? h("div",null,
            histWeek.map(weekCard),
            (histWeek.length&&histPrior.length)
              ? h("div",{className:"fr-eyebrow",style:{marginTop:18,color:"var(--g40)"}},"Earlier"):null,
            histPrior.map(pastCard))
        : h("div",{className:"fr-card",style:{padding:"18px 16px",marginTop:8}},
            h("div",{className:"fr-sub",style:{marginTop:0}},
              !histLoaded ? "Loading\u2026"
                : (histAll?"No finished runs yet.":"Nothing finished under your name yet.")))
    )
  );
}

// Read-only stop list for a run that's already left. Deliberately not
// interactive: it's the driver's "where do I go next" and the paper-equivalent
// backup when there's no signal. Ticking stops off happens in the field ops
// app, against this run's ID.
function OnRun(p){
  const {stops,loadingRun,runDate,runSlot,currentRunId,savedRuns,onBack}=p;
  var run=(savedRuns||[]).filter(function(r){ return r.runId===currentRunId; })[0]||null;
  var totalKegs=(stops||[]).reduce(function(a,b){ return a+(+b.kegs||0); },0);
  return h("div",null,
    h("div",{style:{marginTop:12}},
      h("div",{className:"fr-eyebrow"},"On the road"+(runDate?(" \u00B7 "+fmtWhen(runDate,runSlot)):"")),
      h("div",{className:"fr-title"},"Where to next."),
      h("div",{className:"fr-sub"}, loadingRun?"Fetching the stop list\u2026"
        :((stops||[]).length+" stops \u00B7 "+totalKegs+" kegs"+(run&&run.actualStart?(" \u00B7 left "+run.actualStart):"")))
    ),
    currentRunId?h("div",{className:"fr-card",style:{marginTop:12,padding:"12px 15px"}},
      h("div",{className:"fr-dk"},"Run ID"),
      h("div",{style:{fontFamily:"var(--mono)",fontSize:15,fontWeight:700,marginTop:3}},currentRunId),
      h("div",{className:"fr-sub",style:{marginTop:6,fontSize:11.5}},"Quote this in the field ops app to log deliveries against this run.")
    ):null,
    h("div",{className:"fr-card",style:{padding:"4px 16px"}},
      loadingRun
        ? h("div",{className:"fr-empty"},"Loading stops\u2026")
        : ((stops||[]).length
            ? stops.map(function(st,i){
                return h("div",{className:"fr-orstop",key:(st.id||i)+"-"+i},
                  h("div",{className:"fr-seq"+(i===0?" first":"")},i+1),
                  h("div",{style:{flex:1,minWidth:0}},
                    h("div",{className:"fr-orstop-n"},st.name),
                    h("div",{className:"fr-orstop-s"},
                      (st.custom?"Custom stop":(st.suburb||""))
                      +((+st.kegs||0)?(" \u00B7 "+st.kegs+" kegs"):"")
                      +(st.open?(" \u00B7 opens "+st.open):"")
                      +(st.close?(" \u00B7 closes "+st.close):""))),
                  st.eta?h("div",null,
                    h("div",{className:"fr-orstop-e"},st.eta),
                    h("div",{className:"fr-orstop-el"},"ETA")):null);
              })
            : h("div",{className:"fr-empty"},"No stops on this run."))
    ),
    h("button",{className:"fr-btn out",style:{marginTop:12},onClick:onBack},"Back to today")
  );
}

function Route(p){
  const {stops,info,roadInfo,settings,admin,me,runDate,setRunDate,runSlot,setRunSlot,runMode,setRunMode,runBackBy,setRunBackBy,edit,setEdit,mapEl,move,removeStop,optimise,saveRun,pickerOpen,setPickerOpen,customers,addStop,showToast,onLoad,currentRunId,onDeleteDraft,onDeleteRun,savedRuns,loadingRun,onPickRun,onCloseRun,onBuild,runDriver,setRunDriver,manualOrder,routeDirty,saveIssue,onDismissIssue,onIssue}=p;
  // Who's driving decides the run's ID, so it's set here rather than assumed
  // from whoever happened to build the route.
  const whoDrives=runDriver||me||"";
  const [ovrLong,setOvrLong]=useState(false);
  const [ovrKegs,setOvrKegs]=useState(false);
  // An override already approved on this run. It used to be written into the
  // run's notes as a sentence and never read back, which is why opening the
  // route screen made you justify the same run again every single time.
  const standingOv=(savedRuns||[]).filter(function(r){ return r.runId===currentRunId; })[0];
  const ovStored=(standingOv&&standingOv.override)||null;
  const hoursApproved=ovrLong||!!(ovStored&&ovStored.hours);
  const kegsApproved =ovrKegs||!!(ovStored&&ovStored.kegs);
  function clearStandingOverride(){
    if(!currentRunId) return;
    if(!window.confirm("Remove the override from this run? Its caps will be enforced again.")) return;
    setOvrLong(false); setOvrKegs(false); setOvrWhy("");
    api.post("clearRunOverride",{runId:currentRunId}).then(function(){
      showToast&&showToast("Override removed");
    }).catch(function(){ showToast&&showToast("Couldn\u2019t remove the override"); });
  }
  const [ovrWhy,setOvrWhy]=useState("");
  // Live roads can bring a run back under the cap after the estimate flagged
  // it. Drop the override so it isn't silently carried into the save.
  useEffect(function(){ if(roadInfo && !roadInfo.overCap) setOvrLong(false); },[roadInfo]);
  useEffect(function(){ if(roadInfo && !roadInfo.overKegs) setOvrKegs(false); },[roadInfo]);
  const avail=customers.filter(c=>!stops.some(s=>s.id===c.id));
  const [addr,setAddr]=useState("");
  const [addrWhy,setAddrWhy]=useState("");
  const [finding,setFinding]=useState(false);
  // Geocode a typed address into a custom (non-customer) stop. 0 kegs, no
  // order/tap-card logic. It's just a draft pin until Save run, so if the
  // geocoder lands wrong you can tap \u00D7 to remove it.
  async function findAddress(){
    var q=String(addr||"").trim(), why=String(addrWhy||"").trim();
    if(!q){ showToast&&showToast("Type an address or business name"); return; }
    if(finding) return;
    setFinding(true);
    try{
      var r=await api.post("geocodeAddress",{address:q});
      if(r&&r.success){
        addStop({ id:"X-"+Date.now(), name:q, suburb:"Custom stop", lat:r.lat, lng:r.lng, kegs:0, open:"", close:"", custom:true, note:why });
        setAddr(""); setAddrWhy(""); setPickerOpen(false);
        showToast&&showToast(r.partial?("Added \u2014 check the pin sits right: "+r.label):("Added: "+r.label));
      } else {
        showToast&&showToast((r&&r.error)||"Couldn\u2019t find that address");
      }
    }catch(e){ showToast&&showToast("Address lookup failed \u2014 check connection"); }
    setFinding(false);
  }
  // ---- Nothing open: offer the saved runs rather than an empty editor ----
  // Tapping Route used to land on "No stops - add one below", which gave no
  // way back to a run you'd already saved. Same picker shape as the Load tab.
  if(!stops.length){
    if(loadingRun) return h("div",null,
      h("div",{style:{marginTop:12}},
        h("div",{className:"fr-eyebrow"},"Route"),
        h("div",{className:"fr-title"},"Loading run\u2026"),
        h("div",{className:"fr-sub"},"Fetching this run\u2019s stops.")),
      h("div",{className:"fr-card",style:{textAlign:"center",padding:"30px 16px"}},
        h("div",{className:"fr-sub",style:{marginTop:0}},"Run loading\u2026")));

    // Departed runs aren't editable — their stop list is on Today instead.
    var runs=(savedRuns||[]).filter(function(r){ return !r.onRoad; });
    return h("div",null,
      h("div",{style:{marginTop:12}},
        h("div",{className:"fr-eyebrow"},"Route"),
        h("div",{className:"fr-title"}, runs.length?"Pick a run.":"No runs yet."),
        h("div",{className:"fr-sub"}, runs.length
          ? (runs.length+(runs.length===1?" saved run":" saved runs")+" \u00B7 tap to open the route")
          : "Build one from the week\u2019s orders to get started.")),
      runs.map(function(r){
        return h("div",{className:"fr-run ready",key:r.runId,style:{cursor:"pointer"},onClick:function(){ onPickRun&&onPickRun(r); }},
          h("div",{className:"fr-run-top"},
            h("div",null,
              h("div",{className:"fr-run-meta"},(r.status||"Planned")+(r.date?(" \u00B7 "+r.date):"")+(r.slot?(" \u00B7 "+r.slot):"")),
              h("div",{className:"fr-run-name"}, r.driver||"Run")),
            h("div",{className:"fr-run-tag go"}, admin?"Edit \u2192":"View \u2192")),
          h("div",{className:"fr-run-stats"},
            h("div",{className:"fr-dstat"},h("div",{className:"fr-dstat-v"}, r.totalKegs||0),h("div",{className:"fr-dstat-l"},"Kegs")),
            h("div",{className:"fr-dstat"},h("div",{className:"fr-dstat-v"}, hmMin((r.estHours||0)*60)),h("div",{className:"fr-dstat-l"},"Estimated")),
            h("div",{className:"fr-dstat"},h("div",{className:"fr-dstat-v"}, r.latestStart||"\u2014"),h("div",{className:"fr-dstat-l"},"Leave by"))));
      }),
      admin?h("button",{className:"fr-btn ink",style:{marginTop:14},onClick:function(){ onBuild&&onBuild(); }},"Build a new run"):null
    );
  }

  // The header switches to live road times the moment ORS answers, but the
  // warning banners were still reading the local straight-line estimate — which
  // is deliberately pessimistic — so "over the 3.5h cap" could sit above a run
  // the same screen was calling 3.1h. Both now read the same source.
  const capInfo = roadInfo ? {
    feasibleWindows: roadInfo.feasibleWindows !== false,
    overCap: !!roadInfo.overCap,
    overKegs: !!roadInfo.overKegs,
    bind: roadInfo.tightestStop || null,
    kegs: (roadInfo.kegs != null) ? roadInfo.kegs : (info ? info.kegs : 0),
    backLate: roadInfo.backLate || 0, backToBase: roadInfo.backToBase || ""
  } : (info ? { feasibleWindows: info.feasibleWindows, overCap: info.overCap,
                overKegs: info.overKegs, bind: info.bind, kegs: info.kegs,
                backLate: info.backLate || 0, backToBase: info.backToBase } : null);
  const routeFl = (info && !info.error && capInfo) ? runFlagList(capInfo, settings, runMode==="early" ? "" : runBackBy) : [];

  const hasTimes = info && !info.error;
  const totalMin = roadInfo?roadInfo.totalMin:(hasTimes?info.total:null);
  const leaveBy  = roadInfo?roadInfo.leaveBy:(hasTimes?toHHMM(info.leaveBy):"\u2014");
  const prepByT  = roadInfo?roadInfo.startPrepBy:(hasTimes?toHHMM(info.prepBy):"\u2014");
  const prepMin  = roadInfo?roadInfo.prepMin:(hasTimes?info.prep:null);
  return h("div",null,
    h("div",{style:{marginTop:12}},
      h("div",{style:{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8}},
        h("div",{className:"fr-eyebrow"},(runDriver?(runDriver+" \u00B7 "):"")+(runDate?fmtWhen(runDate,runSlot):"Route")),
        currentRunId?h("button",{className:"fr-btn out sm",style:{width:"auto",padding:"5px 10px",fontSize:11},
          onClick:function(){ onCloseRun&&onCloseRun(); }},"All runs"):null),
      h("div",{className:"fr-title"},"Build the loop."),
      h("div",{className:"fr-sub"},info?(info.error?(stops.length+" stops \u00B7 location needed"):(stops.length+" stops \u00B7 "+(totalMin!=null?hmMin(totalMin):"\u2014")+" est")):"Add stops to begin.")
    ),
    h("div",{className:"fr-when"},
      h("div",{className:"fr-when-l"},"When"),
      h("input",{type:"date",className:"fr-when-date",value:runDate||"",onChange:e=>setRunDate(e.target.value)}),
      h("div",{className:"fr-when-slots"},
        h("button",{className:"fr-slot"+(runSlot==="AM"?" on":""),onClick:()=>setRunSlot("AM")},"AM"),
        h("button",{className:"fr-slot"+(runSlot==="PM"?" on":""),onClick:()=>setRunSlot("PM")},"PM")
      )
    ),
    h("div",{className:"fr-sched"},
      h("div",{className:"fr-when-l",style:{marginBottom:8}},"Driver"),
      admin
        ? h("div",{className:"fr-seg"}, USERS.map(function(u){
            return h("button",{key:u.username,className:"fr-segb"+(whoDrives===u.name?" on":""),
              onClick:function(){ setRunDriver&&setRunDriver(u.name); }},u.name);
          }))
        : h("div",{className:"fr-sched-row"},h("span",{className:"fr-sched-l"},"Driving"),h("span",null,whoDrives||"\u2014")),
      h("div",{className:"fr-sched-note"}, currentRunId
        ? ("Run "+currentRunId)
        : ("Saves as "+runIdFor(whoDrives,runDate)+" \u00B7 a second run for "+(whoDrives||"a driver")+" that day gets a B on the end"))
    ),
    h("div",{className:"fr-sched"},
      h("div",{className:"fr-when-l",style:{marginBottom:8}},"Schedule"),
      h("div",{className:"fr-seg"},
        h("button",{className:"fr-segb"+(runMode==="early"?" on":""),onClick:()=>setRunMode("early")},"As early as possible"),
        h("button",{className:"fr-segb"+(runMode==="back"?" on":""),onClick:()=>setRunMode("back")},"Be back by")
      ),
      runMode==="back"
        ? h("div",{className:"fr-sched-row"},
            h("span",{className:"fr-sched-l"},"Back at base by"),
            h("input",{type:"time",className:"fr-set-input",value:runBackBy||"",onChange:e=>setRunBackBy(e.target.value)}))
        : h("div",{className:"fr-sched-note"}, (info&&!info.error&&info.firstArrival!=null&&isFinite(info.firstArrival))
            ? ("First stop hit "+toHHMM(info.firstArrival)+" \u00B7 back at base \u2248 "+toHHMM(info.backToBase))
            : "Hits the first stop right on open, then works back for drive + prep.")
    ),
    h("div",{className:"fr-card",style:{padding:0,overflow:"hidden",marginTop:14}},h("div",{id:"rmap",ref:mapEl})),
    edit?h("div",{style:{fontSize:11.5,color:"var(--g60)",margin:"8px 4px 0",lineHeight:1.4}},
      "Drag a numbered pin to correct where a venue actually is \u2014 it saves to the customer record, not just this run."):null,
    h("div",{className:"fr-depart"},
      h("div",{className:"fr-drow"},
        h("div",{style:{flex:1}},h("div",{className:"fr-dk"},"Est. run time"),h("div",{className:"fr-dv"},totalMin!=null?hmMin(totalMin):"\u2014")),
        h("div",{style:{flex:1}},h("div",{className:"fr-dk"},"Start prep by"),h("div",{className:"fr-dv"},prepByT)),
        h("div",{style:{flex:1}},h("div",{className:"fr-dk"},(info&&info.mode==="early")?"Leave at":"Leave by"),h("div",{className:"fr-dv accent"},leaveBy))
      ),
      h("div",{className:"fr-dnote"},
        (prepMin!=null?("Approx. "+Math.round(prepMin)+" min van prep"):"Add stops to see prep timing.")
        +((info&&info.mode==="early"&&!info.error&&info.backToBase!=null)?(" \u00B7 back \u2248 "+toHHMM(info.backToBase)):""))
    ),
    info && info.error==="brewery" && h("div",{className:"fr-status bad"},"Brewery location isn't set \u2014 add \u2018Brewery lat\u2019 and \u2018Brewery lng\u2019 rows in the Runs Master Settings tab, then reopen this run."),
    info && info.error==="coords" && h("div",{className:"fr-status bad"},"These stops have no map location yet \u2014 they need lat/lng in the Customers tab before they can be routed."),
    info && !info.error && info.missing>0 && h("div",{className:"fr-status warn"},info.missing+(info.missing>1?" stops are":" stop is")+" missing a location and was left out of the route."),
    // Flags, not blocks (Oct 2026): everything that used to stop a save is
    // listed here. The first time a run carries one, a reason is asked for
    // once; after that it never asks again for this run.
    routeFl.length ? h("div",{className:"fr-status "+(capInfo.feasibleWindows?"warn":"bad")},
      h("div",{style:{fontWeight:700}}, ovStored?"Flagged \u2014 already approved":("Flagged"+(roadInfo?"":" (estimate \u2014 checking live roads\u2026)"))),
      routeFl.map(function(t,i){ return h("div",{key:i,style:{marginTop:4,fontWeight:500}},"\u2022 "+t); }),
      ovStored
        ? h("div",{style:{display:"flex",alignItems:"center",gap:10,marginTop:8}},
            h("span",{style:{flex:1,fontWeight:500}},(ovStored.reason?("\u201C"+ovStored.reason+"\u201D"):"Approved")+(ovStored.at?(" \u00B7 "+ovStored.at):"")+". It won\u2019t ask again."),
            admin?h("button",{className:"fr-btn out sm",style:{width:"auto",flexShrink:0,padding:"6px 12px"},onClick:clearStandingOverride},"Remove"):null)
        : (admin
            ? h("input",{type:"text",value:ovrWhy,placeholder:"Reason to save it anyway (asked once for this run)",
                onChange:function(e){ setOvrWhy(e.target.value); },
                style:{width:"100%",marginTop:8,padding:"10px 12px",border:"1px solid var(--g10)",borderRadius:9,fontSize:13,fontFamily:"var(--sans)",boxSizing:"border-box",background:"#fff",color:"var(--ink)"}})
            : h("div",{style:{marginTop:6,fontWeight:500}},"An admin needs to approve this run once."))
    ) : null,
    h("div",{className:"fr-row"},
      h("button",{className:"fr-btn ink sm",onClick:optimise},"Optimise loop"),
      h("button",{className:"fr-btn out sm",onClick:()=>setEdit(e=>!e)},edit?"Done":"Edit")
    ),
    h("div",{className:"fr-card",style:{padding:"4px 16px"}},
      stops.length?stops.map((s,i)=>h("div",{className:"fr-stop",key:s.id+i},
        h("div",{className:"fr-seq"+(i===0?" first":"")},i+1),
        h("div",null,h("div",{className:"fr-sname"},s.name,
          s.open?h("span",{className:"fr-tag",style:{color:"var(--g60)",background:"var(--g05)"}},"opens "+s.open):null,
          s.close?h("span",{className:"fr-tag"},"closes "+s.close):null),h("div",{className:"fr-ssub"}, s.custom?(s.note?("Stop \u00B7 "+s.note):"Custom stop \u00B7 no delivery"):((s.suburb||"")+" \u00B7 "+(s.kegs||0)+" kegs"+(extrasOfLines(s.lines)?(" \u00B7 "+extrasOfLines(s.lines)+" items"):"")))),
        edit?h("div",{className:"fr-ctrls"},
          h("button",{className:"fr-mini",onClick:()=>move(i,-1)},"\u2191"),
          h("button",{className:"fr-mini",onClick:()=>move(i,1)},"\u2193"),
          h("button",{className:"fr-mini danger",onClick:()=>removeStop(i)},"\u00D7")
        ):null
      )):h("div",{className:"fr-empty"},"No stops \u2014 add one below.")
    ),
    h("button",{className:"fr-btn out",style:{marginTop:8},onClick:()=>setPickerOpen(o=>!o)},pickerOpen?"Close":"+ Add stop"),
    pickerOpen && h("div",{className:"fr-pick"},
      avail.length?avail.map(c=>h("div",{className:"fr-pickrow",key:c.id,onClick:()=>addStop(c)},c.name,h("span",null,c.suburb))):h("div",{className:"fr-empty"},"All customers added")
    ),
    pickerOpen && h("div",{style:{marginTop:8,padding:"12px 14px",border:"1px solid var(--g10)",borderRadius:11}},
      h("div",{style:{fontSize:11,color:"var(--g40)",fontWeight:700,textTransform:"uppercase",letterSpacing:1,marginBottom:8}},"Or add any stop \u00B7 pickup, supplier, errand"),
      h("div",{style:{display:"flex",gap:8}},
        h("input",{type:"text",value:addr,placeholder:"Address, or business name + suburb",
          onChange:e=>setAddr(e.target.value),
          onKeyDown:e=>{ if(e.key==="Enter") findAddress(); },
          style:{flex:1,minWidth:0,padding:"10px 12px",border:"1px solid var(--g10)",borderRadius:9,fontSize:13,fontFamily:"var(--sans)"}}),
        h("button",{className:"fr-btn ink sm",style:{width:"auto",flexShrink:0,opacity:finding?0.6:1},disabled:finding,onClick:findAddress},finding?"Finding\u2026":"Find")
      ),
      h("input",{type:"text",value:addrWhy,placeholder:"Why? e.g. collect printing, pick up regulator",
        onChange:e=>setAddrWhy(e.target.value),
        onKeyDown:e=>{ if(e.key==="Enter") findAddress(); },
        style:{width:"100%",marginTop:8,padding:"10px 12px",border:"1px solid var(--g10)",borderRadius:9,fontSize:13,fontFamily:"var(--sans)",boxSizing:"border-box"}}),
      h("div",{style:{fontSize:11,color:"var(--g40)",marginTop:6}},"Drops a 0-keg stop into the loop and optimises around it. A business name works best with its suburb \u2014 check the pin before saving.")
    ),
    (currentRunId&&manualOrder)?h("div",{style:{fontSize:11.5,color:"var(--g60)",margin:"10px 4px 0",lineHeight:1.4}},
      "Stop order set by hand \u2014 later changes from Plan slot new stops in around it instead of re-ordering the run. Optimise loop hands it back to the optimiser."):null,
    saveIssue?h("div",{className:"fr-status bad",style:{marginTop:12}},
      h("div",{style:{display:"flex",alignItems:"flex-start",gap:10}},
        h("div",{style:{flex:1,fontWeight:700}},saveIssue.title),
        h("button",{className:"fr-btn out sm",style:{width:"auto",flexShrink:0,padding:"4px 10px",fontSize:11},onClick:onDismissIssue},"OK")),
      (saveIssue.lines||[]).map(function(t,i){ return h("div",{key:i,style:{marginTop:5,fontWeight:500}},t); })
    ):null,
    routeDirty?h("div",{style:{fontSize:12,fontWeight:700,color:"#B45309",margin:"12px 4px 0"}},
      "\u25CF Unsaved changes \u2014 tap Save run to keep them"):null,
    h("button",{className:"fr-btn yellow",style:{marginTop:8},onClick:function(){
      var why=String(ovrWhy||"").trim();
      // Only stop the save here on live road times. The straight-line estimate
      // shown straight after a reorder reads long and could flag a run that's
      // fine; the backend checks on real roads and answers either way.
      if(roadInfo&&routeFl.length&&!ovStored&&!why){
        onIssue&&onIssue({title:"Not saved \u2014 this run is flagged",
          lines:routeFl.concat([admin?"Type a reason under the flags above, then tap Save run again.":"An admin needs to approve this run before it can be saved."])});
        return;
      }
      saveRun({hours:!!why,kegs:!!why,reason:why});
    }},"Save run"),
    currentRunId?h("button",{className:"fr-btn out",style:{marginTop:8},onClick:onLoad},"Load van \u2192"):null,
    (!currentRunId&&stops.length)?h("button",{className:"fr-btn ink",style:{marginTop:8},onClick:function(){ if(window.confirm("Discard this draft run? It hasn\u2019t been saved.")) onDeleteDraft(); }},"Delete draft"):null,
    currentRunId?h("button",{className:"fr-btn danger",style:{marginTop:8},onClick:function(){ if(window.confirm("Delete this run? Its orders go back to available for planning.")) onDeleteRun(currentRunId); }},"Delete run"):null
  );
}

function SettingsView({settings,live,admin,setSettings,showToast}){
  const [form,setForm]=useState(null);
  const [saving,setSaving]=useState(false);
  useEffect(function(){
    setForm({
      maxKegs:settings.maxKegs, maxHours:settings.maxHours, serviceMin:settings.serviceMin,
      prepSec:Math.round((settings.prepPerKeg||0)*60), slotFactor:settings.slotFactor,
      breweryLat:settings.breweryLat, breweryLng:settings.breweryLng,
      roadFactor:settings.roadFactor, avgSpeed:settings.avgSpeed,
      planFillHours:settings.planFillHours, planMaxExtraMin:settings.planMaxExtraMin,
      planReliefTolMin:settings.planReliefTolMin, lateAfter:settings.lateAfter||"",
      friRunHours:settings.friRunHours,
      friEarlyBackBy:settings.friEarlyBackBy||"", friLateBackBy:settings.friLateBackBy||"",
      planDriver:settings.planDriver||"", planTodayUntil:settings.planTodayUntil||""
    });
  },[settings]);

  // ----- read-only (drivers) -----
  if(!admin){
    var fin=function(v){return typeof v==="number"&&isFinite(v);};
    var brewery=fin(settings.breweryLat)&&fin(settings.breweryLng)?settings.breweryLat.toFixed(4)+", "+settings.breweryLng.toFixed(4):"not set";
    var rows=[["Max kegs per run",settings.maxKegs],["Max hours per run",settings.maxHours],["Default stop time",settings.serviceMin+" min"],["Brewery",brewery]];
    return h("div",null,
      h("div",{style:{marginTop:12}},h("div",{className:"fr-eyebrow"},"Account"),h("div",{className:"fr-title"},"The rules.")),
      h("div",{className:"fr-card"},rows.map(function(r,i){ return h("div",{key:i,style:{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:i<rows.length-1?"1px solid var(--g05)":"none",fontSize:14}},h("span",{style:{color:"var(--g60)"}},r[0]),h("span",{style:{fontWeight:600,fontFamily:"var(--mono)"}},String(r[1]))); })),
      h("div",{className:"fr-sub",style:{marginTop:12}},"Only an admin can change these.")
    );
  }

  if(!form) return null;
  function set(k,v){ setForm(function(f){ var n=Object.assign({},f); n[k]=v; return n; }); }
  function numInput(k,opts){
    opts=opts||{};
    return h("input",{className:"fr-set-input",type:"text",inputMode:opts.dec?"decimal":"numeric",
      value:(form[k]==null||(typeof form[k]==="number"&&!isFinite(form[k])))?"":String(form[k]),
      onChange:function(e){ var raw=e.target.value.trim(); set(k, raw===""?"":(opts.dec?parseFloat(raw):parseInt(raw,10))); }});
  }
  function textInput(k,ph){
    return h("input",{className:"fr-set-input",type:"text",placeholder:ph||"",
      value:form[k]==null?"":String(form[k]),
      onChange:function(e){ set(k, e.target.value); }});
  }
  function row(label,hint,inputEl){
    return h("div",{className:"fr-set-row"},
      h("div",{className:"fr-set-l"}, label, hint?h("div",{className:"fr-set-hint"},hint):null),
      inputEl);
  }
  async function save(){
    if(saving) return;
    setSaving(true);
    var payload={ settings:{
      maxKegs:+form.maxKegs, maxHours:+form.maxHours, serviceMin:+form.serviceMin,
      prepPerKeg:(+form.prepSec||0)/60, slotFactor:+form.slotFactor,
      breweryLat:+form.breweryLat, breweryLng:+form.breweryLng,
      roadFactor:+form.roadFactor, avgSpeed:+form.avgSpeed,
      planFillHours:+form.planFillHours, planMaxExtraMin:+form.planMaxExtraMin,
      planReliefTolMin:+form.planReliefTolMin, friRunHours:+form.friRunHours,
      lateAfter:String(form.lateAfter||"").trim(),
      friEarlyBackBy:String(form.friEarlyBackBy||"").trim(), friLateBackBy:String(form.friLateBackBy||"").trim(),
      planDriver:String(form.planDriver||"").trim(),
      planTodayUntil:String(form.planTodayUntil||"").trim()
    }};
    try{
      if(offline()){ setSettings(Object.assign({},settings,payload.settings)); showToast("Saved (preview \u2014 not written to the sheet)"); setSaving(false); return; }
      var r=await api.post("saveSettings",payload);
      if(r&&r.success&&r.settings){ setSettings(r.settings); showToast("Settings saved"); }
      else showToast("Couldn\u2019t save settings");
    }catch(e){ showToast("Couldn\u2019t save settings"); }
    setSaving(false);
  }
  return h("div",null,
    h("div",{style:{marginTop:12}},h("div",{className:"fr-eyebrow"},"Settings"),h("div",{className:"fr-title"},"Rules."),
      h("div",{className:"fr-sub"},live?"Edit and save \u2014 writes straight to the Runs Master sheet.":"Preview mode \u2014 changes won\u2019t reach the sheet until the backend is connected.")),
    h("div",{className:"fr-card"},
      row("Max kegs per run","Planner warns above this", numInput("maxKegs")),
      row("Max hours per run","Hard cap on a run", numInput("maxHours",{dec:true})),
      row("Default stop time","Minutes \u2014 used when the Regions tab has no Stop minutes", numInput("serviceMin")),
      row("Prep per keg","Seconds, for van-prep time", numInput("prepSec")),
      row("9L slot factor","A 9L counts as this vs an 18L", numInput("slotFactor",{dec:true}))
    ),
    h("div",{className:"fr-sub",style:{marginTop:8,fontSize:12}},"Return time is now set per run on the Route screen (as early as possible / be back by)."),
    h("div",{className:"fr-eyebrow",style:{margin:"18px 2px 0"}},"Week planner (Suggest week)"),
    h("div",{className:"fr-card",style:{marginTop:8}},
      row("Fill earlier runs to","Hours \u2014 stop pulling orders forward past this", numInput("planFillHours",{dec:true})),
      row("Max extra drive","Minutes a stockist may add to a run it isn\u2019t usually on", numInput("planMaxExtraMin")),
      row("Relief tolerance","Minutes a move may cost the week when it eases a busier day", numInput("planReliefTolMin")),
      row("Late orders after","Named in the notes, e.g. Tue 20:00", textInput("lateAfter","Tue 20:00")),
      row("Friday run length","Hours, each of the two Friday runs", numInput("friRunHours",{dec:true})),
      row("Fri early run back by","HH:mm \u2014 sets when it leaves, not a cap", textInput("friEarlyBackBy","09:00")),
      row("Fri late run back by","HH:mm \u2014 sets when it leaves, not a cap", textInput("friLateBackBy","13:00")),
      row("Default driver","Pre-filled on suggested runs", textInput("planDriver","")),
      row("Plan today until","HH:mm \u2014 after this, Suggest week leaves today out", textInput("planTodayUntil","12:00"))
    ),
    h("div",{className:"fr-sub",style:{marginTop:8,fontSize:12}},"Who goes on which day \u2014 usual day, fixed or flexible, can hold stock early, Friday slot, stop minutes \u2014 lives in the Regions tab of the Runs Master."),
    h("div",{className:"fr-eyebrow",style:{margin:"18px 2px 0"}},"Brewery & fallback"),
    h("div",{className:"fr-card",style:{marginTop:8}},
      row("Brewery lat","Loop start/end", numInput("breweryLat",{dec:true})),
      row("Brewery lng",null, numInput("breweryLng",{dec:true})),
      row("Road factor","Used only if live roads fail", numInput("roadFactor",{dec:true})),
      row("Avg speed km/h","Fallback estimate", numInput("avgSpeed",{dec:true}))
    ),
    h("button",{className:"fr-btn yellow",style:{marginTop:14},disabled:saving,onClick:save}, saving?"Saving\u2026":"Save settings")
  );
}

function Placeholder({title,note}){
  return h("div",null,
    h("div",{style:{marginTop:12}},h("div",{className:"fr-eyebrow"},"Coming next"),h("div",{className:"fr-title"},title)),
    h("div",{className:"fr-card"},h("div",{className:"fr-sub"},note))
  );
}

// ============================================================
// Products + orders (Plan)
// ============================================================
var PRODUCT_COLOURS={"Classic (Nitro)":"#C43E2A","Choc Banana (N)":"#C49A1A","Lamington (N)":"#6B3A2A","Choc Mint (Nitro)":"#2A8B6A","Choc Orange (Nitro)":"#D4870A","Summer (Nitro)":"#D4598A","Iced Espresso":"#2563A8"};
// Matched on the bare product name, so the suffix doesn't decide the colour.
// The three apps disagree about it \u2014 Ops and Brewery key on "Choc Orange",
// Runs on "Choc Mint (Nitro)" and "Choc Banana (N)" \u2014 and a product arriving
// from the wholesale sheet without a canonical mapping keeps whatever form the
// sheet used. An exact-match lookup silently greys those out, which is what
// happened to Choc Orange.
function prodColour(p){
  if(!p) return "#A8A8A0";
  if(PRODUCT_COLOURS[p]) return PRODUCT_COLOURS[p];
  var k=prodShort(p).toLowerCase();
  for(var name in PRODUCT_COLOURS) if(prodShort(name).toLowerCase()===k) return PRODUCT_COLOURS[name];
  return "#A8A8A0";
}
function prodShort(p){ return String(p||"").replace(/\s*\((nitro|n)\)\s*$/i,"").trim(); }

// Preview-mode sample orders (used when the backend isn't connected).
var FALLBACK_ORDERS=[
  {customerId:"3",customerName:"Be Free Cafe",date:"13 Jun 2026",status:"Pending",lines:[{product:"Classic (Nitro)",size:"18L",qty:2,priority:"Normal",notes:""},{product:"Choc Mint (Nitro)",size:"9L",qty:1,priority:"Normal",notes:""}]},
  {customerId:"8",customerName:"Daisies Cottesloe",date:"13 Jun 2026",status:"Pending",lines:[{product:"Iced Espresso",size:"18L",qty:1,priority:"Normal",notes:""}]},
  {customerId:"13",customerName:"Fields Of Vincent",date:"13 Jun 2026",status:"Pending",lines:[{product:"Classic (Nitro)",size:"18L",qty:3,priority:"Normal",notes:""}]},
  {customerId:"44",customerName:"Someday Coffee Co",date:"13 Jun 2026",status:"Pending",lines:[{product:"Classic (Nitro)",size:"18L",qty:1,priority:"Normal",notes:""},{product:"Lamington (N)",size:"9L",qty:1,priority:"Normal",notes:""}]}
];

// Survives leaving the Plan tab, but not a page reload. The app renders
// screen==="plan" && h(Plan,...) inside a Boundary keyed on the screen, so the
// whole component unmounts on every navigation and takes the suggestion with
// it. Coming back remounted with autoTried false and silently recomputed the
// whole plan — slow, and it looks broken when you're showing someone how the
// app works. Cleared deliberately by Sync and Suggest, never by navigating.
var PLAN_CACHE = { sugg:null, assign:{}, ovr:{}, autoTried:false, sugSrc:null, outMin:"", sugOpen:{}, weekNotes:null, planOpts:null };
// Which days Suggest week plans by default (Oct 2026). Tue-Fri still ahead
// this week; today only until planTodayUntil (runs leave in the morning, so at
// 5pm today's are done). Nothing left (Friday afternoon, weekend) -> next week.
var PLAN_DAY_NAMES=["Tue","Wed","Thu","Fri"], PLAN_DAY_DOW=[2,3,4,5];
function autoPlanDays(s){
  var n=new Date(), dow=n.getDay(), nowMin=n.getHours()*60+n.getMinutes();
  var cut=toMin((s&&s.planTodayUntil)||"12:00"); if(cut==null) cut=720;
  if(dow===0||dow===6) return { days:PLAN_DAY_NAMES.slice(), fromDate:"", nextWeek:true };
  var out=[];
  PLAN_DAY_NAMES.forEach(function(d,i){ var o=PLAN_DAY_DOW[i];
    if(o>dow||(o===dow&&nowMin<cut)) out.push(d); });
  if(out.length) return { days:out, fromDate:"", nextWeek:false };
  var m=new Date(n.getFullYear(),n.getMonth(),n.getDate()+(8-dow));
  return { days:PLAN_DAY_NAMES.slice(), nextWeek:true,
    fromDate:m.getFullYear()+"-"+("0"+(m.getMonth()+1)).slice(-2)+"-"+("0"+m.getDate()).slice(-2) };
}
function planCacheReset(){
  // outlierMinutes is a setting, not part of the suggestion — it survives.
  PLAN_CACHE = { sugg:null, assign:{}, ovr:{}, autoTried:false, sugSrc:null, outMin:PLAN_CACHE.outMin, sugOpen:{}, weekNotes:null, planOpts:PLAN_CACHE.planOpts };
}

function Plan({live,customers,settings,orders,runs,history,boardStops,pendingCount,onCommit,onDiscard,committing,lockInfo,lockHeld,shiftStart,onBuild,onOpenRun,onDeleteRun,onSync,showToast,onAddToRun,onAddManyToRun,onSaveRun,driverDefault,onMoveRun,onRemoveRun,onSkipOrder}){
  const [sel,setSel]=useState({});
  const [openOrders,setOpenOrders]=useState(false);
  const [openRuns,setOpenRuns]=useState(false);
  const [openDone,setOpenDone]=useState(false);
  const [expRun,setExpRun]=useState({});
  const [syncing,setSyncing]=useState(false);
  const [resolved,setResolved]=useState({});   // customerId -> {lat,lng,label,reason}
  const [addrOpen,setAddrOpen]=useState("");   // customerId whose form is open
  const [addrVal,setAddrVal]=useState("");
  const [reasonVal,setReasonVal]=useState("");
  const [finding,setFinding]=useState(false);
  const [sugg,setSugg]=useState(PLAN_CACHE.sugg);          // [{stops,outlier,region}]
  const [weekNotes,setWeekNotes]=useState(PLAN_CACHE.weekNotes);   // notes from the last Suggest week
  const [suggesting,setSuggesting]=useState(false);
  const [sugOpen,setSugOpen]=useState(PLAN_CACHE.sugOpen);
  const [moving,setMoving]=useState(null);      // {run, stop}
  const [outMin,setOutMin]=useState(PLAN_CACHE.outMin);
  const [sugSrc,setSugSrc]=useState(PLAN_CACHE.sugSrc);      // {source,note} from the last suggest
  const [savingRun,setSavingRun]=useState(-1);  // index mid-save
  const [movingSaved,setMovingSaved]=useState(null);  // {runId, custId}
  const [moveBusy,setMoveBusy]=useState(false);
  const [moveAsk,setMoveAsk]=useState(null);    // {from,to,cust,why,reason}
  const [dropAsk,setDropAsk]=useState(null);    // {runId,cust,name} awaiting confirmation
  const [addAsk,setAddAsk]=useState(null);      // {run,cust,why,reason} — quick add over a cap

  async function doRemove(){
    var a=dropAsk; if(!a) return;
    setDropAsk(null); setMovingSaved(null); setMoveBusy(true);
    await onRemoveRun(a.runId,a.cust);
    setMoveBusy(false);
  }

  // Blocked adds ask for a reason in the page, same as a blocked move, rather
  // than failing with a toast you can't act on.
  async function doAdd(run,custId){
    if(moveBusy) return;
    setMoveBusy(true);
    var res=await onAddToRun(run,custId,"");
    setMoveBusy(false);
    if(res&&res.blocked&&res.overridable){
      setAddAsk({run:run,cust:custId,why:res.reason||"That would go over a cap.",reason:""});
      return;
    }
    if(res&&res.blocked) showToast("Blocked: "+(res.reason||"can\u2019t add"));
  }

  async function confirmAddOverride(){
    var a=addAsk; if(!a||!String(a.reason||"").trim()) return;
    setAddAsk(null); setMoveBusy(true);
    var res=await onAddToRun(a.run,a.cust,String(a.reason).trim());
    setMoveBusy(false);
    if(res&&res.blocked) showToast("Blocked: "+(res.reason||"can\u2019t add"));
  }

  async function doMoveSaved(fromRunId,toRunId,custId){
    if(moveBusy) return;
    setMoveBusy(true);
    var res=await onMoveRun(fromRunId,toRunId,custId,"");
    setMoveBusy(false);
    if(res&&res.blocked&&res.overridable){
      // Ask in the page rather than a native prompt — you can see the run, the
      // reason and the numbers while deciding, and Cancel means cancel.
      setMoveAsk({from:fromRunId,to:toRunId,cust:custId,why:res.reason||"That would go over a cap.",reason:""});
      return;
    }
    if(res&&res.blocked) showToast("Blocked: "+(res.reason||"can\u2019t move"));
    setMovingSaved(null);
  }

  async function confirmMoveOverride(){
    var a=moveAsk; if(!a||!String(a.reason||"").trim()) return;
    setMoveAsk(null); setMoveBusy(true);
    var res=await onMoveRun(a.from,a.to,a.cust,String(a.reason).trim());
    setMoveBusy(false); setMovingSaved(null);
    if(res&&res.blocked) showToast("Blocked: "+(res.reason||"can\u2019t move"));
  }

  // Save without leaving the screen, then drop that card so the next one is
  // ready to allocate.
  async function saveOne(ri,r,a,o,blocked){
    if(savingRun>=0) return;
    var target=r.addTo?savedTarget(r):null;
    if(target){
      // Joins the saved run. The prune effect drops the card once its orders
      // show as planned.
      setSavingRun(ri);
      onAddManyToRun(target, r.stops.map(function(x){ return x.id; }), blocked?String(o.reason||"").trim():"");
      setSavingRun(-1);
      return;
    }
    setSavingRun(ri);
    var res=await onSaveRun(r.stops.map(function(x){ return Object.assign({},x); }),
      (orders&&orders[0]&&orders[0].date)||"",
      { driver:a.driver||driverDefault, runDate:a.date, slot:a.slot, scheduleMode:r.scheduleMode, mustBeBackBy:r.mustBeBackBy||"",
        override: blocked?{ hours:true, kegs:true, reason:String(o.reason||"").trim() }:null });
    setSavingRun(-1);
    if(res&&res.success) setSugg(function(list){ return list.filter(function(_,i){ return i!==ri; }); });
  }
  const [assign,setAssign]=useState(PLAN_CACHE.assign);        // run index -> {driver,date,slot}
  const [ovr,setOvr]=useState(PLAN_CACHE.ovr);                 // run index -> {on,reason}
  const [autoTried,setAutoTried]=useState(PLAN_CACHE.autoTried);
  // Suggest week options. days:null = automatic (see autoPlanDays); an array
  // once the planner has ticked days by hand.
  const [planOpts,setPlanOpts]=useState(PLAN_CACHE.planOpts||{ days:null, runsPerDay:0, mode:"usual" });
  useEffect(function(){ PLAN_CACHE.planOpts=planOpts; },[planOpts]);
  const autoDays=autoPlanDays(settings);
  const shownDays=planOpts.days||autoDays.days;
  function toggleDay(d){ setPlanOpts(function(o){
    var cur=(o.days||autoDays.days).slice(), at=cur.indexOf(d);
    if(at>=0){ if(cur.length===1) return o; cur.splice(at,1); } else cur.push(d);
    cur.sort(function(a,b){ return PLAN_DAY_NAMES.indexOf(a)-PLAN_DAY_NAMES.indexOf(b); });
    return Object.assign({},o,{days:cur}); }); }
  const [addTo,setAddTo]=useState("");          // customerId whose run picker is open

  // Mirror the in-progress plan out to the cache so the next mount picks it up.
  useEffect(function(){
    PLAN_CACHE.sugg=sugg; PLAN_CACHE.assign=assign; PLAN_CACHE.ovr=ovr;
    PLAN_CACHE.autoTried=autoTried; PLAN_CACHE.sugSrc=sugSrc;
    PLAN_CACHE.outMin=outMin; PLAN_CACHE.sugOpen=sugOpen; PLAN_CACHE.weekNotes=weekNotes;
  },[sugg,assign,ovr,autoTried,sugSrc,outMin,sugOpen,weekNotes]);

  // The suggestion IS the plan, so it's what you land on. Fires once per visit,
  // only when there's something unplanned to work with.
  useEffect(function(){
    if(autoTried||sugg||suggesting) return;
    if(!orders||!orders.length) return;
    var toPlan=orders.filter(function(o){ return !assignedOf(o)&&!flaggedOf(o)&&!skippedOf(o); }).length;
    if(!toPlan) return;
    setAutoTried(true);
    // The week planner is what you land on. An older backend answers
    // "Unknown action", and that falls back to the balanced split quietly.
    doSuggestWeek(true);
  },[orders]);

  // A suggestion is a snapshot of the orders at the moment it was made. Park an
  // order, save a run, or add one to an existing run, and any suggestion still
  // holding that stop is out of date — prune it rather than let it propose a
  // stop that no longer needs planning.
  useEffect(function(){
    if(!sugg) return;
    var ok={};
    (orders||[]).forEach(function(o){ if(!assignedOf(o)&&!skippedOf(o)) ok[String(o.customerId)]=true; });
    var before=sugg.reduce(function(a,r){ return a+r.stops.length; },0);
    var next=sugg.map(function(r){
      var kept=r.stops.filter(function(st){ return ok[String(st.id)]; });
      return kept.length===r.stops.length ? r : Object.assign({},r,{stops:kept,stale:true});
    }).filter(function(r){ return r.stops.length; });
    var after=next.reduce(function(a,r){ return a+r.stops.length; },0);
    if(after===before) return;
    setSugg(next.length?next:null);
    // Anything that lost a stop needs its timing redone.
    next.forEach(function(r,i){ if(r.stale) retime(i,r.stops,r); });
  },[orders]);

  function setAsg(ri,k,v){
    setSugg(function(list){
      if(!list||!list[ri]) return list;
      var n=list.slice(), as=Object.assign({},n[ri].asg||{}); as[k]=v;
      n[ri]=Object.assign({},n[ri],{asg:as}); return n; });
  }
  function asgOf(ri){
    var a=(sugg&&sugg[ri]&&sugg[ri].asg)||{};
    return { driver:a.driver||"", date:a.date||tomorrowISO(), slot:a.slot||"AM" };
  }

  // Ask the backend to split the unplanned orders into balanced runs. It does
  // the clustering server-side because that's where the drive matrix lives.
  async function doSuggest(){
    if(suggesting) return;
    setSuggesting(true);
    try{
      var r=await api.post("suggestRuns", outMin?{outlierMinutes:Number(outMin)}:{});
      if(r&&r.success){
        // Keep the backend's numbers. They come from the live road matrix; the
        // local estimator is straight-line and reads far more pessimistic, so
        // recomputing here made runs look over cap when they weren't.
        setSugg(r.runs.map(function(x){ return {stops:x.stops,outlier:!!x.outlier,region:x.region,
          lateOpen:!!x.lateOpen,opensAt:x.opensAt||"",scheduleMode:x.scheduleMode||"back",
          m:{totalMin:x.totalMin,leaveBy:x.leaveBy,totalKegs:x.totalKegs,overCap:x.overCap,overKegs:x.overKegs,feasible:x.feasibleWindows,
             backLate:x.scheduleMode==="early"?0:(x.backLate||0),backToBase:x.backToBase||"",tightestStop:x.tightestStop},
          stale:false}; }));
        setSugOpen({}); setMoving(null); setWeekNotes(null); setAssign({});
        if(!outMin && r.outlierMinutes) setOutMin(String(r.outlierMinutes));
        setSugSrc({source:r.source,note:r.note||""});
        showToast(r.runs.length+" runs suggested"+(r.skipped&&r.skipped.length?(" \u00B7 "+r.skipped.length+" skipped, no location"):""));
      } else showToast((r&&r.error)||"Couldn\u2019t suggest runs");
    }catch(e){ showToast("Couldn\u2019t suggest runs \u2014 check signal"); }
    setSuggesting(false);
  }

  // Plan the week from the Regions tab: every stockist on its usual day, then
  // earlier runs filled from later in the week where it saves time or eases a
  // busier day. Day, driver and slot come back pre-filled on each run.
  async function doSuggestWeek(fallbackQuietly){
    if(suggesting) return;
    setSuggesting(true);
    try{
      // The board goes with it, unsaved runs included, so a day that already
      // has a run gets new stops added to it rather than a second run.
      var existing=(runs||[]).filter(function(x){ return !x.onRoad; }).map(function(x){
        var bs=boardStops&&boardStops[x.runId];
        return { runId:x.runId, date:isoOf(x.date), slot:x.slot||"", driver:x.driver||"",
          scheduleMode:x.scheduleMode||"back", mustBeBackBy:x.mustBeBackBy||"",
          stops: bs ? bs.map(function(st){ return {id:st.id,name:st.name,lat:st.lat,lng:st.lng,kegs:+st.kegs||0,
                        open:st.open||"",close:st.close||"",stopMin:st.stopMin||null}; })
                    : (x.stopRows||[]).map(function(st){ return {id:st.customerId,name:st.name,kegs:+st.kegs||0}; }) };
      });
      var auto=autoPlanDays(settings);
      var r=await api.post("suggestWeek",{existing:existing, days:planOpts.days||auto.days,
        fromDate:planOpts.days?"":auto.fromDate, runsPerDay:planOpts.runsPerDay||0, mode:planOpts.mode||"usual"});
      if(r&&r.error&&/unknown action/i.test(String(r.error))){
        setSuggesting(false);
        if(fallbackQuietly) return doSuggest();
        showToast("Suggest week needs the new backend deployed \u2014 using Suggest runs");
        return doSuggest();
      }
      if(r&&r.success){
        var list=r.runs.map(function(x){
          var lastOpen=(x.stops||[]).reduce(function(a,st){ var op=toMin(st.open); return (op!=null&&op>=LATE_OPENER_FROM&&(a==null||op>a))?op:a; },null);
          return {stops:x.stops, week:true, kind:x.kind, day:x.day, title:x.title, addTo:x.addTo||"",
          outlier:x.kind==="adhoc", region:x.region, lateOpen:lastOpen!=null, opensAt:lastOpen!=null?toHHMM(lastOpen):"",
          scheduleMode:x.scheduleMode||"back", mustBeBackBy:x.mustBeBackBy||"", moved:x.moved||0,
          asg:{ driver:x.driver||"", date:x.date, slot:x.slot||"AM" },
          m:{totalMin:x.totalMin,leaveBy:x.leaveBy,totalKegs:x.totalKegs,overCap:x.overCap,overKegs:x.overKegs,
             feasible:x.feasibleWindows,tightestStop:x.tightestStop,
             backLate:x.scheduleMode==="early"?0:(x.backLate||0),backToBase:x.backToBase||""},
          stale:false}; });
        setSugg(list.length?list:null); setAssign({}); setOvr({}); setSugOpen({}); setMoving(null);
        setSugSrc({source:r.source,note:r.note||""}); setWeekNotes(r.notes||[]);
        showToast(r.runs.length+(r.runs.length===1?" run":" runs")+" for the week \u00B7 "
          +(r.moves||0)+(r.moves===1?" move":" moves")+" earlier");
      } else showToast((r&&r.error)||"Couldn\u2019t suggest the week");
    }catch(e){ showToast("Couldn\u2019t suggest the week \u2014 check signal"); }
    setSuggesting(false);
  }

  // Re-time a run on the backend after it changes. optimiseRun re-orders the
  // stops on live roads and hands back the real duration, so a moved stop gets
  // slotted into the loop rather than stuck on the end with a guessed ETA.
  // Re-time a suggested run on the spot, using the optimiser the app already
  // carries. This used to be a round trip per affected run — so moving one stop
  // between two suggestions meant two calls, each of which re-read the Ops
  // master once per stop before it answered. Straight-line rather than road
  // distances, so the figures read LONG; the real ones are worked out on Save.
  // The saved run a suggestion joins, if it's still on the board and hasn't left.
  function savedTarget(r){
    if(!r||!r.addTo) return null;
    return (runs||[]).filter(function(x){ return x.runId===r.addTo&&!x.onRoad; })[0]||null;
  }
  function savedStopsOf(r){ var t=savedTarget(r); return t?((boardStops&&boardStops[t.runId])||[]):[]; }

  function retime(ri,stopsList,meta){
    if(!stopsList||!stopsList.length) return;
    // Joining a saved run: time the run as it would be with them added, but the
    // card still lists only the new stops.
    var base=(meta&&meta.addTo)?savedStopsOf(meta):[];
    var keepNew=stopsList;
    if(base.length) stopsList=base.concat(stopsList);
    var brewery={lat:Number(settings.breweryLat),lng:Number(settings.breweryLng)};
    var good=stopsList.filter(geoOK);
    var kegs=stopsList.reduce(function(a,b){ return a+(+b.kegs||0); },0);
    var ordered=stopsList, m={ totalMin:null, leaveBy:"", totalKegs:kegs,
      overCap:false, overKegs:kegs>(settings.maxKegs||25), feasible:true };
    if(geoOK(brewery)&&good.length){
      // A week suggestion carries its own schedule — Friday's early run is back
      // by 9, its late run by 1, and both are shorter than a weekday run.
      var mt=meta||{}, friRun=(mt.kind==="early"||mt.kind==="late");
      var s=Object.assign({},settings,{scheduleMode:(mt.scheduleMode==="early"?"early":"back"),
        mustBeBackBy:mt.mustBeBackBy||settings.mustBeBackBy,shiftStart:shiftStart||"06:30",
        maxHours:friRun?(settings.friRunHours||settings.maxHours):settings.maxHours});
      var M=estMatrix([brewery].concat(good),s);
      var order=optimiseLocal(good,s,M);
      var f=feas(order,good,s,M);
      ordered=order.map(function(i){ return good[i]; })
        .concat(stopsList.filter(function(x){ return !geoOK(x); }));
      m.totalMin=Math.round(f.total); m.leaveBy=toHHMM(f.anchor);
      m.overCap=f.overCap; m.feasible=f.feasibleWindows; m.tightestStop=f.bind;
      m.backLate=(s.scheduleMode==="early")?0:f.backLate; m.backToBase=toHHMM(f.backToBase);
    }
    if(base.length){
      var seen={}; ordered.forEach(function(x,i){ seen[String(x.id)]=i; });
      ordered=keepNew.slice().sort(function(a,b){ return (seen[String(a.id)]||0)-(seen[String(b.id)]||0); });
    }
    setSugg(function(list){
      var n=list.slice();
      if(!n[ri]) return n;
      n[ri]=Object.assign({},n[ri],{stops:ordered,stale:false,m:m});
      return n;
    });
  }

  // A suggestion isn't saved yet, so removing a stop is purely local: the order
  // was never taken out of "Orders to plan", it just stops being proposed here.
  // Moving was possible from the start; removing wasn't, which left no way to
  // say "not this week" without saving the run and then unpicking it.
  function dropSuggested(runIdx,stopIdx){
    var list=(sugg||[]).map(function(r){ return Object.assign({},r,{stops:r.stops.slice()}); });
    if(!list[runIdx]) return;
    var st=list[runIdx].stops.splice(stopIdx,1)[0];
    if(!st) return;
    list[runIdx].stale=true;
    var kept=list.filter(function(r){ return r.stops.length; });
    setSugg(kept.length?kept:null);
    setMoving(null);
    showToast(st.name+" taken off \u2014 still in orders to plan");
    kept.forEach(function(r,i){ if(r.stale) retime(i,r.stops,r); });
  }

  function moveStop(fromIdx,stopIdx,toIdx){
    // Worked out from current state rather than inside the updater, so the
    // re-time calls below act on the list that was actually committed.
    var list=(sugg||[]).map(function(r){ return Object.assign({},r,{stops:r.stops.slice()}); });
    if(!list[fromIdx]||!list[toIdx]) return;
    var st=list[fromIdx].stops.splice(stopIdx,1)[0];
    if(!st) return;
    st=Object.assign({},st);
    if(st.why0===undefined){ st.why0=st.why||""; st.moved0=!!st.moved; st.title0=list[fromIdx].title||""; }
    var toR=list[toIdx], usual=String(st.usualDay||"").trim(), uKey=usual.substring(0,3).toLowerCase();
    var toDay=String(toR.day||toR.title||"").substring(0,3), toLbl=String(toR.title||toDay);
    if(st.title0&&toR.title===st.title0){ st.why=st.why0; st.moved=st.moved0; }
    else if(uKey&&toDay.toLowerCase()===uKey){ st.why="Usual day (moved back by you)"; st.moved=false; }
    else { st.why="Moved by you to "+toLbl+(usual?" \u2014 usually "+usual:""); st.moved=true; }
    list[toIdx].stops.push(st);
    list[fromIdx].stale=true; list[toIdx].stale=true;
    var kept=list.filter(function(r){ return r.stops.length; });
    setSugg(kept);
    setMoving(null);
    showToast("Moved \u2014 re-timing on live roads\u2026");
    kept.forEach(function(r,i){ if(r.stale) retime(i,r.stops,r); });
  }

  // Geocode a typed address so a non-customer stop can be routed. The reason
  // rides along to the run so whoever reads it later knows why a place that
  // isn't a stockist ended up on the list.
  async function findAddr(o){
    var q=String(addrVal||"").trim(), why=String(reasonVal||"").trim();
    if(!q){ showToast("Type an address first"); return; }
    if(!why){ showToast("Add a reason for this stop"); return; }
    if(finding) return;
    setFinding(true);
    try{
      var r=await api.post("geocodeAddress",{address:q});
      if(r&&r.success){
        setResolved(function(m){ var n=Object.assign({},m); n[o.customerId]={lat:r.lat,lng:r.lng,label:r.label,reason:why}; return n; });
        setAddrOpen(""); setAddrVal(""); setReasonVal("");
        showToast(r.partial?("Added \u2014 check the pin: "+r.label):("Added: "+r.label));
      } else showToast((r&&r.error)||"Couldn\u2019t find that address");
    }catch(e){ showToast("Address lookup failed \u2014 check signal"); }
    setFinding(false);
  }
  // History as well as the active board. The board is only the next eight days,
  // so an order held on a run that already went out had nothing to resolve
  // against and its pill fell back to a bare "PLANNED" — no use at all when the
  // question is which run took it. Active runs win where both have the ID.
  const runById=useMemo(function(){
    var m={};
    (history||[]).forEach(function(r){ if(r&&r.runId) m[r.runId]=r; });
    (runs||[]).forEach(function(r){ if(r&&r.runId) m[r.runId]=r; });
    return m;
  },[runs,history]);
  // A run that's already on the road can't take another drop.
  const plannable=useMemo(function(){ return (runs||[]).filter(function(r){ return !r.onRoad; }); },[runs]);
  function runTitle(r){
    var p=String(isoOf(r.date)||"").split("-");
    var dow=p.length>2?DOW[new Date(+p[0],+p[1]-1,+p[2]).getDay()]:"";
    return [(r.driver||"Run"),dow,(r.slot||"")].filter(Boolean).join(" ");
  }

  // "Brendan Wed AM" — who's taking it and when, rather than a bare "on a run".
  // Falls back to the raw status if the order predates the Run ID column.
  // "BG THU AM BG-090926". The initials come off the front of the run ID rather
  // than from the driver's name — the backend builds the ID from a lookup table
  // of its own, so deriving them here a second way risks a pill that disagrees
  // with the ID printed next to it.
  function runLabel(o){
    var id=String(o.runId||"");
    if(!id) return String(o.status||"On a run").toUpperCase();
    var ini=id.split("-")[0]||"RUN";
    var r=runById[id];
    if(!r) return (ini+" "+id).toUpperCase();     // older than the history window
    var p=String(isoOf(r.date)||"").split("-");
    var dow=p.length>2?DOW[new Date(+p[0],+p[1]-1,+p[2]).getDay()]:"";
    return [ini,dow,(r.slot||""),id].filter(Boolean).join(" ").toUpperCase();
  }
  const custById=useMemo(function(){ var m={}; customers.forEach(function(c){ m[c.id]=c; }); return m; },[customers]);

  // Sync is one of the two things that SHOULD throw the current plan away —
  // the orders underneath it have just changed. Navigation is not.
  // A sync used to wipe the suggested runs on the grounds that the orders
  // underneath them had changed. That makes it impossible to draft the week
  // before every order is in \u2014 which is exactly when you want to draft it.
  // The suggestions now hold; the effect below prunes any stop that has since
  // been planned or parked, and new orders arrive in "Orders to plan" ready to
  // be added to a run or folded in with a re-suggest.
  async function doSync(){
    if(syncing||!onSync) return;
    setSyncing(true);
    try{ await onSync(); }catch(e){}
    setSyncing(false);
  }

  function kegsOf(o){ return kegsOfLines(o.lines); }
  // A venue with no customer record (or no coordinates) can't be routed. It
  // stays greyed out until an address is supplied — Aaron drops to plenty of
  // places that aren't keg customers, and they used to be a dead end here.
  function unroutableOf(o){ var c=custById[o.customerId]; return !o.customerId||!c||!geoOK(c); }
  function flaggedOf(o){ return unroutableOf(o) && !resolved[o.customerId]; }
  // Three states, not two: still to plan, on a run, or parked as not needed.
  // Parked orders used to read as "on a run", which put them in the planned
  // list and made them look delivered.
  function skippedOf(o){ return String(o.status||"").trim().toLowerCase()==="no delivery"; }
  function assignedOf(o){ var st=String(o.status||"").toLowerCase(); return st!==""&&st!=="pending"&&!skippedOf(o); }
  function lockedOf(o){ return flaggedOf(o)||assignedOf(o)||skippedOf(o); }
  function toggle(cid){ setSel(function(s){ var n=Object.assign({},s); if(n[cid]) delete n[cid]; else n[cid]=true; return n; }); }

  const selList=orders?orders.filter(function(o){ return sel[o.customerId] && !lockedOf(o); }):[];
  const selKegs=selList.reduce(function(a,o){ return a+kegsOf(o); },0);
  const lineCount=orders?orders.reduce(function(a,o){ return a+o.lines.length; },0):0;
  const plannedCount=orders?orders.filter(assignedOf).length:0;
  // Parked orders are neither planned nor plannable — leaving them in this
  // count kept the Suggest button live and put them back into suggestions.
  const toPlanCount=orders?orders.filter(function(o){ return !assignedOf(o)&&!flaggedOf(o)&&!skippedOf(o); }).length:0;
  const kegTotal=orders?orders.reduce(function(a,o){ return a+kegsOf(o); },0):0;

  // An order becomes a stop the same way wherever it's used.
  function stopFromOrder(o){
    var c=custById[o.customerId]||{}, rz=resolved[o.customerId];
    // A resolved non-customer keeps its order's ID so saveRun can still mark
    // the order planned; it just carries its own coordinates and reason.
    if(rz) return { id:o.customerId, name:o.customerName, suburb:"Custom stop",
      lat:Number(rz.lat), lng:Number(rz.lng), open:"", close:"",
      kegs:kegsOf(o), orderNo:o.orderNo||"", products:o.lines.map(function(l){ return l.product; }), lines:o.lines,
      custom:true, note:rz.reason };
    return { id:o.customerId, name:o.customerName||c.name, suburb:c.suburb||"", lat:Number(c.lat), lng:Number(c.lng),
      open:c.open||"", close:c.close||"", kegs:kegsOf(o), products:o.lines.map(function(l){ return l.product; }), lines:o.lines, orderNo:o.orderNo||"", stopMin:c.stopMin||null };
  }

  function build(){
    if(!selList.length){ showToast("Pick some orders first"); return; }
    const stops=selList.map(stopFromOrder);
    onBuild(stops,(selList[0]&&selList[0].date)||"");
  }

  // Extras read by name with no size: "A-Frame ×1", not "Extra Extra ×0".
  function pill(l,i){
    var keg=isKegLine(l);
    return h("span",{key:i,className:"fr-ppill"},
      h("span",{className:"fr-pdot",style:{background:keg?prodColour(l.product):"var(--g40)"}}),
      keg?prodShort(l.product):extraName(l),
      h("span",{className:"fr-pq"}," "+(keg?(l.size||"")+" ":"")+"\u00D7"+qtyLabel(lineQty(l)))); }

  function renderOrder(o){
    var c=custById[o.customerId], kegs=kegsOf(o), flagged=flaggedOf(o), assigned=assignedOf(o);
    var parked=skippedOf(o);
    var rz=resolved[o.customerId], isCustom=!!rz;
    var locked=flagged||assigned||parked, isSel=!!sel[o.customerId]&&!locked;
    var formOpen=addrOpen===o.customerId;
    return h("div",{key:o.customerId||o.customerName,className:"fr-ord"+(isSel?" sel":"")+(locked?" locked":"")+(isCustom?" resolved":""),
      onClick:locked?null:function(){ if(!formOpen) toggle(o.customerId); }},
      h("div",{className:"fr-ord-top"},
        h("div",{style:{flex:1}},
          h("div",{className:"fr-ord-name"},o.customerName||"(unmatched venue)"),
          h("div",{className:"fr-ord-sub"},(c&&c.suburb)?c.suburb:(isCustom?rz.label:(flagged?"not a keg customer":""))),
          parked?h("span",{className:"fr-flag",style:{marginTop:5,display:"inline-block",color:"var(--g60)",background:"var(--g05)"}},"NO DELIVERY")
            :(flagged?h("span",{className:"fr-flag",style:{marginTop:5,display:"inline-block"}},"NO ADDRESS")
            :(isCustom?h("span",{className:"fr-flag ok",style:{marginTop:5,display:"inline-block"}},"CUSTOM STOP")
            :(assigned?h("span",{className:"fr-flag ok",style:{marginTop:5,display:"inline-block"}}, runLabel(o)):null)))
        ),
        h("div",{style:{display:"flex",alignItems:"center",gap:11}},
          h("div",{style:{textAlign:"right"}},h("div",{className:"fr-ord-k"},kegs),h("div",{className:"fr-ord-kl"},"kegs")),
          locked?null:h("div",{className:"fr-check"+(isSel?" on":"")},isSel?"\u2713":"")
        )
      ),
      h("div",{className:"fr-pills"},o.lines.map(pill)),
      isCustom?h("div",{className:"fr-ord-sub",style:{marginTop:8}},"\u201C"+rz.reason+"\u201D"):null,
      // Orders that turn up after the week was planned go straight onto a run
      // that hasn't left yet, rather than forcing a re-plan.
      parked?h("button",{className:"fr-btn out sm",style:{marginTop:10},
        onClick:function(e){ e.stopPropagation(); onSkipOrder(o.customerId,false); }},
        "Delivery needed after all"):null,
      // One order that needs its own trip — straight to the route screen,
      // where the driver gets picked, rather than through a selection.
      (!locked&&!parked)?h("button",{className:"fr-btn ink sm",style:{marginTop:10},
        onClick:function(e){ e.stopPropagation(); onBuild([stopFromOrder(o)],o.date||""); }},
        "Plan this on its own \u2192"):null,
      (!locked&&!parked)?h("button",{className:"fr-btn out sm",style:{marginTop:10,color:"var(--g60)"},
        onClick:function(e){ e.stopPropagation(); onSkipOrder(o.customerId,true); }},
        "No delivery needed"):null,
      (!locked&&plannable.length)?h("div",null,
        h("button",{className:"fr-btn out sm",style:{marginTop:10},
          onClick:function(e){ e.stopPropagation(); setAddTo(addTo===o.customerId?"":o.customerId); }},
          addTo===o.customerId?"Cancel":"+ Add to a planned run"),
        addTo===o.customerId?h("div",{className:"fr-sug-pick"},
          plannable.map(function(rn){
            return h("button",{key:rn.runId,onClick:function(e){ e.stopPropagation(); setAddTo(""); doAdd(rn,o.customerId); }},
              runTitle(rn));
          })):null
      ):null,
      (flagged&&!formOpen)?h("button",{className:"fr-btn out sm",style:{marginTop:10},
        onClick:function(e){ e.stopPropagation(); setAddrOpen(o.customerId); setAddrVal(""); setReasonVal(""); }},
        "+ Add address to plan this"):null,
      formOpen?h("div",{className:"fr-addr",onClick:function(e){ e.stopPropagation(); }},
        h("div",{className:"fr-addr-l"},"Address"),
        h("input",{className:"fr-addr-in",type:"text",value:addrVal,placeholder:"e.g. Optus Stadium, Burswood",
          onChange:function(e){ setAddrVal(e.target.value); }}),
        h("div",{className:"fr-addr-l",style:{marginTop:9}},"Reason for the stop"),
        h("input",{className:"fr-addr-in",type:"text",value:reasonVal,placeholder:"e.g. event pour, staff pickup",
          onChange:function(e){ setReasonVal(e.target.value); }}),
        h("div",{style:{display:"flex",gap:8,marginTop:10}},
          h("button",{className:"fr-btn ink sm",style:{flex:1,opacity:finding?0.6:1},disabled:finding,
            onClick:function(){ findAddr(o); }},finding?"Finding\u2026":"Find address"),
          h("button",{className:"fr-btn out sm",style:{width:"auto"},
            onClick:function(){ setAddrOpen(""); }},"Cancel"))
      ):null
    );
  }

  // A built run, collapsed to one line. Expanding lists the orders that went
  // on it — those orders are deliberately no longer in the list above, so the
  // planning space only ever shows what still needs planning.
  function renderRunBand(r){
    var open=!!expRun[r.runId];
    var mine=(orders||[]).filter(function(o){ return o.runId && o.runId===r.runId; })
      .sort(function(a,b){ return String(a.customerName||"").localeCompare(String(b.customerName||""),undefined,{sensitivity:"base"}); });
    var p=String(isoOf(r.date)||"").split("-");
    var dow=p.length>2?DOW[new Date(+p[0],+p[1]-1,+p[2]).getDay()]:"";
    return h("div",{className:"fr-runwrap",key:r.runId},
      h("div",{className:"fr-runhead",onClick:function(){ setExpRun(function(d){ var n=Object.assign({},d); if(n[r.runId]) delete n[r.runId]; else n[r.runId]=true; return n; }); }},
        h("div",{style:{flex:1,minWidth:0}},
          h("div",{className:"fr-runhead-n"},[(r.driver||"Run"),dow,(r.slot||"")].filter(Boolean).join(" ")),
          h("div",{className:"fr-runhead-s"},(r.date||"")+" \u00B7 "+(r.status||"Planned")
            +(r.estHours?(" \u00B7 "+hmMin(r.estHours*60)+" est"):"")
            +(r.latestStart?(" \u00B7 leave "+r.latestStart):"")
            +(r.manualOrder?" \u00B7 order set by hand":"")),
          (function(){
            var fl=runFlagList({overCap:r.overCap,overKegs:r.overKegs,totalKegs:r.totalKegs,feasible:r.feasible,bind:r.bind,
              backLate:r.backLate,backToBase:r.backToBase},settings,r.backBy||"");
            if(!fl.length) return null;
            var ok=!!(r.override||String(r.overrideReason||"").trim());
            return h("div",{style:{fontSize:11,fontWeight:700,marginTop:3,color:(r.feasible===false?"var(--red)":"#B45309")}},
              "\u2691 "+fl.join(" \u00B7 ")+(ok?" \u2014 approved":" \u2014 asks for a reason on Save"));
          })()),
        h("div",{style:{textAlign:"right"}},h("div",{className:"fr-ord-k"},r.totalKegs||0),h("div",{className:"fr-ord-kl"},"kegs")),
        h("span",{style:{color:"var(--g40)",fontSize:13,marginLeft:2}},open?"\u25BE":"\u25B8")),
      open?h("div",{className:"fr-runbody"},
        mine.length?mine.map(function(o){
          var picking=movingSaved&&movingSaved.runId===r.runId&&movingSaved.custId===o.customerId;
          var others=plannable.filter(function(x){ return x.runId!==r.runId; });
          return h("div",{key:o.customerId},
            h("div",{className:"fr-runstop"},
              h("span",{className:"fr-runstop-n"},o.customerName||"(unmatched)"),
              h("span",{className:"fr-runstop-k"},kegsOf(o)+" kegs"+(extrasOfLines(o.lines)?(" \u00B7 "+extrasOfLines(o.lines)+" items"):"")),
              others.length?h("button",{className:"fr-sug-mv",
                onClick:function(e){ e.stopPropagation(); setMovingSaved(picking?null:{runId:r.runId,custId:o.customerId}); }},
                moveBusy&&picking?"\u2026":"Move"):null,
              h("button",{className:"fr-sug-mv",style:{color:"var(--red)",borderColor:"var(--red)"},
                onClick:function(e){ e.stopPropagation(); setMovingSaved(null); setDropAsk({runId:r.runId,cust:o.customerId,name:o.customerName||"this stop"}); }},
                "Remove")),
            picking?h("div",{className:"fr-sug-pick"},
              others.map(function(t){ return h("button",{key:t.runId,
                onClick:function(e){ e.stopPropagation(); doMoveSaved(r.runId,t.runId,o.customerId); }},
                "\u2192 "+runTitle(t)); })):null,
            (dropAsk&&dropAsk.runId===r.runId&&dropAsk.cust===o.customerId)?h("div",{className:"fr-sug-pick"},
              h("span",{style:{fontSize:11.5,color:"var(--g60)",width:"100%",paddingBottom:2}},
                "Take "+(o.customerName||"this stop")+" off the run? Its order goes back to unplanned."),
              h("button",{style:{borderColor:"var(--red)",color:"var(--red)"},
                onClick:function(e){ e.stopPropagation(); doRemove(); }},"Yes, remove"),
              h("button",{onClick:function(e){ e.stopPropagation(); setDropAsk(null); }},"Cancel")):null);
        }):h("div",{className:"fr-sub",style:{marginTop:0,padding:"4px 2px"}},
          "Stops aren\u2019t linked to this run yet \u2014 open it to see them."),
        h("div",{style:{display:"flex",gap:8,marginTop:10}},
          h("button",{className:"fr-btn ink sm",style:{flex:1},onClick:function(e){ e.stopPropagation(); onOpenRun(r.runId,r.date,r.slot,r.orderWeek,"route",r.scheduleMode,r.mustBeBackBy); }},"Edit route"),
          h("button",{className:"fr-btn danger sm",style:{width:"auto",flexShrink:0},onClick:function(e){ e.stopPropagation(); if(window.confirm("Delete this run? Its orders go back to available for planning.")) onDeleteRun(r.runId); }},"Delete"))
      ):null);
  }

  // Someone else is planning. There is deliberately no take-over \u2014 the board
  // is read-only until they save or the lock lapses on its own.
  if(lockInfo&&lockInfo.locked&&!lockHeld){
    return h("div",null,
      h("div",{style:{marginTop:12}},
        h("div",{className:"fr-eyebrow"},"Plan \u00B7 the week"),
        h("div",{className:"fr-title"},"Someone\u2019s planning.")),
      h("div",{className:"fr-card",style:{borderColor:"var(--yellow)",borderWidth:2}},
        h("div",{style:{fontSize:15,fontWeight:700,letterSpacing:-.2}},
          (lockInfo.user||"Someone")+" is planning"+(lockInfo.since?(" \u2014 started "+lockInfo.since):"")),
        h("div",{className:"fr-sub",style:{marginTop:8,lineHeight:1.5}},
          "Only one person can plan at a time, so the week can\u2019t be worked on from two places at once. "
          +"It frees up as soon as they save"
          +(lockInfo.expiresIn?(", and by itself in about "+lockInfo.expiresIn+" minute"+(lockInfo.expiresIn===1?"":"s")+" if they\u2019ve walked away"):"")
          +"."),
        h("div",{className:"fr-sub",style:{marginTop:10}},"Today and Load still work as normal.")));
  }

  return h("div",null,
    pendingCount?h("div",{style:{position:"sticky",top:0,zIndex:120,display:"flex",alignItems:"center",
      gap:10,padding:"11px 14px",margin:"8px 0 0",background:"var(--ink)",color:"#fff",borderRadius:12}},
      h("div",{style:{flex:1,minWidth:0}},
        h("div",{style:{fontSize:14,fontWeight:700,letterSpacing:-.2}},
          pendingCount+(pendingCount===1?" unsaved change":" unsaved changes")),
        h("div",{style:{fontSize:11.5,color:"var(--g40)",marginTop:2}},"Nothing is written until you save")),
      h("button",{className:"fr-btn out sm",style:{width:"auto",flexShrink:0,padding:"7px 11px"},
        disabled:committing,onClick:function(){ if(window.confirm("Discard your unsaved changes?")) onDiscard(); }},"Discard"),
      h("button",{className:"fr-btn yellow sm",style:{width:"auto",flexShrink:0,padding:"7px 14px"},
        disabled:committing,onClick:onCommit},committing?"Saving\u2026":"Save")
    ):null,
    h("div",{style:{marginTop:12}},
      h("div",{className:"fr-eyebrow"},"Plan \u00B7 the week"),
      h("div",{className:"fr-title"},"Orders in."),
      h("div",{className:"fr-sub"}, orders===null?"Loading orders\u2026"
        :(orders.length?(lineCount+" lines \u00B7 "+kegTotal+" kegs \u00B7 "+orders.length+" venues"):"No orders yet.")),
      orders&&orders.length?h("div",{className:"fr-plancount"},
        h("span",{className:"fr-plancount-n"}, plannedCount+" / "+orders.length),
        h("span",{className:"fr-plancount-l"}, " planned"),
        toPlanCount?h("span",{className:"fr-plancount-x"}, " \u00B7 "+toPlanCount+" to plan"):null
      ):null,
      orders&&orders.length?h("div",{className:"fr-sub",style:{marginTop:6,color:"var(--g40)"}},
        selList.length?(selList.length+" selected \u00B7 tap Next to plan the route"):"Tap the venues going on this run."):null,
      h("div",{style:{marginTop:8,padding:"9px 12px",background:"var(--g05)",borderRadius:9,
        fontSize:11.5,color:"var(--g60)",lineHeight:1.45}},
        "Run times are estimates only until saved \u2014 estimates are generally longer than the real drive."),
      h("div",{style:{display:"flex",gap:8,marginTop:10,flexWrap:"wrap"}},
        h("button",{className:"fr-btn out sm",style:{width:"auto",opacity:syncing?0.6:1},
          disabled:syncing,onClick:doSync},
          syncing?"Syncing\u2026":"\u21BB Sync orders from sheet"),
        toPlanCount?h("button",{className:"fr-btn ink sm",style:{width:"auto",opacity:suggesting?0.6:1},
          disabled:suggesting,onClick:function(){ doSuggestWeek(false); }},
          suggesting?"Working\u2026":"\u2726 Suggest week"):null,
        toPlanCount?h("button",{className:"fr-btn out sm",style:{width:"auto",opacity:suggesting?0.6:1},
          disabled:suggesting,onClick:doSuggest},"Suggest runs (balanced)"):null),
      toPlanCount?h("div",{className:"fr-sched",style:{marginTop:10}},
        h("div",{className:"fr-when-l",style:{marginBottom:6}},
          "Suggest week \u00B7 days"+(!planOpts.days&&autoDays.nextWeek?" (next week)":"")),
        h("div",{className:"fr-seg"}, PLAN_DAY_NAMES.map(function(d){
          return h("button",{key:d,className:"fr-segb"+(shownDays.indexOf(d)>=0?" on":""),
            onClick:function(){ toggleDay(d); }},d); })),
        h("div",{className:"fr-when-l",style:{margin:"10px 0 6px"}},"Runs per day"),
        h("div",{className:"fr-seg"}, [[0,"Auto"],[1,"1"],[2,"Up to 2"]].map(function(x){
          return h("button",{key:x[0],className:"fr-segb"+((planOpts.runsPerDay||0)===x[0]?" on":""),
            onClick:function(){ setPlanOpts(function(o){ return Object.assign({},o,{runsPerDay:x[0]}); }); }},x[1]); })),
        h("div",{className:"fr-when-l",style:{margin:"10px 0 6px"}},"Plan by"),
        h("div",{className:"fr-seg"}, [["usual","Usual days"],["even","Even split"]].map(function(x){
          return h("button",{key:x[0],className:"fr-segb"+((planOpts.mode||"usual")===x[0]?" on":""),
            onClick:function(){ setPlanOpts(function(o){ return Object.assign({},o,{mode:x[0]}); }); }},x[1]); })),
        h("div",{className:"fr-sched-note"},
          ((planOpts.mode||"usual")==="even"
            ? "Splits every order evenly across the ticked days, ignoring usual days."
            : "Each stockist on its usual day; an unticked day's stops move to the cheapest ticked day.")
          +(planOpts.days?"":" Today drops off after "+((settings&&settings.planTodayUntil)||"12:00")+".")),
        planOpts.days?h("button",{className:"fr-btn out sm",style:{width:"auto",marginTop:8,padding:"5px 10px",fontSize:11},
          onClick:function(){ setPlanOpts(function(o){ return Object.assign({},o,{days:null}); }); }},"Reset days to automatic"):null
      ):null
    ),
    h("div",{className:"fr-acc"},
      h("div",{className:"fr-acc-h",onClick:function(){ setOpenOrders(function(o){ return !o; }); }},
        h("span",null,"Orders to plan"),
        h("span",{className:"fr-acc-c"},String(toPlanCount)+"  "+(openOrders?"\u25BE":"\u25B8"))),
      openOrders?(function(){
        var byName=function(a,b){ return String(a.customerName||"").localeCompare(String(b.customerName||""),undefined,{sensitivity:"base"}); };
        var pending=(orders||[]).filter(function(o){ return !assignedOf(o)&&!skippedOf(o); }).sort(byName);
        var parked=(orders||[]).filter(skippedOf).sort(byName);
        if(!pending.length&&parked.length) return [
          h("div",{className:"fr-plan-divider",key:"__np"},"No delivery needed \u00B7 "+parked.length)
        ].concat(parked.map(renderOrder));
        if(!pending.length) return h("div",{className:"fr-empty"}, orders===null?"Loading\u2026"
          :((orders&&orders.length)?"All orders are on a run \u2014 see below.":"Nothing to plan yet. Tap \u201CSync orders from sheet\u201D above to pull this week."));
        var out=pending.map(renderOrder);
        if(parked.length){
          out.push(h("div",{className:"fr-plan-divider",key:"__np"},"No delivery needed \u00B7 "+parked.length));
          parked.forEach(function(o){ out.push(renderOrder(o)); });
        }
        return out;
      })():null
    ),
    sugg?h("div",{className:"fr-acc"},
      h("div",{className:"fr-acc-h"},
        h("span",null,"Suggested runs"),
        h("span",{className:"fr-acc-c"},String(sugg.length))),
      h("div",{className:"fr-sub",style:{margin:"8px 2px 0",fontSize:11.5}},
        (sugg[0]&&sugg[0].week)
          ? "From the Regions tab: each stockist starts on its usual day, then earlier runs are filled from later in the week where it saves time or eases a busier day. Moved stops say why \u2014 move or remove any stop to see the impact."
          : "Balanced on kegs and time. Distant stops are split out as their own trip \u2014 move any stop into another run to see the impact."),
      (weekNotes&&weekNotes.length&&sugg[0]&&sugg[0].week)?h("div",{style:{marginTop:8,padding:"10px 12px",
        background:"#FFF8E1",border:"1px solid #FFE082",borderRadius:10,fontSize:12,color:"#8a5a00",lineHeight:1.45}},
        weekNotes.map(function(t,i){ return h("div",{key:i,style:{marginTop:i?5:0}},"\u2022 "+t); })):null,
      // Straight-line estimates run far longer than real roads, so a run can
      // look over cap when it isn't. Never let that pass as a road time.
      (sugSrc&&sugSrc.source!=="ors")?h("div",{className:"fr-status bad",style:{marginTop:8}},
        "Times below are straight-line estimates, not road times \u2014 they read long. "
        +(sugSrc.note?("Reason: "+sugSrc.note):"Live routing unavailable.")):null,
      sugg.map(function(r,ri){
        var m=r.m||{}, open=!!sugOpen[ri];
        var tgt=savedTarget(r), base=tgt?savedStopsOf(r):[];
        var kegs=r.stops.concat(base).reduce(function(a,b){ return a+(+b.kegs||0); },0);
        var sFl=r.stale?[]:runFlagList(Object.assign({},m,{kegs:kegs}),settings,r.scheduleMode==="early"?"":(r.mustBeBackBy||settings.mustBeBackBy));
        var over=sFl.length>0;
        return h("div",{className:"fr-sug"+(r.outlier?" out":"")+(over?" bad":""),key:"sug"+ri},
          h("div",{className:"fr-sug-h",onClick:function(){ setSugOpen(function(d){ var n=Object.assign({},d); if(n[ri]) delete n[ri]; else n[ri]=true; return n; }); }},
            h("div",{style:{flex:1,minWidth:0}},
              h("div",{className:"fr-sug-t"},tgt?((r.day?r.day+" \u00B7 ":"")+"add to "+runTitle(tgt)):((r.week&&r.title?r.title:("Run "+(ri+1)))+" \u00B7 "+r.region)),
              h("div",{className:"fr-sug-s"},
                (tgt?("+"+r.stops.length+" onto "+base.length+" saved \u00B7 "+kegs+" kegs in total \u00B7 ")
                    :(r.stops.length+" stops \u00B7 "+kegs+" kegs \u00B7 "))
                +(r.stale?"re-timing\u2026":((m.totalMin!=null)?hmMin(m.totalMin):"\u2014"))
                +((!r.stale&&m.leaveBy)?(" \u00B7 leave "+m.leaveBy):""))),
            tgt?h("span",{className:"fr-sug-tag",style:{background:"var(--ink)",color:"var(--paper)"}},"Saved run"):null,
            r.outlier?h("span",{className:"fr-sug-tag"},"Separate trip"):null,
            r.lateOpen?h("span",{className:"fr-sug-tag"},r.opensAt?("Opens "+r.opensAt):"Late opening"):null,
            h("span",{style:{color:"var(--g40)",fontSize:13}},open?"\u25BE":"\u25B8")),
          (over&&!r.stale)?h("div",{className:"fr-status "+(m.feasible===false?"bad":"warn"),style:{margin:"0 12px 10px"}},
            sFl.map(function(t,i){ return h("div",{key:i,style:{marginTop:i?3:0}},"\u2022 "+t+(r.outlier&&/cap/.test(t)?" (expected on a long trip)":"")); })):null,
          (!r.stale&&r.lateOpen)?h("div",{className:"fr-status warn",style:{margin:"0 12px 10px"}},
            "Ends with a venue that opens at "+(r.opensAt||"lunchtime")+" \u2014 the run is timed to arrive on the opening and leaves "
            +(m.leaveBy||"later")+", rather than going out twice."):null,
          open?h("div",{className:"fr-runbody"},
            r.stops.map(function(st,si){
              return h("div",{key:(st.id||si)+"-"+si},
                h("div",{className:"fr-runstop"},
                  h("span",{className:"fr-runstop-n"},st.name,
                    (r.week&&st.why)?h("div",{style:{fontSize:10.5,fontWeight:st.moved?700:500,marginTop:2,
                      color:st.moved?"#B45309":"var(--g40)"}},st.why):null),
                  h("span",{className:"fr-runstop-k"},(+st.kegs||0)+" kegs"),
                  (sugg.length>1||plannable.length)?h("button",{className:"fr-sug-mv",
                    onClick:function(e){ e.stopPropagation(); setMoving((moving&&moving.r===ri&&moving.s===si)?null:{r:ri,s:si}); }},"Move"):null,
                  h("button",{className:"fr-sug-mv",style:{color:"var(--red)",borderColor:"var(--red)"},
                    onClick:function(e){ e.stopPropagation(); dropSuggested(ri,si); }},"Remove")),
                (moving&&moving.r===ri&&moving.s===si)?h("div",{className:"fr-sug-pick"},
                  sugg.map(function(_,ti){ var t2=savedTarget(sugg[ti]); return ti===ri?null:h("button",{key:"t"+ti,
                    onClick:function(e){ e.stopPropagation(); moveStop(ri,si,ti); }},
                    "\u2192 "+(t2?("Suggested: add to "+runTitle(t2))
                      :((sugg[ti]&&sugg[ti].week&&sugg[ti].title)?sugg[ti].title:("Run "+(ti+1))))); }),
                  // Straight onto a run that's already on the board, the same as
                  // "Add to saved run" in Orders to plan.
                  plannable.length?h("div",{key:"__sv",style:{width:"100%",fontSize:10,fontWeight:700,letterSpacing:".5px",
                    textTransform:"uppercase",color:"var(--g40)",marginTop:4}},"Saved runs"):null,
                  plannable.map(function(sr){ return h("button",{key:"s"+sr.runId,
                    style:{background:"var(--ink)",color:"var(--paper)"},
                    disabled:moveBusy,
                    onClick:function(e){ e.stopPropagation(); setMoving(null); doAdd(sr,st.id); }},
                    "\u2192 "+runTitle(sr)+((boardStops&&boardStops[sr.runId])?(" \u00B7 "+boardStops[sr.runId].length+" stops"):"")); })
                ):null);
            }),
            (function(){
              var a=asgOf(ri), o=ovr[ri]||{}, blocked=!!over, ready=!blocked||(o.on&&String(o.reason||"").trim());
              if(tgt) return h("div",null,
                h("div",{className:"fr-sub",style:{marginTop:12,fontSize:12}},
                  "Joins "+runTitle(tgt)+" ("+tgt.runId+") \u2014 driver, day and slot come from the saved run. "
                  +base.length+(base.length===1?" stop":" stops")+" already on it; the times above are for the run with these added."),
                blocked?h("div",{style:{marginTop:10}},
                  h("button",{className:"fr-btn out sm",
                    onClick:function(e){ e.stopPropagation(); setOvr(function(d){ var n=Object.assign({},d); n[ri]=Object.assign({},n[ri]||{},{on:!(n[ri]&&n[ri].on)}); return n; }); }},
                    o.on?"Cancel override":"Approve these flags"),
                  o.on?h("input",{type:"text",value:o.reason||"",placeholder:"Reason to add them anyway",
                    onClick:function(e){ e.stopPropagation(); },
                    onChange:function(e){ var v=e.target.value; setOvr(function(d){ var n=Object.assign({},d); n[ri]=Object.assign({},n[ri]||{},{reason:v}); return n; }); },
                    style:{width:"100%",marginTop:8,padding:"9px 11px",border:"1px solid var(--red)",borderRadius:9,fontSize:13,fontFamily:"var(--sans)",boxSizing:"border-box"}}):null
                ):null,
                h("div",{style:{display:"flex",gap:8,marginTop:10}},
                  h("button",{className:"fr-btn "+(ready?"yellow":"disabled"),style:{flex:1},
                    disabled:!ready||savingRun>=0,
                    onClick:function(e){ e.stopPropagation(); if(!ready) return; saveOne(ri,r,a,o,blocked); }},
                    ready?("Add "+r.stops.length+" to "+runTitle(tgt)):"Override needed")));
              return h("div",null,
                // ---- who and when ----
                h("div",{style:{display:"flex",gap:6,alignItems:"center",marginTop:12,flexWrap:"wrap"}},
                  h("span",{className:"fr-dk",style:{width:"100%"}},"Driver"),
                  USERS.map(function(u){
                    return h("button",{key:u.username,className:"fr-sug-mv",
                      style:{padding:"6px 11px",fontSize:12,
                        background:a.driver===u.name?"var(--ink)":"var(--paper)",
                        color:a.driver===u.name?"var(--paper)":"var(--g60)",
                        borderColor:a.driver===u.name?"var(--ink)":"var(--g10)"},
                      onClick:function(e){ e.stopPropagation(); setAsg(ri,"driver",a.driver===u.name?"":u.name); }},u.name);
                  })),
                h("div",{style:{display:"flex",gap:8,alignItems:"center",marginTop:8}},
                  h("input",{type:"date",value:a.date,onClick:function(e){ e.stopPropagation(); },
                    onChange:function(e){ setAsg(ri,"date",e.target.value); },
                    style:{flex:1,minWidth:0,padding:"8px 10px",border:"1px solid var(--g10)",borderRadius:9,fontSize:13,fontFamily:"var(--mono)"}}),
                  h("div",{className:"fr-when-slots"},
                    ["AM","PM"].map(function(sl){
                      return h("button",{key:sl,className:"fr-slot"+(a.slot===sl?" on":""),
                        onClick:function(e){ e.stopPropagation(); setAsg(ri,"slot",sl); }},sl);
                    }))),

                // ---- override, only when something's actually blocking ----
                blocked?h("div",{style:{marginTop:10}},
                  h("button",{className:"fr-btn out sm",
                    onClick:function(e){ e.stopPropagation(); setOvr(function(d){ var n=Object.assign({},d); n[ri]=Object.assign({},n[ri]||{},{on:!(n[ri]&&n[ri].on)}); return n; }); }},
                    o.on?"Cancel override":"Approve these flags"),
                  o.on?h("input",{type:"text",value:o.reason||"",placeholder:"Reason to save it anyway (asked once for this run)",
                    onClick:function(e){ e.stopPropagation(); },
                    onChange:function(e){ var v=e.target.value; setOvr(function(d){ var n=Object.assign({},d); n[ri]=Object.assign({},n[ri]||{},{reason:v}); return n; }); },
                    style:{width:"100%",marginTop:8,padding:"9px 11px",border:"1px solid var(--red)",borderRadius:9,fontSize:13,fontFamily:"var(--sans)",boxSizing:"border-box"}}):null
                ):null,

                h("div",{style:{display:"flex",gap:8,marginTop:10}},
                  h("button",{className:"fr-btn "+(ready?"yellow":"disabled"),style:{flex:1},
                    disabled:!ready||savingRun>=0,
                    onClick:function(e){ e.stopPropagation(); if(!ready) return; saveOne(ri,r,a,o,blocked); }},
                    savingRun===ri?"Saving\u2026":(ready?("Save for "+(a.driver||driverDefault||"me")):"Override needed")),
                  h("button",{className:"fr-btn out",style:{width:"auto",flexShrink:0},
                    disabled:!ready||savingRun>=0,
                    onClick:function(e){ e.stopPropagation();
                      if(!ready) return;
                      onBuild(r.stops.map(function(x){ return Object.assign({},x); }),
                        (orders&&orders[0]&&orders[0].date)||"",
                        { driver:a.driver||driverDefault, runDate:a.date, slot:a.slot, scheduleMode:r.scheduleMode, mustBeBackBy:r.mustBeBackBy||"",
                          override: blocked?{ hours:true, kegs:true, reason:String(o.reason||"").trim() }:null });
                    }},"Route \u2192")));
            })()
          ):null);
      }),
      h("div",{style:{display:"flex",gap:8,alignItems:"center",marginTop:10}},
        (sugg[0]&&sugg[0].week)?null:h("span",{style:{fontSize:11,color:"var(--g60)",fontWeight:600}},"Separate-trip cut-off"),
        (sugg[0]&&sugg[0].week)?null:h("input",{type:"number",value:outMin,onChange:function(e){ setOutMin(e.target.value); },
          style:{width:64,padding:"7px 9px",border:"1px solid var(--g10)",borderRadius:8,fontSize:13,fontFamily:"var(--mono)",textAlign:"center"}}),
        (sugg[0]&&sugg[0].week)?null:h("span",{style:{fontSize:11,color:"var(--g60)"}},"min from base"),
        h("button",{className:"fr-btn out sm",style:{width:"auto",marginLeft:"auto"},
          onClick:function(){ if(sugg[0]&&sugg[0].week) doSuggestWeek(false); else doSuggest(); }},"Re-suggest"),
        h("button",{className:"fr-btn out sm",style:{width:"auto"},onClick:function(){ setSugg(null); setMoving(null); setWeekNotes(null); }},"Clear"))
    ):null,
    h("div",{className:"fr-acc"},
      h("div",{className:"fr-acc-h dark",onClick:function(){ setOpenRuns(function(o){ return !o; }); }},
        h("span",null,"Runs this week"),
        h("span",{className:"fr-acc-c"},String(runs.length)+"  "+(openRuns?"\u25BE":"\u25B8"))),
      openRuns?(runs.length?runs.map(renderRunBand):h("div",{className:"fr-empty"},"No runs built yet \u2014 select orders above and build one.")):null,
      addAsk?h("div",{className:"fr-card",style:{borderColor:"var(--red)",borderWidth:2}},
        h("div",{className:"fr-eyebrow",style:{color:"var(--red)"}},"Over the cap"),
        h("div",{className:"fr-sub",style:{marginTop:6}},addAsk.why),
        h("input",{type:"text",value:addAsk.reason,placeholder:"Reason for adding it anyway (required)",
          onChange:function(e){ var v=e.target.value; setAddAsk(function(a){ return Object.assign({},a,{reason:v}); }); },
          style:{width:"100%",marginTop:10,padding:"9px 11px",border:"1px solid var(--red)",borderRadius:9,fontSize:13,fontFamily:"var(--sans)",boxSizing:"border-box"}}),
        h("div",{style:{display:"flex",gap:8,marginTop:10}},
          h("button",{className:"fr-btn "+(String(addAsk.reason||"").trim()?"ink":"disabled"),style:{flex:1},
            disabled:!String(addAsk.reason||"").trim(),onClick:confirmAddOverride},"Add it anyway"),
          h("button",{className:"fr-btn out",style:{width:"auto"},
            onClick:function(){ setAddAsk(null); showToast("Not added"); }},"Cancel"))
      ):null,
      moveAsk?h("div",{className:"fr-card",style:{borderColor:"var(--red)",borderWidth:2}},
        h("div",{className:"fr-eyebrow",style:{color:"var(--red)"}},"Over the cap"),
        h("div",{className:"fr-sub",style:{marginTop:6}},moveAsk.why),
        h("input",{type:"text",value:moveAsk.reason,placeholder:"Reason for moving it anyway (required)",
          onChange:function(e){ var v=e.target.value; setMoveAsk(function(a){ return Object.assign({},a,{reason:v}); }); },
          style:{width:"100%",marginTop:10,padding:"9px 11px",border:"1px solid var(--red)",borderRadius:9,fontSize:13,fontFamily:"var(--sans)",boxSizing:"border-box"}}),
        h("div",{style:{display:"flex",gap:8,marginTop:10}},
          h("button",{className:"fr-btn "+(String(moveAsk.reason||"").trim()?"ink":"disabled"),style:{flex:1},
            disabled:!String(moveAsk.reason||"").trim(),onClick:confirmMoveOverride},"Move it anyway"),
          h("button",{className:"fr-btn out",style:{width:"auto"},
            onClick:function(){ setMoveAsk(null); setMovingSaved(null); showToast("Move cancelled"); }},"Cancel"))
      ):null
    ),
    (function(){
      var byName=function(a,b){ return String(a.customerName||"").localeCompare(String(b.customerName||""),undefined,{sensitivity:"base"}); };
      var done=(orders||[]).filter(assignedOf).sort(byName);
      var kegs=done.reduce(function(a,o){ return a+kegsOf(o); },0);
      var lines=done.reduce(function(a,o){ return a+o.lines.length; },0);
      return h("div",{className:"fr-acc"},
        h("div",{className:"fr-acc-h plain",onClick:function(){ setOpenDone(function(o){ return !o; }); }},
          h("span",null,"Orders planned"),
          h("span",{className:"fr-acc-c"},String(done.length)+"  "+(openDone?"\u25BE":"\u25B8"))),
        openDone?(done.length?[
          h("div",{className:"fr-sub",key:"__tot",style:{margin:"8px 2px 0",fontSize:11.5}},
            lines+" lines \u00B7 "+kegs+" kegs \u00B7 "+done.length+" venues \u2014 cross-check against the wholesale sheet")
        ].concat(done.map(renderOrder))
          :h("div",{className:"fr-empty"},"Nothing planned yet.")):null
      );
    })(),
    h("div",{style:{height:64}}),
    selList.length?h("div",{className:"fr-buildbar"},
      h("button",{className:"fr-btn yellow",onClick:build},"Next \u00B7 "+selList.length+(selList.length===1?" stop \u00B7 ":" stops \u00B7 ")+selKegs+" kegs \u2192")):null
  );
}

const NAV_ICONS={
  today:[["rect",{x:3,y:4,width:18,height:18,rx:2}],["line",{x1:3,y1:9,x2:21,y2:9}],["line",{x1:8,y1:2,x2:8,y2:6}],["line",{x1:16,y1:2,x2:16,y2:6}]],
  plan:[["rect",{x:3,y:3,width:18,height:18,rx:2}],["line",{x1:9,y1:3,x2:9,y2:21}],["line",{x1:15,y1:3,x2:15,y2:21}]],
  load:[["path",{d:"M21 8l-9-5-9 5v8l9 5 9-5z"}],["path",{d:"M3 8l9 5 9-5"}],["line",{x1:12,y1:13,x2:12,y2:21}]],
  route:[["path",{d:"M12 21s-7-6.3-7-11a7 7 0 0 1 14 0c0 4.7-7 11-7 11z"}],["circle",{cx:12,cy:10,r:2.5}]],
  settings:[["circle",{cx:12,cy:12,r:3}],["path",{d:"M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 0 1-4 0v-.1a1.6 1.6 0 0 0-2.7-1.1l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0-1.1-2.7H3a2 2 0 0 1 0-4h.1A1.6 1.6 0 0 0 4.2 9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.6 1.6 0 0 0 2.7 1.1l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z"}]],
  account:[["path",{d:"M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"}],["circle",{cx:12,cy:7,r:4}]]
};
function refreshIcon(){
  return h("svg",{viewBox:"0 0 24 24"},
    h("polyline",{points:"23 4 23 10 17 10"}),
    h("polyline",{points:"1 20 1 14 7 14"}),
    h("path",{d:"M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"}));
}
function navIcon(name){
  return h.apply(null,["svg",{viewBox:"0 0 24 24"}].concat((NAV_ICONS[name]||[]).map(function(e){return h(e[0],e[1]);})));
}
function Nav({user,screen,setScreen}){
  const items=user.admin
    ?[["today","Today","today"],["plan","Plan","plan"],["route","Route","route"],["load","Load","load"],["settings","Settings","settings"]]
    :[["today","Today","today"],["route","Route","route"],["load","Load","load"],["settings","Account","account"]];
  return h("div",{className:"fr-nav"},items.map(function(it){
    return h("button",{key:it[0],className:"fr-ni"+(screen===it[0]?" on":""),onClick:function(){setScreen(it[0]);}},
      navIcon(it[2]),it[1]);
  }));
}


// ============================================================
// Load the van  — 3 steps: Kegs -> Extras -> Vehicle check
// Keg matrix is built from the run's order lines (product x size).
// Extras (tap cards / corflute) come from the saved run's per-stop
// needs. Vehicle check is a fixed pre-drive safety list.
// ============================================================
function buildLoadList(stops){
  var bySize = {}, order = [], totalKegs = 0, hasLines = false;
  (stops||[]).forEach(function(s){
    totalKegs += (+s.kegs || 0);
    var lines = s.lines && s.lines.length ? s.lines : null;
    if(lines){
      hasLines = true;
      lines.forEach(function(l){
        // Anything that isn't a keg used to fall through to the 18L column,
        // because that was the else branch \u2014 so a box of squealers read as an
        // 18L keg to grab from the fridge. They belong on the extras step.
        if(!isKegLine(l)) return;
        var p = l.product || "Unknown", q = +l.qty || 0;
        if(!bySize[p]){ bySize[p] = {product:p, c18:0, c9:0, c7:0}; order.push(p); }
        var sz = String(l.size||"").replace(/\s/g,"").toLowerCase();
        if(sz.indexOf("7")===0) bySize[p].c7 += q;
        else if(sz.indexOf("9")===0) bySize[p].c9 += q;
        else bySize[p].c18 += q;
      });
    }
  });
  var rows = order.map(function(p){
    return { product:p, colour:prodColour(p), short:prodShort(p), c18:bySize[p].c18, c9:bySize[p].c9, c7:bySize[p].c7 };
  });
  rows.sort(function(a,b){ return (b.c18+b.c9+b.c7)-(a.c18+a.c9+a.c7); });
  var t18 = rows.reduce(function(a,r){return a+r.c18;},0);
  var t9  = rows.reduce(function(a,r){return a+r.c9;},0);
  var t7  = rows.reduce(function(a,r){return a+r.c7;},0);
  // The 7L column only exists when someone on the run is on small kegs.
  return { rows:rows, total18:t18, total9:t9, total7:t7, has7:t7>0, totalKegs:totalKegs, hasLines:hasLines };
}

// Two Venn sites put their own branded labels on squealers and glasses, so the
// driver has to take VENN stock rather than standard. Add a line here if
// another stockist starts running its own labels \u2014 matched loosely on the
// venue name so a change of trading name ("Venn Coffee (NEW OWNERS)") doesn't
// quietly drop the note.
var OWN_LABEL_VENUES=[{match:"venn", label:"VENN labels"}];
function ownLabelFor(stopName,product){
  if(!/squealer|glass/i.test(String(product||""))) return "";
  var n=String(stopName||"").toLowerCase();
  for(var i=0;i<OWN_LABEL_VENUES.length;i++)
    if(n.indexOf(OWN_LABEL_VENUES[i].match)!==-1) return OWN_LABEL_VENUES[i].label;
  return "";
}
// How many to a box, read from the item name ("Squealers - Box of 12").
function boxSizeOf(p){ var m=String(p||"").match(/box of\s*(\d+)/i); return m?+m[1]:0; }
// One order line becomes one or more load-list entries, named the way the
// driver needs to read them: "Squealers: HALF Box (6)". The wholesale sheet
// writes half a box as qty 0.50 against "Squealers - Box of 12", so the box
// size and the fraction together give the actual bottle count. Venue-specific
// labelling rides on the name, because VENN squealers come off a different
// shelf to standard ones \u2014 they can't be pooled into one line.
function extraEntries(product,qty,stopName){
  var per=boxSizeOf(product), q=+qty||0, out=[];
  if(!q) return out;
  var base=String(product||"").replace(/\s*-\s*box of\s*\d+\s*$/i,"").trim();
  var own=ownLabelFor(stopName,product);
  var suffix=own?(" "+own):"";
  if(!per){ out.push({label:base+suffix, qty:q}); return out; }
  var whole=Math.floor(q+0.001), frac=q-whole;
  if(whole>0) out.push({label:base+": FULL Box ("+per+")"+suffix, qty:whole});
  if(frac>0.01){
    var n=Math.round(per*frac);
    var word=(Math.abs(frac-0.5)<0.01)?"HALF":"PART";
    out.push({label:base+": "+word+" Box ("+n+")"+suffix, qty:1});
  }
  return out;
}
// 0.5 reads as \u00bd, 1.5 as 1\u00bd \u2014 "1.5 boxes" invites a miscount.
function qtyLabel(n){
  var q=+n||0;
  if(q===Math.floor(q)) return String(q);
  var whole=Math.floor(q), frac=q-whole;
  var f=(Math.abs(frac-0.5)<0.01)?"\u00bd":(Math.abs(frac-0.25)<0.01)?"\u00bc":(Math.abs(frac-0.75)<0.01)?"\u00be":null;
  if(!f) return String(Math.round(q*100)/100);
  return (whole?String(whole):"")+f;
}

// Normalise whatever the backend gave us for one stop's extras into
// [{product, qty}]. The backend now sends JSON [{product,qty}] so a venue
// needing two of something says so; older saved runs sent a plain array of
// names, or a comma-separated string for corflute. All three are handled,
// with repeats counting as quantity.
function extraItems(v){
  if(v==null||v==="") return [];
  var raw=v;
  if(typeof raw==="string"){
    var s=raw.trim(); if(!s) return [];
    if(s.charAt(0)==="["){ try{ raw=JSON.parse(s); }catch(e){ raw=s.split(","); } }
    else raw=s.split(",");
  }
  if(!Array.isArray(raw)) return [];
  var out=[], at={};
  raw.forEach(function(x){
    var p,q,why;
    if(x&&typeof x==="object"){ p=String(x.product||"").trim(); q=Math.max(1,+x.qty||1); why=x.reason||""; }
    else { p=String(x||"").trim(); q=1; why=""; }
    if(!p) return;
    if(at[p]==null){ at[p]=out.length; out.push({product:p,qty:0,reason:why}); }
    out[at[p]].qty+=q;
    if(!out[at[p]].reason && why) out[at[p]].reason=why;
  });
  return out;
}

// Aggregate per-stop extras into product-grouped rows with stockist detail.
// qtyTotal is the real number of items to grab, not the number of stops.
function buildExtras(stops){
  function group(getList){
    var map = {}, order = [];
    (stops||[]).forEach(function(s){
      extraItems(getList(s)).forEach(function(it){
        var p = it.product;
        // Runs saved before the fix gave an extras-only stop a tap card called
        // "Extra". Signage is only ever for a coffee, so drop anything else.
        if(isNonKegName(p)) return;
        if(!map[p]){ map[p] = { product:p, colour:prodColour(p), short:prodShort(p), stockists:[], qtyTotal:0 }; order.push(p); }
        map[p].stockists.push({ name:(s.name||"Stop"), qty:it.qty, reason:it.reason||"" });
        map[p].qtyTotal += it.qty;
      });
    });
    return order.map(function(p){ return map[p]; });
  }
  // Everything on the order that isn't a keg: squealers, glasses, coffee bags,
  // a gas bottle, a spare A-frame, a regulator Aaron is sending out as a
  // one-off. The name is whatever the Item column said, so a new kind of thing
  // needs no code change to reach the driver.
  function fromOrders(){
    var map = {}, order = [];
    (stops||[]).forEach(function(s){
      (s.lines||[]).forEach(function(l){
        if(isKegLine(l)) return;
        var p = extraName(l); if(!p) return;
        extraEntries(p,lineQty(l),s.name).forEach(function(e){
          if(!map[e.label]){ map[e.label] = { product:e.label, colour:"", short:e.label, stockists:[], qtyTotal:0 }; order.push(e.label); }
          map[e.label].stockists.push({ name:(s.name||"Stop"), qty:e.qty, reason:"" });
          map[e.label].qtyTotal += e.qty;
        });
      });
    });
    return order.map(function(p){ return map[p]; });
  }

  return {
    cards: group(function(s){ return s.tapCards; }),
    corflute: group(function(s){ return s.corflute; }),
    other: fromOrders()
  };
}

function Load(p){
  const {stops,loadingRun,loadPick,currentRunId,savedRuns,runDate,runSlot,live,showToast,onPickRun,onDepart,onGoToday}=p;
  const list   = useMemo(function(){ return buildLoadList(stops); },[stops]);
  const extras = useMemo(function(){ return buildExtras(stops); },[stops]);

  const [step,setStep]   = useState("kegs");
  const [done,setDone]   = useState({});   // keg cell keys: "<i>-18" / "<i>-9"
  const [exDone,setExDone] = useState({}); // extras row keys
  const [exExp,setExExp]   = useState({}); // expanded extras rows
  const [vc,setVc]       = useState({});   // vehicle check rows
  const [departing,setDeparting] = useState(false);
  // Line cleans due on this run (Oct 2026). Fetched separately so a slow read
  // of the Ops master never holds up the keg list. null = loading.
  const [lc,setLc] = useState(null);
  const [lcOpen,setLcOpen] = useState(false);
  useEffect(function(){
    setLc(null);
    if(!loadPick||!currentRunId||offline()){ setLc({unavailable:true}); return; }
    var alive=true;
    api.get("runLineCleans",{runId:currentRunId}).then(function(r){
      if(!alive) return;
      setLc((r&&!r.error&&Array.isArray(r.stops))?r:{unavailable:true,error:(r&&r.error)||""});
    }).catch(function(){ if(alive) setLc({unavailable:true}); });
    return function(){ alive=false; };
  },[currentRunId,loadPick]);
  const lcLines = (lc&&!lc.unavailable) ? (lc.totalLines||0) : null;
  const lcLitres = (lc&&!lc.unavailable) ? (lc.litres!=null?lc.litres:lcLines) : null;

  // ----- no saved run open: show a picker (a draft is NOT loadable) -----
  if(!loadPick){
    // Departed runs are excluded — the van has gone, there's nothing to load.
    var runs = (savedRuns || []).filter(function(r){ return !r.onRoad; });
    return h("div",null,
      h("div",{style:{marginTop:12}},
        h("div",{className:"fr-eyebrow"},"Load the van"),
        h("div",{className:"fr-title"}, runs.length?"Pick a run.":"Nothing to load."),
        h("div",{className:"fr-sub"}, runs.length?"Choose a saved run to load.":"Save a run first, then load it here.")
      ),
      runs.length
        ? runs.map(function(r){ return h("div",{className:"fr-run ready",key:r.runId,style:{cursor:"pointer"},onClick:function(){ onPickRun(r); }},
            h("div",{className:"fr-run-top"},
              h("div",null,
                h("div",{className:"fr-run-meta"},(r.status||"Planned")+(r.date?(" \u00B7 "+r.date):"")+(r.slot?(" \u00B7 "+r.slot):"")),
                h("div",{className:"fr-run-name"}, r.driver||"Run")),
              h("div",{className:"fr-run-tag go"},"Load \u2192")),
            h("div",{className:"fr-run-stats"},
              h("div",{className:"fr-dstat"},h("div",{className:"fr-dstat-v"}, r.totalKegs||0),h("div",{className:"fr-dstat-l"},"Kegs")),
              h("div",{className:"fr-dstat"},h("div",{className:"fr-dstat-v"}, r.scheduleMode==="early"?"ASAP":(r.mustBeBackBy||"\u2014")),h("div",{className:"fr-dstat-l"}, r.scheduleMode==="early"?"Start":"Back by")),
              h("div",{className:"fr-dstat"},h("div",{className:"fr-dstat-v"}, r.latestStart||"\u2014"),h("div",{className:"fr-dstat-l"},"Leave"))
            )
          ); })
        : h("div",{className:"fr-card",style:{textAlign:"center",padding:"26px 16px"}},
            h("div",{className:"fr-sub",style:{marginTop:0}},"Build and save a run on the Plan tab, then come back."),
            h("button",{className:"fr-btn ink sm",style:{marginTop:14,maxWidth:200,marginLeft:"auto",marginRight:"auto"},onClick:onGoToday},"Go to Today"))
    );
  }

  // ----- empty state: run still loading, or genuinely no run -----
  if(!stops || !stops.length){
    if(loadingRun){
      return h("div",null,
        h("div",{style:{marginTop:12}},
          h("div",{className:"fr-eyebrow"},"Load the van"),
          h("div",{className:"fr-title"},"Loading run\u2026"),
          h("div",{className:"fr-sub"},"Fetching this run\u2019s keg list.")
        ),
        h("div",{className:"fr-card",style:{textAlign:"center",padding:"30px 16px"}},
          h("div",{className:"fr-sub",style:{marginTop:0}},"Run loading\u2026")
        )
      );
    }
    return h("div",null,
      h("div",{style:{marginTop:12}},
        h("div",{className:"fr-eyebrow"},"Load the van"),
        h("div",{className:"fr-title"},"Nothing to load."),
        h("div",{className:"fr-sub"},"Open a run from Today first, then come back to load it.")
      ),
      h("div",{className:"fr-card",style:{textAlign:"center",padding:"26px 16px"}},
        h("div",{className:"fr-sub",style:{marginTop:0}},"No run is open right now."),
        h("button",{className:"fr-btn ink sm",style:{marginTop:14,maxWidth:200,marginLeft:"auto",marginRight:"auto"},onClick:onGoToday},"Go to Today")
      )
    );
  }

  // ----- keg progress -----
  const kegTarget = list.total18 + list.total9 + (list.total7||0);
  const kegDoneCount = list.rows.reduce(function(a,r,i){
    var n=0; if(r.c18 && done[i+"-18"]) n+=r.c18; if(r.c9 && done[i+"-9"]) n+=r.c9;
    if(r.c7 && done[i+"-7"]) n+=r.c7; return a+n;
  },0);
  const kegsAllDone = kegTarget>0 && kegDoneCount>=kegTarget;
  const t18done = list.total18>0 && list.rows.every(function(r,i){ return !r.c18 || done[i+"-18"]; });
  const t9done  = list.total9>0  && list.rows.every(function(r,i){ return !r.c9  || done[i+"-9"];  });
  const t7done  = (list.total7||0)>0 && list.rows.every(function(r,i){ return !r.c7 || done[i+"-7"]; });

  function toggleCell(i,size){
    var key=i+"-"+size;
    setDone(function(d){ var n=Object.assign({},d); if(n[key]) delete n[key]; else n[key]=true; return n; });
  }
  function confirmAllKegs(){
    var n={}; list.rows.forEach(function(r,i){ if(r.c18) n[i+"-18"]=true; if(r.c9) n[i+"-9"]=true; if(r.c7) n[i+"-7"]=true; });
    setDone(n); showToast("All kegs confirmed");
  }

  // ----- extras -----
  const exRows = [].concat(
    extras.cards.map(function(r,i){ return {key:"c"+i, kind:"Tap card", row:r}; }),
    extras.corflute.map(function(r,i){ return {key:"f"+i, kind:"Corflute", row:r}; }),
    (extras.other||[]).map(function(r,i){ return {key:"o"+i, kind:"Other", row:r}; })
  );
  const hasExtras = exRows.length>0;
  const exTotal = exRows.reduce(function(a,x){ return a+(+x.row.qtyTotal||0); },0);
  const exAllDone = !hasExtras || exRows.every(function(x){ return exDone[x.key]; });
  function toggleEx(key){ setExDone(function(d){ var n=Object.assign({},d); if(n[key]) delete n[key]; else n[key]=true; return n; }); }
  function toggleExp(key){ setExExp(function(d){ var n=Object.assign({},d); if(n[key]) delete n[key]; else n[key]=true; return n; }); }
  function confirmAllExtras(){ var n={}; exRows.forEach(function(x){ n[x.key]=true; }); setExDone(n); showToast("All extras confirmed"); }

  // ----- vehicle check -----
  const VC=[
    {k:"tyres", name:"Tyres look ok",  hint:"No flats, no major wear"},
    {k:"load",  name:"Load secure",    hint:"Kegs strapped or wedged \u00B7 no rolling room"},
    {k:"oil",   name:"Oil level",      hint:"Check warning light when the engine starts"},
    {k:"fuel",  name:"Fuel",           hint:"More than a quarter tank"},
    // Line cleans every 14 days (Oct 2026) — the kit has to be on the van.
    {k:"lineclean", name:"Line cleaning equipment", green:true, tag:"Line cleans",
      hint:(lcLines==null)?"Line clean kit and PBW on board"
        :(lcLines?("Cleaning keg filled \u2014 about "+qtyLabel(lcLitres)+" L for "+lcLines+(lcLines===1?" line":" lines")+" \u00B7 kit and PBW on board")
                 :"No line cleans due this run \u2014 kit and PBW on board anyway")}
  ];
  // The final walk-round: standing at the van after loading, one last look at
  // everything that should be in it. Ticked on its own, separate from safety.
  const vcAllDone = VC.every(function(r){ return vc[r.k]; }) && !!vc.loadedFinal;
  const sumKegRows = [];
  list.rows.forEach(function(r){
    [["18L",r.c18],["9L",r.c9],["7L",r.c7]].forEach(function(sz){
      if(sz[1]) sumKegRows.push({key:r.product+"|"+sz[0], colour:r.colour, short:r.short, size:sz[0], qty:sz[1]});
    });
  });
  function toggleVc(k){ setVc(function(d){ var n=Object.assign({},d); if(n[k]) delete n[k]; else n[k]=true; return n; }); }

  // extras: one tickable row (qty = how many stockists need it), with per-stockist detail
  function exRowEl(r,key){
    var isDone=!!exDone[key], isExp=!!exExp[key];
    return h(React.Fragment,{key:key},
      h("div",{className:"fr-exrow"+(isDone?" done":"")+(isExp?" exp":"")},
        h("div",{className:"fr-extick"},
          r.colour?h("span",{className:"d",style:{background:r.colour}}):null,
          h("span",{className:"fr-exname"}, r.short)
        ),
        h("div",{className:"fr-exqty",onClick:function(){ toggleEx(key); }}, r.qtyTotal!=null ? qtyLabel(r.qtyTotal) : r.stockists.length),
        h("div",{className:"fr-exchev",onClick:function(){ toggleExp(key); }},
          h("svg",{viewBox:"0 0 24 24"},h("polyline",{points:"9 18 15 12 9 6"})))
      ),
      h("div",{className:"fr-exdet"},
        r.stockists.map(function(st,j){
          var nm = (st && st.name!=null) ? st.name : st;       // tolerate old string-only shape
          var q  = (st && st.qty!=null) ? st.qty : 1;
          var why = (st && st.reason) ? st.reason : "";
          return h("div",{className:"fr-exdrow",key:j,onClick:function(e){ e.currentTarget.classList.toggle("done"); }},
            h("div",{className:"fr-exds"}, nm,
              why?h("div",{className:"fr-exdm"}, why):null),
            h("div",{className:"fr-exdq"}, qtyLabel(q)));
        })
      )
    );
  }
  // a category is always shown: list when there are items, else a 0 + reassurance line
  function exCat(title,items,prefix,emptyMsg){
    return h("div",{className:"fr-exsec",key:prefix},
      h("div",{className:"fr-exhead"}, title, items.length?h("span",{className:"fr-exhead-c"}," \u00B7 "+items.length):null),
      items.length
        ? h("div",{className:"fr-exlist"}, items.map(function(r,i){ return exRowEl(r,prefix+i); }))
        : h("div",{className:"fr-exempty"},
            h("div",{className:"fr-exempty-n"},"0"),
            h("div",{className:"fr-exempty-m"}, emptyMsg))
    );
  }

  // ----- line cleans: how many lines, so the cleaning keg is filled first -----
  function lcCard(){
    var base={background:"#EEFBF3",border:"1.5px solid #2D7A4F",borderRadius:12,padding:"14px 16px",marginTop:14};
    var tag=h("div",{className:"fr-vc-tag"},"Line cleans");
    if(lc===null) return h("div",{style:base},tag,
      h("div",{className:"fr-sub",style:{marginTop:2}},"Checking who\u2019s due a line clean\u2026"));
    if(lc.unavailable) return h("div",{style:Object.assign({},base,{background:"var(--g05)",borderColor:"var(--g10)"})},tag,
      h("div",{className:"fr-sub",style:{marginTop:2}},"Couldn\u2019t check line cleans for this run \u2014 check Field Ops before filling the cleaning keg."));
    if(!lc.stops.length) return h("div",{style:base},tag,
      h("div",{style:{fontSize:15,fontWeight:700,marginTop:2}},"No line cleans due on this run"),
      h("div",{className:"fr-sub",style:{marginTop:4}},"No need to fill the cleaning keg today."));
    return h("div",{style:base},
      h("div",{style:{display:"flex",alignItems:"flex-start",gap:12,cursor:"pointer"},onClick:function(){ setLcOpen(function(o){ return !o; }); }},
        h("div",{style:{flex:1,minWidth:0}},tag,
          h("div",{style:{fontSize:17,fontWeight:800,letterSpacing:-.3,marginTop:2}},
            lcLines+(lcLines===1?" line":" lines")+" across "+lc.venues+(lc.venues===1?" venue":" venues")),
          h("div",{style:{fontSize:13,marginTop:3,color:"var(--ink)"}},
            "Fill the cleaning keg with about "+qtyLabel(lcLitres)+" L ("+qtyLabel(lc.litresPerLine||1)+" L per line).")),
        h("div",{style:{textAlign:"right",flexShrink:0}},
          h("div",{style:{fontFamily:"var(--mono)",fontSize:26,fontWeight:800,lineHeight:1,color:"#2D7A4F"}},qtyLabel(lcLitres)+"L"),
          h("div",{style:{fontSize:11,color:"var(--g60)",marginTop:4}},lcOpen?"Hide \u25BE":"Who \u25B8"))),
      lcOpen?h("div",{style:{marginTop:10,borderTop:"1px solid #B8E4CA",paddingTop:6}},
        lc.stops.map(function(st){
          return h("div",{key:st.customerId,style:{display:"flex",alignItems:"baseline",gap:10,padding:"7px 0",borderBottom:"1px solid #D5F0E0",fontSize:13}},
            h("div",{style:{flex:1,minWidth:0}},
              h("div",{style:{fontWeight:600}},st.name),
              h("div",{style:{fontSize:11.5,color:"var(--g60)",marginTop:1}},
                (st.daysSince==null?"No clean on record":("Last done "+st.daysSince+" days ago"))+" \u00B7 every "+st.every+" days"
                +(st.tapsKnown?"":" \u00B7 taps not recorded, counted as 1"))),
            h("div",{style:{fontFamily:"var(--mono)",fontWeight:700,flexShrink:0}},st.lines+(st.lines===1?" line":" lines")));
        })):null);
  }

  // ----- step / footer wiring -----
  const stepsDone = { kegs:kegsAllDone, acc:exAllDone, vc:vcAllDone };
  function go(s){ setStep(s); window.scrollTo(0,0); }

  function check(){ return h("svg",{viewBox:"0 0 24 24"},h("polyline",{points:"20 6 9 17 4 12"})); }

  function footer(){
    if(step==="kegs"){
      return kegsAllDone
        ? {cls:"ink", label:"Next: Extras", on:function(){ go("acc"); }}
        : {cls:"disabled", label:(kegTarget?("Confirm "+(kegTarget-kegDoneCount)+" more kegs"):"No kegs on this run"), on:null};
    }
    if(step==="acc"){
      return exAllDone
        ? {cls:"ink", label:"Next: Vehicle check", on:function(){ go("vc"); }}
        : {cls:"disabled", label:("Confirm "+exRows.filter(function(x){return !exDone[x.key];}).length+" more extras"), on:null};
    }
    return vcAllDone
      ? {cls:"yellow", label:"Ready to roll \u00B7 Start run", on:function(){ setDeparting(true); }}
      : {cls:"disabled", label:(vc.loadedFinal?"Complete safety check":"Confirm the van is loaded"), on:null};
  }
  const ft = footer();

  return h("div",null,
    h("div",{style:{marginTop:12}},
      h("div",{className:"fr-eyebrow"}, runDate?fmtWhen(runDate,runSlot):"Delivery run"),
      h("div",{className:"fr-title"},"Load the van."),
      h("div",{className:"fr-sub"}, list.totalKegs + (list.totalKegs===1?" keg":" kegs") + " across " + stops.length + (stops.length===1?" stop":" stops"))
    ),

    // stepper
    h("div",{className:"fr-lstepper"},
      ["kegs","acc","vc"].map(function(s,i){
        var labels={kegs:"Kegs",acc:"Extras",vc:"Vehicle"};
        var cls="fr-lstep"+(step===s?" on":"")+(stepsDone[s]&&step!==s?" done":"");
        return h("button",{key:s,className:cls,onClick:function(){ go(s); }},(i+1)+" \u00B7 "+labels[s]);
      })
    ),

    // ---------- STEP 1: KEGS ----------
    step==="kegs" && h("div",null,
      lcCard(),
      h("div",{className:"fr-card",style:{padding:14}},
        h("div",{className:"fr-lprog"},
          h("div",null,
            h("div",{className:"fr-eyebrow"},"Confirmed"),
            h("div",{className:"fr-lprog-v"}, kegDoneCount + " / " + kegTarget)
          )
        ),
        h("div",{className:"fr-lbar"},h("div",{className:"fr-lbar-fill",style:{width:(kegTarget?(kegDoneCount/kegTarget*100):0)+"%"}}))
      ),

      list.hasLines ? h("div",null,
        h("div",{className:"fr-seclab"},
          h("span",{className:"fr-eyebrow"},"Grab from fridge"),
          h("span",{className:"fr-eyebrow muted"},"Tap a number to mark loaded")
        ),
        h("div",{className:"fr-card",style:{padding:"4px 14px"}},
          h("table",{className:"fr-kegm"},
            h("thead",null,h("tr",null,
              h("th",null,"Product"), h("th",null,"18L"), h("th",null,"9L"), list.has7?h("th",null,"7L"):null
            )),
            h("tbody",null,
              list.rows.map(function(r,i){
                return h("tr",{key:r.product},
                  h("td",{className:"prod"}, h("span",{className:"d",style:{background:r.colour}}), r.short),
                  r.c18 ? h("td",{className:"cell"+(done[i+"-18"]?" done":""),onClick:function(){ toggleCell(i,"18"); }}, r.c18)
                        : h("td",{className:"zero"},"\u2014"),
                  r.c9  ? h("td",{className:"cell"+(done[i+"-9"]?" done":""),onClick:function(){ toggleCell(i,"9"); }}, r.c9)
                        : h("td",{className:"zero"},"\u2014"),
                  list.has7 ? (r.c7 ? h("td",{className:"cell"+(done[i+"-7"]?" done":""),onClick:function(){ toggleCell(i,"7"); }}, r.c7)
                                    : h("td",{className:"zero"},"\u2014")) : null
                );
              }),
              h("tr",{className:"tot"},
                h("td",{className:"prod"},"Total"),
                h("td",{className:(t18done?"tdone":"")},list.total18||"\u2014"),
                h("td",{className:(t9done?"tdone":"")},list.total9||"\u2014"),
                list.has7?h("td",{className:(t7done?"tdone":"")},list.total7||"\u2014"):null
              )
            )
          )
        ),
        // A 7L isn't a keg size — it's a 9L body filled short, so it scans as
        // KEG-09-. Said out loud here so nobody goes looking for a 7L keg.
        list.has7?h("div",{className:"fr-sub",style:{marginTop:8,fontSize:11.5,padding:"0 4px"}},
          "7L are 9L kegs filled short \u2014 keg IDs still read KEG-09-. Grab the part-filled ones, not full 9Ls."):null
      ) : h("div",{className:"fr-card"},
        h("div",{className:"fr-eyebrow"},"Total to load"),
        h("div",{className:"fr-lprog-v",style:{marginTop:6}}, list.totalKegs + (list.totalKegs===1?" keg":" kegs")),
        h("div",{className:"fr-sub",style:{marginTop:10}},
          "Per-product breakdown isn't stored for this saved run yet \u2014 it's available for runs built this session. Tap \u2018Confirm all\u2019 once the kegs are on board.")
      ),
      // Under the list, not above it: count the kegs first, then confirm.
      kegTarget>0 ? h("div",{className:"fr-exconfirm"},
        h("div",{className:"fr-exconfirm-l"},"Already loaded every keg?"),
        h("button",{className:"fr-btn ink sm",onClick:confirmAllKegs},"Confirm all")
      ) : null
    ),

    // ---------- STEP 2: EXTRAS ----------
    step==="acc" && h("div",null,
      h("div",{style:{marginTop:14}},
        h("div",{className:"fr-sub",style:{marginTop:0}}, hasExtras ? ("Grab "+exTotal+(exTotal===1?" item":" items")+". Tap the number to clear, chevron for the per-stockist detail.") : "Nothing extra to grab \u2014 here's the rundown.")
      ),
      exCat("Tap cards", extras.cards, "c",
        "No tap cards needed \u2014 every stockist on this run already has the tap cards for the products being delivered."),
      exCat("Corflute", extras.corflute, "f",
        "No corflute needed \u2014 every stockist on this run already has corflute for the products being delivered."),
      exCat("Other", extras.other||[], "o",
        "No other extras on this run (squealer bottles, glasses, gas bottles, A-frames, coffee bags)."),
      hasExtras ? h("div",{className:"fr-exconfirm"},
        h("div",{className:"fr-exconfirm-l"},"Already grabbed everything?"),
        h("button",{className:"fr-btn ink sm",onClick:confirmAllExtras},"Mark all done")
      ) : null
    ),

    // ---------- STEP 3: VEHICLE ----------
    step==="vc" && h("div",null,
      // Final load check — read it off standing at the van.
      h("div",{className:"fr-seclab",style:{marginTop:14}},
        h("span",{className:"fr-eyebrow"},"Final check · in the van"),
        h("span",{className:"fr-eyebrow muted"}, list.totalKegs + (list.totalKegs===1?" keg":" kegs") + (exTotal?(" · "+qtyLabel(exTotal)+(exTotal===1?" extra":" extras")):""))
      ),
      h("div",{className:"fr-card",style:{padding:"8px 16px"}},
        h("div",{className:"fr-sum-h"},"Kegs"),
        list.hasLines && sumKegRows.length
          ? sumKegRows.map(function(r){
              return h("div",{className:"fr-sum-row",key:r.key},
                h("span",{className:"fr-sum-n"}, r.colour?h("span",{className:"d",style:{background:r.colour}}):null, r.short),
                h("span",{className:"fr-sum-s"}, r.size),
                h("span",{className:"fr-sum-q"}, "×"+r.qty));
            })
          : h("div",{className:"fr-sum-row"},
              h("span",{className:"fr-sum-n"},"Kegs (no product breakdown saved)"),
              h("span",{className:"fr-sum-s"},""),
              h("span",{className:"fr-sum-q"},"×"+list.totalKegs)),
        h("div",{className:"fr-sum-h"},"Extras"),
        exRows.length
          ? exRows.map(function(x){
              return h("div",{className:"fr-sum-row",key:x.key},
                h("span",{className:"fr-sum-n"}, x.row.colour?h("span",{className:"d",style:{background:x.row.colour}}):null,
                  (x.kind==="Other"?"":x.kind+" · ") + x.row.short),
                h("span",{className:"fr-sum-s"},""),
                h("span",{className:"fr-sum-q"}, "×"+(x.row.qtyTotal!=null?qtyLabel(x.row.qtyTotal):x.row.stockists.length)));
            })
          : h("div",{className:"fr-sum-row"}, h("span",{className:"fr-sum-n",style:{color:"var(--g60)"}},"No extras on this run"))
      ),
      h("div",{className:"fr-card",style:{padding:"6px 18px"}},
        h("div",{className:"fr-vc"+(vc.loadedFinal?" done":""),onClick:function(){ toggleVc("loadedFinal"); }},
          h("div",{className:"fr-vc-chk"}, check()),
          h("div",null,
            h("div",{className:"fr-vc-name"},"Everything above is in the van"),
            h("div",{className:"fr-vc-hint"},"Counted at the van, not the fridge")
          )
        )
      ),
      h("div",{style:{marginTop:14}},
        h("div",{className:"fr-sub",style:{marginTop:0}},"Quick safety check before you go.")
      ),
      h("div",{className:"fr-card",style:{padding:"6px 18px"}},
        VC.map(function(r){
          return h("div",{className:"fr-vc"+(vc[r.k]?" done":"")+(r.green?" green":""),key:r.k,onClick:function(){ toggleVc(r.k); }},
            h("div",{className:"fr-vc-chk"}, check()),
            h("div",null,
              r.tag?h("div",{className:"fr-vc-tag"}, r.tag):null,
              h("div",{className:"fr-vc-name"}, r.name),
              h("div",{className:"fr-vc-hint"}, r.hint)
            )
          );
        })
      )
    ),

    // ---------- sticky footer ----------
    h("div",{className:"fr-lfoot"},
      h("button",{className:"fr-btn "+ft.cls, disabled:!ft.on, onClick:ft.on||undefined}, ft.label)
    ),

    // ---------- depart confirmation ----------
    departing && h("div",{className:"fr-depart-ov",onClick:function(e){ if(e.target===e.currentTarget) setDeparting(false); }},
      h("div",{className:"fr-depart-card"},
        h("div",{className:"fr-depart-ic"}, h("svg",{viewBox:"0 0 24 24"},h("polyline",{points:"20 6 9 17 4 12"}))),
        h("div",{className:"fr-title",style:{fontSize:26}},"Van loaded."),
        h("div",{className:"fr-sub"}, list.totalKegs + " kegs" + (hasExtras?" \u00B7 extras grabbed":"") + " \u00B7 safety checked. Hit the road."),
        h("button",{className:"fr-btn yellow",style:{marginTop:20},onClick:function(){ setDeparting(false); onDepart(); }},"Start the run"),
        h("button",{className:"fr-btn out",style:{marginTop:8},onClick:function(){ setDeparting(false); }},"Not yet")
      )
    )
  );
}

ReactDOM.render(h(App),document.getElementById("root"));